/* ==================================================================
   Garden pod geometry engine.

   Pure functions and data, no React and no three.js. Everything the
   drawings, the 3D views and the STL export share lives here, so the
   views can never disagree about the building.

   X = length, Z = width, Y = up. All units millimetres.
================================================================== */

const SECTIONS = [
  { id: "44x75", t: 44, d: 75, label: "44 × 75", nominal: '3" × 2"' },
  { id: "44x100", t: 44, d: 100, label: "44 × 100", nominal: '4" × 2"' },
  { id: "44x150", t: 44, d: 150, label: "44 × 150", nominal: '6" × 2"' },
  /* Battens. Laid flat against the sheathing a 2 × 1 gives 50 mm on the face
     to fix through and 22 mm of drainage gap behind the cladding, so the
     label reads the way a merchant lists it rather than as `t × d`.
     Treated, because both jobs it does are wet ones: a cladding batten lives
     in the drained cavity behind the boards, and a ledger lives in the cold
     void under the floor insulation. The id cannot change — a saved design
     names it — so the label and the nominal carry it. */
  { id: "50x22", t: 22, d: 50, label: "50 × 22 treated", nominal: '2" × 1" treated' },
];

/* Sections that used to ship. A saved design can name one, and dropping it
   from the catalogue would otherwise leave `sectionFor` falling back to
   whatever is first — silently rebuilding somebody's pod in a different
   timber. `migrateDesign` turns any of these that a design still refers to
   into a custom section, so the dimensions it was drawn at survive. Never
   delete an entry here: it is the record of what an id once meant. */
const LEGACY_SECTIONS = [
  { id: "42x70", t: 42, d: 70 },
  { id: "38x89", t: 38, d: 89 },
  { id: "38x140", t: 38, d: 140 },
  { id: "45x95", t: 45, d: 95 },
  { id: "45x100", t: 45, d: 100 },
  { id: "45x120", t: 45, d: 120 },
  { id: "45x145", t: 45, d: 145 },
  { id: "47x150", t: 47, d: 150 },
  { id: "47x175", t: 47, d: 175 },
];

/* Sawn, planed and treated stock all measure differently, and the number
   that matters is the one on your tape, not the one on the label. */
function makeSection(t, d, note = "") {
  const th = Math.round(Number(t));
  const dp = Math.round(Number(d));
  if (!Number.isFinite(th) || !Number.isFinite(dp)) return null;
  if (th < 10 || th > 400 || dp < 10 || dp > 500) return null;
  const clean = String(note).trim().slice(0, 18);
  const slug = clean.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return {
    id: `u${th}x${dp}${slug ? `-${slug}` : ""}`,
    t: th, d: dp,
    label: `${th} × ${dp}${clean ? ` ${clean}` : ""}`,
    nominal: clean || "yours",
    custom: true,
  };
}

const SHEETS = [
  { id: "2397", l: 2397, w: 1197, label: "2397 × 1197", note: "UK merchant size" },
  { id: "2400", l: 2400, w: 1200, label: "2400 × 1200", note: "Common in Ireland" },
  { id: "2440", l: 2440, w: 1220, label: "2440 × 1220", note: "8 × 4 ft" },
];

const PIR_BOARDS = [
  { id: "2400x1200", l: 2400, w: 1200, label: "2400 × 1200" },
  { id: "2400x1220", l: 2400, w: 1220, label: "2400 × 1220" },
  { id: "1200x600", l: 1200, w: 600, label: "1200 × 600" },
];

const PIR_T = [25, 30, 40, 50, 60, 70, 75, 80, 100, 120, 140, 150];
/* Foil tape widths a merchant actually stocks. 100 mm is the usual one
   for PIR: 50 mm of lap each side of a butt joint. */
const TAPE_W = [50, 60, 72, 75, 100, 150];
const OSB_T = [11, 15, 18, 22];
const STOCK = [3000, 3600, 4200, 4800, 6000];
const EPDM_WIDTHS = [3050, 4570, 6100, 7620, 9150];
const SIDES = [
  { id: "rear", label: "Rear" },
  { id: "front", label: "Front" },
  { id: "left", label: "Left" },
  { id: "right", label: "Right" },
];

/* Model palettes. The UI chrome stays neutral drafting grey whichever is
   picked — only the geometry changes colour, so the drawings stay readable. */
const PALETTES = [
  {
    id: "drafting", label: "Drafting",
    header: "#8A5340", timber: "#C5877C", noggin: "#B0716A", osb: "#C6A268", osbCut: "#A87F42",
    batten: "#8E6F4E", cladding: "#A98258", upstand: "#9D6A60", pir: "#E3DBA4", tape: "#B8BEC2", wrap: "#DCDDD6", glass: "#8FB2C4", gutter: "#506B7D",
    neighbour: "#9AA39C", floorblock: "#BFAE9C", wallblock: "#D6CFC2",
    sides: { rear: "#913636", front: "#C8B26A", left: "#369151", right: "#6A99C8" },
  },
  {
    id: "bloom", label: "Bloom",
    header: "#9C4A2E", timber: "#E0A08C", noggin: "#C98872", osb: "#E8C27A", osbCut: "#C99C4E",
    batten: "#A87A4C", cladding: "#C49360", upstand: "#B87C68", pir: "#F2E8B0", tape: "#C9CFD4", wrap: "#E7E6DD", glass: "#9FC9DE", gutter: "#4E7C96",
    neighbour: "#A6ADA4", floorblock: "#CBB79F", wallblock: "#E4DAC8",
    sides: { rear: "#9B2727", front: "#D5B858", left: "#279B4A", right: "#74A8DC" },
  },
  {
    id: "ash", label: "Ash",
    header: "#7E5B49", timber: "#B9B2A6", noggin: "#A39C90", osb: "#CFC5B2", osbCut: "#B4A992",
    batten: "#8C8371", cladding: "#A39880", upstand: "#97907F", pir: "#E5E0CE", tape: "#AFB6BA", wrap: "#D6D5CC", glass: "#A8BAC4", gutter: "#6E7A80",
    neighbour: "#9DA39D", floorblock: "#B5AC9E", wallblock: "#D8D3C7",
    sides: { rear: "#A85C57", front: "#C1B786", left: "#3E7954", right: "#6E8AB4" },
  },
  {
    id: "ink", label: "Ink",
    header: "#43352C", timber: "#7C8A93", noggin: "#68757D", osb: "#A9B3B6", osbCut: "#8B979B",
    batten: "#6A6258", cladding: "#7E7568", upstand: "#5F6C74", pir: "#D5DBD4", tape: "#93A0A6", wrap: "#B4B8B4", glass: "#7FA7BC", gutter: "#39505E",
    neighbour: "#8E948E", floorblock: "#8C8F8C", wallblock: "#BCC2C1",
    sides: { rear: "#573C33", front: "#B8B551", left: "#51B885", right: "#384B8A" },
  },
];

/* The full default design, used for resets and as the base every example
   overrides, so an example can never load a half-populated state. */
const DEFAULT_DESIGN = {
  sheetFor: { 11: "2397", 15: "2397", 18: "2397", 22: "2397" },
  enabled: ["44x75", "44x100", "44x150", "50x22"], pirBoardId: "2400x1200", stock: 4800,
  gap: 3, stagger: true, goal: "cuts",
  wSheathAlign: "auto", wSheathOrient: "auto", wSheathCut: "over",
  glazedFix: "window",
  prices: {}, currency: "€",
  wallSide: "none", wallGap: 600, wallGapFrom: "frame",
  groundClear: 50, targetHeight: 2500, boundaryH: 2000, views: null,
  show: ["floorDeck", "floorPir", "wallPir", "sheathing", "glazing", "battens",
         "roofDeck", "upstand", "pir", "topDeck", "gutter", "boundary", "dims",
         "fixings"],
  renderMode: "ghost",
  paletteId: "drafting", colourBy: "wall", explode: 0, customSections: [],
  furrOn: false, furrLow: 25, furrHigh: 70, furrId: "44x75",
  fPirT: 0, wPirT: 0,
  fBattenOn: false, fBattenPct: 80, fBattenOnEdge: true,
  pirPiece: "flat", pirPieceL: 400, pirPieceW: 50, pirPieceMax: 4, pirTol: 5,
  tapeOn: true, tapeW: 100, tapeRoll: 45, tapeWaste: 10, tapeLayers: "all",
  wrapOn: true, wrapRollW: 1500, wrapOverlap: 150, wrapDrop: 20, wrapRollLen: 50,
  wrapTapeW: 60, wrapTapeRoll: 25,
  rWrapOn: true, rWrapOrient: "auto",
  locationId: "dublin", siteWinter: -3, siteSummer: 24,
  frontFaces: "s", indoorWinter: 21, indoorSummer: 25,
  airChanges: 0.5, internalGain: 150, shadingFactor: 0.9,
  uGlazing: 0.8, gGlazing: 0.5, uDoorSolid: 1.4, lambdaPir: 0.022,
  fixOn: true, fixJointScrews: 3, fixNogginScrews: 2, fixHangers: true,
  fixSheetPitch: 200,
  fL: 2570, fW: 1670, fSectionId: "44x150", fSpacing: 400, fDeckT: 18,
  fAlign: "centre", fOrient: "auto", fJoist: "deck", fDouble: true,
  fNoggins: 1,
  wSectionId: "44x75", wLayout: "lengthThrough", wLap: "lengthOver",
  battenOn: false, battenId: "50x22", battenOnEdge: false, battenStock: 4500,
  cladOn: true, cladFace: 98, cladGap: 6, cladT: 18, cladStock: 3.6,
  cladSides: ["rear", "front", "left", "right"],
  cladding: "horizontal", battenSpacing: 600, battenMode: "equal",
  battenHSpacing: 600, battenHMode: "equal",
  ceilingHeight: 2300, wSpacing: 400, wStudAlign: "deck", wNoggins: 2,
  wSheathT: 11,
  wMinBay: 150, spacingMode: "centres",
  doorOn: false, doorSide: "front", doorW: 900, doorH: 2050, doorGap: 5,
  doorHeaderOnEdge: true,
  doorFrom: "start", doorOffset: 600, doorSolid: false, doorFullHeader: false,
  winOn: true, winStyle: "standard", winSide: "front", winW: 1500, winH: 1500, winSill: 600,
  winGap: 5, winCentred: true, winCentre: 1285, headerId: "44x150", headerOnEdge: true,
  frameDepth: 75, frameWidth: 50, doorFrameWidth: 50,
  ohLeft: 300, ohRight: 300, ohFront: 500, ohRear: 300,
  rSectionId: "44x150", rSpacing: 400, rJoist: "deck", rNoggins: 1,
  rDeckT: 18, rAlign: "edge", rOrient: "auto",
  upstandId: "44x100", upstandOnEdge: true, pirT: 100, rPirMode: "thickness",
  topT: 11, topAlign: "edge", topOrient: "auto",
  epdmMargin: 100, gutterSide: "rear",

  asmMode: "framing", asmScale: 20,
  asmInclude: ["floor", "walls", "sheathing", "roof"],
  asmGhost: false, asmMinFeature: 0.9,
};

/* The component initialises every piece of state from this object, so the
   two can never drift — which they did: the section ids here were retired
   while the component kept its own copies, and every timber in a fresh
   design silently fell back to the first in the catalogue. Sharing the
   objects means a stray in-place write would poison the defaults for the
   session, so they are frozen and such a write throws instead. */
const deepFreeze = (o) => {
  Object.values(o).forEach((v) => {
    if (v && typeof v === "object" && !Object.isFrozen(v)) deepFreeze(v);
  });
  return Object.freeze(o);
};
deepFreeze(DEFAULT_DESIGN);

const EXAMPLES = [
  {
    id: "micro-glazed",
    name: "Micro Pod with wall to wall glazing",
    blurb:
      "2570 \u00d7 1686 on 44 \u00d7 100 treated joists at 300 centres and 44 \u00d7 75 studs, 2000 mm ceiling, 2457 mm overall. A glazed wall on the right, where the header runs the full width, replaces the top plate and sits on the furring plane, and a 900 \u00d7 1950 solid door 600 mm off centre on the left. Vertical cladding on two layers of treated 2 \u00d7 1, 50 mm PIR in the floor and walls with ledgers under it, 100 mm on a 35 \u00d7 115 upstand up top. Roof falls 19 mm per metre to a gutter on the left. Priced end to end.",
    notes: "Three sheathing joints still have no stud behind them, on the walls the door and the glazed opening interrupt. The board runs over both openings and is cut out after, which throws 4.07 m\u00b2 away in four pieces \u2014 piece around them on the Walls tab to buy less. Eight battens work out under 150 mm over the door head and under the sill and are left off the cut list. The roof\u2019s 100 mm PIR is its own pile, separate from the 50 mm the floor and walls share: 2 \u00d7 the wall\u2019s on the Roof tab reaches the same depth off one.",
    design: {
      sheetFor: {
        "11": "2440",
        "15": "2397",
        "18": "2440",
        "22": "2397",
      },
      enabled: [
        "44x100", "44x150", "44x75", "u44x100-treated", "u44x75-treated", "u35x115-treated",
        "50x22"
      ],
      pirBoardId: "2400x1200",
      stock: 4800,
      gap: 3,
      stagger: false,
      goal: "cuts",
      fPirT: 50,
      wPirT: 50,
      fBattenOn: true,
      fBattenPct: 80,
      fBattenOnEdge: true,
      pirPiece: "flat",
      pirPieceL: 400,
      pirPieceW: 50,
      pirPieceMax: 4,
      pirTol: 5,
      locationId: "dublin",
      siteWinter: -3,
      siteSummer: 24,
      frontFaces: "s",
      indoorWinter: 21,
      indoorSummer: 25,
      airChanges: 0.5,
      internalGain: 150,
      shadingFactor: 0.9,
      uGlazing: 0.8,
      gGlazing: 0.5,
      uDoorSolid: 1.4,
      lambdaPir: 0.022,
      fixOn: true,
      fixJointScrews: 2,
      fixNogginScrews: 2,
      fixHangers: true,
      fixSheetPitch: 200,
      wallSide: "front",
      wallGap: 100,
      groundClear: 50,
      targetHeight: 2500,
      show: ["gutter", "pir", "glazing", "roofDeck", "upstand", "topDeck", "battens", "ledgers",
             "tape", "wrap", "cladding"],
      renderMode: "solid",
      boundaryH: 1800,
      views: {
        floor: {
          theta: -4.225,
          phi: 1.23,
          zoom: 2.128,
          focus: 5.46,
        },
        walls: {
          theta: 2.241,
          phi: 1.304,
          zoom: 2.254,
          focus: 0.826,
        },
        roof: {
          theta: 2.15,
          phi: 1.351,
          zoom: 2.05,
          focus: 0.944,
        },
      },
      paletteId: "bloom",
      colourBy: "wall",
      explode: 0,
      customSections: [
        {
          id: "u44x100-treated",
          t: 44,
          d: 100,
          label: "44 × 100 treated",
          nominal: "treated",
          custom: true,
        },
        {
          id: "u44x75-treated",
          t: 44,
          d: 75,
          label: "44 × 75 treated",
          nominal: "treated",
          custom: true,
        },
        {
          id: "u35x115-treated",
          t: 35,
          d: 115,
          label: "35 × 115 treated",
          nominal: "treated",
          custom: true,
        },
      ],
      prices: {
        "osb-11-2440": 22.5,
        "pir-50-2400x1200": 45,
        "timber-44x75-4800": 11.25,
        "pir-100-2400x1200": 76.5,
        "timber-u44x100-treated-4800": 16.4,
        "timber-u44x75-treated-4800": 12.95,
        "timber-u35x115-treated-4800": 14.75,
        "osb-18-2440": 32.95,
        "timber-50x22-4500": 4.35,
      },
      currency: "€",
      furrOn: true,
      furrLow: 20,
      furrHigh: 70,
      furrId: "u44x75-treated",
      fL: 2570,
      fW: 1686,
      fSectionId: "u44x100-treated",
      fSpacing: 300,
      fDeckT: 18,
      fAlign: "edge",
      fOrient: "auto",
      fJoist: "deck",
      fDouble: true,
      fNoggins: 1,
      wSectionId: "44x75",
      wLayout: "widthThrough",
      wLap: "widthOver",
      battenOn: true,
      battenId: "50x22",
      battenOnEdge: false,
      battenStock: 4500,
      cladding: "vertical",
      battenSpacing: 600,
      battenMode: "equal",
      battenHSpacing: 600,
      battenHMode: "equal",
      wSheathAlign: "auto",
      wSheathOrient: "auto",
      wSheathCut: "over",
      ceilingHeight: 2000,
      wSpacing: 400,
      wStudAlign: "deck",
      wNoggins: 1,
      wSheathT: 11,
      winOn: true,
      winSide: "right",
      winW: 1500,
      winH: 1500,
      winSill: 200,
      winGap: 5,
      winCentred: true,
      winCentre: 1285,
      headerId: "44x75",
      winStyle: "glazedWall",
      headerOnEdge: true,
      glazedFix: "window",
      doorOn: true,
      doorSide: "left",
      doorW: 900,
      doorH: 1950,
      doorGap: 5,
      doorFrom: "centre",
      doorOffset: 600,
      doorSolid: true,
      doorFullHeader: true,
      doorHeaderOnEdge: false,
      wMinBay: 150,
      spacingMode: "centres",
      frameDepth: 75,
      frameWidth: 50,
      doorFrameWidth: 50,
      ohLeft: 250,
      ohRight: 250,
      ohFront: 75,
      ohRear: 200,
      rSectionId: "u44x75-treated",
      rSpacing: 400,
      rJoist: "deck",
      rNoggins: 1,
      rDeckT: 18,
      rAlign: "auto",
      rOrient: "across",
      rPirMode: "thickness",
      upstandId: "u35x115-treated",
      upstandOnEdge: true,
      pirT: 100,
      topT: 11,
      topAlign: "auto",
      topOrient: "auto",
      epdmMargin: 100,
      gutterSide: "left",
      asmMode: "framing",
      asmScale: 50,
      asmInclude: ["floor", "walls", "sheathing", "roof", "insulation", "glazing"],
      asmGhost: true,
      asmMinFeature: 0.9,
    },
  },
  {
    id: "office-door",
    name: "Garden office with a door and a window",
    blurb:
      "3600 \u00d7 2600 on 45 \u00d7 120 joists and 38 \u00d7 89 studs, 2300 mm ceiling, 2832 mm overall. A 900 \u00d7 2050 door 600 mm off the corner of the front wall, and a 1200 \u00d7 1000 window on the left. Roof falls 19 mm per metre to a gutter at the rear.",
    notes:
      "Ordinary openings rather than a glazed wall: trimmers, king studs and a header on each, with the bottom plate cut away across the doorway. Only 82 mm was left above the door header, so it is deepened to 202 mm and runs up to the top plate.",
    design: {
      sheetFor: { "11": "2397", "15": "2397", "18": "2397", "22": "2397" },
      enabled: ["44x100", "44x150"],
      stock: 4800,
      gap: 3,
      stagger: true,
      goal: "largest",
      wallSide: "none",
      groundClear: 50,
      targetHeight: 3000,
      renderMode: "ghost",
      paletteId: "drafting",
      colourBy: "wall",
      furrOn: true,
      furrLow: 25,
      furrHigh: 75,
      furrId: "44x150",
      fL: 3600,
      fW: 2600,
      fSectionId: "44x150",
      fSpacing: 400,
      fDeckT: 18,
      fAlign: "auto",
      fOrient: "auto",
      fJoist: "deck",
      fDouble: true,
      fNoggins: 1,
      wSectionId: "44x100",
      wLayout: "lengthThrough",
      wLap: "lengthOver",
      wSheathAlign: "equal",
      wSheathOrient: "auto",
      ceilingHeight: 2300,
      wSpacing: 400,
      wStudAlign: "deck",
      wNoggins: 2,
      wSheathT: 11,
      doorOn: true,
      doorSide: "front",
      doorW: 900,
      doorH: 2050,
      doorGap: 5,
      doorFrom: "start",
      doorOffset: 600,
      doorSolid: false,
      winOn: true,
      winStyle: "standard",
      winSide: "left",
      winW: 1200,
      winH: 1000,
      winSill: 900,
      winGap: 5,
      winCentred: true,
      headerId: "44x150",
      headerOnEdge: true,
      frameDepth: 75,
      frameWidth: 50,
      ohLeft: 300,
      ohRight: 300,
      ohFront: 400,
      ohRear: 300,
      rSectionId: "44x150",
      rSpacing: 400,
      rJoist: "deck",
      rNoggins: 1,
      rDeckT: 18,
      rAlign: "auto",
      rOrient: "auto",
      upstandId: "44x150",
      upstandOnEdge: true,
      pirT: 100,
      topT: 11,
      topAlign: "auto",
      topOrient: "auto",
      epdmMargin: 100,
      gutterSide: "rear",
    },
  },
  {
    id: "door-header-plate",
    name: "Door header doing the top plate's job",
    blurb:
      "3200 \u00d7 2400 with a 1000 mm door on the front wall under a header that runs the full width, replaces the top plate and sits on the furring plane. Studs and trimmers stop under it, so the door reaches its underside. Roof falls to a gutter at the rear.",
    notes:
      "The same header treatment as a wall-to-wall glazed wall, but the opening itself is an ordinary one part-way along. Turn Header back to \u201cAbove the opening\u201d on the Walls tab to see the difference.",
    design: {
      sheetFor: { 11: "2397", 15: "2397", 18: "2397", 22: "2397" },
      enabled: ["44x100", "44x150"],
      stock: 4800, gap: 3, stagger: true, goal: "largest",
      wallSide: "none", groundClear: 50, targetHeight: 3000,
      renderMode: "ghost", paletteId: "ink", colourBy: "layer",
      furrOn: true, furrLow: 25, furrHigh: 70, furrId: "44x150",
      fL: 3200, fW: 2400, fSectionId: "44x150", fSpacing: 400, fDeckT: 18,
      fAlign: "auto", fOrient: "auto", fJoist: "deck", fDouble: true, fNoggins: 1,
      wSectionId: "44x100", wLayout: "lengthThrough", wLap: "lengthOver",
      wSheathAlign: "equal", wSheathOrient: "auto",
      ceilingHeight: 2300, wSpacing: 400, wStudAlign: "deck", wNoggins: 2, wSheathT: 11,
      doorOn: true, doorSide: "front", doorW: 1000, doorH: 2050, doorGap: 5,
      doorFrom: "centre", doorOffset: 600, doorSolid: false, doorFullHeader: true,
      winOn: false,
      headerId: "44x150", headerOnEdge: true, frameDepth: 75, frameWidth: 50,
      ohLeft: 300, ohRight: 300, ohFront: 400, ohRear: 300,
      rSectionId: "44x150", rSpacing: 400, rJoist: "deck", rNoggins: 1, rDeckT: 18,
      rAlign: "auto", rOrient: "auto", upstandId: "44x150", upstandOnEdge: true,
      pirT: 100, topT: 11, topAlign: "auto", topOrient: "auto",
      epdmMargin: 100, gutterSide: "rear",
    },
  },
];

/* ---------------------------- geometry ----------------------------

   Every solid is eight corners. Most are plain axis-aligned boxes, but a
   furring strip has a sloping top and everything above the wall plate is
   sheared to the fall, so the renderer, the STL writer and the tests all
   work from corners rather than from width-and-height.

   The fall is modelled as a shear, not a rotation: heights change with
   position but verticals stay plumb. At the pitches involved — a 1:60 fall
   is under a degree — the difference is immaterial, and it keeps studs,
   joists and sheet edges square, which is how they are actually cut.
------------------------------------------------------------------ */
function cornersOf(b) {
  const x0 = b.x, x1 = b.x + b.dx;
  const z0 = b.z, z1 = b.z + b.dz;
  const yb = b.y;
  const yt = b.y + b.dy;

  /* A sloping top face, given as heights at the low and high ends of an axis. */
  const topAt = (x, z) => {
    if (!b.slopeTop) return yt;
    const { axis, y0, y1 } = b.slopeTop;
    const p = axis === "x" ? x : z;
    const lo = axis === "x" ? x0 : z0;
    const hi = axis === "x" ? x1 : z1;
    return hi === lo ? y0 : y0 + ((p - lo) / (hi - lo)) * (y1 - y0);
  };

  const pts = [
    [x0, yb, z0], [x1, yb, z0], [x1, topAt(x1, z0), z0], [x0, topAt(x0, z0), z0],
    [x0, yb, z1], [x1, yb, z1], [x1, topAt(x1, z1), z1], [x0, topAt(x0, z1), z1],
  ];

  if (b.shear) {
    const { axis, origin, slope, base = 0 } = b.shear;
    pts.forEach((pt) => {
      pt[1] += base + ((axis === "x" ? pt[0] : pt[2]) - origin) * slope;
    });
  }
  return pts;
}

/* Faces as vertex indices into cornersOf's order, wound outward. */
const BOX_FACES = [
  { n: [0, 0, -1], v: [0, 3, 2, 1] },
  { n: [0, 0, 1], v: [4, 5, 6, 7] },
  { n: [-1, 0, 0], v: [0, 4, 7, 3] },
  { n: [1, 0, 0], v: [1, 2, 6, 5] },
  { n: [0, -1, 0], v: [0, 1, 5, 4] },
  { n: [0, 1, 0], v: [3, 7, 6, 2] },
];

/* Laminations across the wall thickness, with a hair gap so the seam reads
   in the model — two 38s should not look like one 76. */
function laminate(m, thickness, seam = 1.5) {
  const n = m.plies || 1;
  if (n < 2 || !m.plyThickness) return [{ off: 0, depth: thickness }];
  const total = n * m.plyThickness + (n - 1) * seam;
  const start = Math.max(0, (thickness - total) / 2);
  return Array.from({ length: n }, (_, i) => ({
    off: start + i * (m.plyThickness + seam),
    depth: m.plyThickness,
  }));
}

/* Starting cameras, captured from a working session rather than guessed.
   phi is measured from straight down, zoom multiplies the model's largest
   dimension, and focus lifts the pivot as a fraction of the view's own
   focal height — so these hold up when the building changes size. */
const DEFAULT_VIEWS = {
  floor: { theta: -4.225, phi: 1.23, zoom: 2.128, focus: 5.46 },
  walls: { theta: 2.241, phi: 1.304, zoom: 2.254, focus: 0.826 },
  roof: { theta: 2.15, phi: 1.351, zoom: 2.05, focus: 0.944 },
};

/* The defaults above were framed with the window on the right wall and the
   gutter at the rear. Move either and the camera rotates by the difference,
   keeping the same elevation and framing on whichever side now matters. */
const SIDE_ANGLE = { front: 0, right: Math.PI / 2, rear: Math.PI, left: -Math.PI / 2 };
const REFERENCE_SIDE = { walls: "right", roof: "rear" };

function viewForSide(base, which, side) {
  const ref = REFERENCE_SIDE[which];
  if (!ref || !(side in SIDE_ANGLE)) return base;
  return { ...base, theta: base.theta + (SIDE_ANGLE[side] - SIDE_ANGLE[ref]) };
}

/* When the point is telling layers apart rather than looking like timber.
   Generated for maximum pairwise separation: the closest pair is 60 apart in
   RGB, and a new entry has to clear that rather than merely find a free hue.
   Ledgers sit at 95 from their nearest neighbour. Tape is the exception
   that proves the rule: foil tape is silver, and a silver has to be a grey,
   so it cannot go looking for a free hue. #9AA3AA is the most separated
   near-neutral there is — 69 from the nearest layer and 59 from the ground
   plane, which it also has to be legible against. */
const LAYER_COLOURS = {
  framing: "#B1452F",
  floorDeck: "#367FC9",
  sheathing: "#A7D666",
  roofDeck: "#999029",
  upstand: "#D04E8F",
  battens: "#E08A00",
  ledgers: "#28D29B",
  tape: "#9AA3AA",
  wrap: "#EAEAA8",
  cladding: "#E4E430",
  pir: "#B57EDD",
  floorPir: "#6A4DB5",
  wallPir: "#8C2F6B",
  topDeck: "#24895F",
  glazing: "#7FC8DE",
  leaf: "#602020",
  gutter: "#4A5C6B",
};

/* Which layer colour a part belongs to. */
const PART_LAYER = {
  "floor frame": "framing", "wall frame": "framing", "roof frame": "framing",
  "roof noggin": "framing", header: "framing",
  "floor deck": "floorDeck", sheathing: "sheathing", "roof deck": "roofDeck",
  upstand: "upstand", insulation: "pir", "top deck": "topDeck",
  "floor insulation": "floorPir", "wall insulation": "wallPir",
  furring: "upstand", "furring packer": "upstand",
  glazing: "glazing", leaf: "leaf", gutter: "gutter",
  batten: "battens", "counter batten": "battens", "joist batten": "ledgers",
  tape: "tape",
  wrap: "wrap",
  "roof wrap": "wrap",
  cladding: "cladding",
};

const hexInt = (h) => parseInt(String(h).replace("#", ""), 16);
const darken = (h, k = 0.16) => {
  const n = typeof h === "number" ? h : hexInt(h);
  const mix = (c) => Math.round(c * (1 - k));
  return (mix((n >> 16) & 255) << 16) | (mix((n >> 8) & 255) << 8) | mix(n & 255);
};

const lighten = (h, k = 0.45) => {
  const n = hexInt(h);
  const mix = (c) => Math.round(c + (255 - c) * k);
  return (mix((n >> 16) & 255) << 16) | (mix((n >> 8) & 255) << 8) | mix(n & 255);
};

/* What can be shown or hidden, and from which view onward it exists.
   Depth mirrors the cumulative views: a floor-stage layer is togglable
   everywhere, a roof-stage one only once the roof is on. */
const LAYERS = [
  { id: "floorDeck", label: "Floor deck", depth: 1 },
  { id: "floorPir", label: "Floor insulation", depth: 1 },
  { id: "wallPir", label: "Wall insulation", depth: 2 },
  { id: "sheathing", label: "Sheathing", depth: 2 },
  { id: "glazing", label: "Glazing", depth: 2 },
  { id: "roofDeck", label: "Roof deck", depth: 3 },
  { id: "upstand", label: "Upstand", depth: 3, timber: true },
  { id: "pir", label: "PIR", depth: 3 },
  { id: "topDeck", label: "Top deck", depth: 3 },
  { id: "ledgers", label: "Ledgers", depth: 1, timber: true },
  { id: "tape", label: "Foil tape", depth: 1 },
  { id: "wrap", label: "Membrane", depth: 2 },
  { id: "cladding", label: "Cladding", depth: 2, timber: true },
  { id: "battens", label: "Cladding battens", depth: 2, timber: true },
  { id: "gutter", label: "Gutter", depth: 3 },
  { id: "fixings", label: "Fixings", depth: 1, context: true },
  { id: "boundary", label: "Boundary wall", depth: 1, context: true },
  { id: "dims", label: "Dimensions", depth: 1, context: true },
];

const ALL_LAYERS = LAYERS.map((l) => l.id);
/* The saved-design format version, and the version each layer arrived in. A
   file saved before a layer existed cannot say whether it wanted it, and its
   `show` list will not mention it — so on load those layers default to on
   rather than silently staying hidden. Bump DESIGN_VERSION and add an entry
   here whenever a layer is added, or it will not show up for anyone with a
   saved design. */
const DESIGN_VERSION = 8;
const LAYER_SINCE = { fixings: 2, floorPir: 2, wallPir: 2, battens: 4, ledgers: 5,
                     tape: 6, wrap: 7, cladding: 8 };
/* Framing means timber: the upstand stays, everything sheet, glazed or
   plumbed goes. The boundary wall and dimensions are context, not fabric. */
const FRAMING_ONLY = LAYERS.filter((l) => l.timber || l.context).map((l) => l.id);
const VIEW_DEPTH = { floor: 1, walls: 2, roof: 3 };

/* Older saved designs kept visibility per section; fold them into the
   global set rather than dropping the settings on load. */
/* The design fields that name a timber section. */
const SECTION_KEYS = ["fSectionId", "wSectionId", "rSectionId", "upstandId",
                      "furrId", "headerId"];

/* A design naming a section that no longer ships is rebuilt with that
   section's real dimensions as a custom entry, rather than being left to
   `sectionFor`'s fallback — which would quietly redraw it in whatever timber
   happens to be first in the list. */
function reviveSections(d) {
  const custom = Array.isArray(d.customSections) ? [...d.customSections] : [];
  const known = new Set([
    ...SECTIONS.map((x) => x.id),
    ...custom.filter((x) => x && x.id).map((x) => x.id),
  ]);
  const wanted = new Set(
    [...SECTION_KEYS.map((k) => d[k]), ...(Array.isArray(d.enabled) ? d.enabled : [])]
      .filter((id) => typeof id === "string" && id && !known.has(id))
  );
  if (!wanted.size) return d;

  const revived = [];
  wanted.forEach((id) => {
    const old = LEGACY_SECTIONS.find((x) => x.id === id);
    if (old) revived.push({ ...makeSection(old.t, old.d, "retired"), id });
  });
  if (!revived.length) return d;

  const out = { ...d, customSections: [...custom, ...revived] };
  /* A revived section has to be selectable, or `sectionFor` drops it. */
  if (Array.isArray(out.enabled)) {
    const en = new Set(out.enabled);
    revived.forEach((x) => en.add(x.id));
    out.enabled = [...en];
  }
  return out;
}

function migrateDesign(design, version = 1) {
  const d = reviveSections({ ...design });
  /* The door frame used to borrow the window's face width. A file saved
     before they split keeps the frame it was drawn with, rather than jumping
     to the default the moment it is opened — the loader only sets keys the
     file actually names, so without this a 70 mm frame would silently come
     back as 50. */
  if (version < 3 && d.doorFrameWidth == null && d.frameWidth != null)
    d.doorFrameWidth = d.frameWidth;
  if (d.colourBy == null && d.wallColour != null)
    d.colourBy = d.wallColour === "side" ? "wall" : "material";
  if (d.show || d.renderMode) {
    if (Array.isArray(d.show)) {
      const show = new Set(d.show);
      LAYERS.forEach((l) => {
        if ((LAYER_SINCE[l.id] || 1) > version) show.add(l.id);
      });
      d.show = [...show];
    }
    return d;
  }
  const show = new Set(ALL_LAYERS);
  if (d.fDeckMode === "off") show.delete("floorDeck");
  if (d.wShowSheath === false) show.delete("sheathing");
  if (Array.isArray(d.rShow)) {
    const map = { deck: "roofDeck", upstand: "upstand", pir: "pir", top: "topDeck" };
    Object.values(map).forEach((v) => show.delete(v));
    d.rShow.forEach((k) => map[k] && show.add(map[k]));
  }
  if (d.showDims === false) show.delete("dims");
  d.show = [...show];
  d.renderMode = d.fDeckMode === "solid" || d.rGhost === false ? "solid" : "ghost";
  return d;
}

const MIN_PIECE = 8;
const MIN_OFFCUT = 200;
/* The shortest batten worth cutting. Over a door head the arithmetic can
   leave a 40 mm stub; putting it on the cut list makes the list untrue. */
const BATTEN_MIN = 150;
/* How far a joint in one layer of roof PIR has to clear the joint below it.
   Two boards butted with their joints in line is a chimney through the tray;
   a board's width is 1200, so a few hundred millimetres is a real offset
   rather than a rounding. */
const PIR_STAGGER_MIN = 150;
const GUTTER = 75;

const r = (n) => Math.round(n);

/* How many decimal places a step implies. Everything structural here is
   whole millimetres, so a step of 1 or more means integers — but a g-value,
   an air-change rate and a conductivity are not millimetres, and rounding
   those to integers destroys them. */
const stepPlaces = (step) => {
  const t = String(step);
  const dot = t.indexOf(".");
  return dot < 0 ? 0 : t.length - dot - 1;
};

/* Snap a typed number to its field's precision and range. Integer fields
   behave exactly as they always did; fractional ones survive. */
function quantise(n, { min = -Infinity, max = Infinity, step = 1 } = {}) {
  if (!Number.isFinite(n)) return null;
  const f = 10 ** stepPlaces(step);
  return Math.max(min, Math.min(max, Math.round(n * f) / f));
}
const fmt = (n) => r(n).toLocaleString("en-GB");
const m2 = (mm2) => (mm2 / 1e6).toFixed(2);
const sideLabel = (id) => SIDES.find((s) => s.id === id)?.label ?? id;

/* ------------------------ sheet layout ------------------------ */

function splitAxis(D, P, g, mode) {
  const capacity = (k) => k * P + Math.max(0, k - 1) * g;

  /* Equal division: the fewest pieces that fit, all the same size. Wastes
     more sheet than packing full ones, but it never leaves a ribbon, which
     on wall sheathing is the trade worth making. */
  if (mode === "equal") {
    const n = Math.max(1, Math.ceil((D + g) / (P + g)));
    const each = (D - (n - 1) * g) / n;
    if (n === 1) return [{ len: r(D), cut: Math.abs(D - P) > 0.5 }];
    return Array.from({ length: n }, () => ({ len: r(each), cut: Math.abs(each - P) > 0.5 }));
  }

  const nFull = Math.max(0, Math.floor((D + g) / (P + g)));
  if (nFull === 0) return [{ len: r(D), cut: true }];

  const segs = [];
  const fulls = (k) => {
    for (let i = 0; i < k; i++) segs.push({ len: P, cut: false });
  };

  if (mode === "halfEdge") {
    const head = Math.round(P / 2);
    const after = D - head - g;
    const k = Math.max(0, Math.floor((after + g) / (P + g)));
    segs.push({ len: head, cut: true });
    fulls(k);
    const tail = after - capacity(k) - (k > 0 ? g : 0);
    if (tail > MIN_PIECE) segs.push({ len: r(tail), cut: true });
    return segs;
  }

  const rem = D - capacity(nFull);

  if (mode === "edge") {
    fulls(nFull);
    const tail = rem - g;
    if (tail > MIN_PIECE) segs.push({ len: r(tail), cut: true });
    return segs;
  }

  if (rem <= MIN_PIECE) {
    fulls(nFull);
    return segs;
  }
  const end = (rem - 2 * g) / 2;
  if (end > MIN_PIECE) {
    segs.push({ len: r(end), cut: true });
    fulls(nFull);
    segs.push({ len: r(end), cut: true });
    return segs;
  }
  fulls(nFull);
  const tail = rem - g;
  if (tail > MIN_PIECE) segs.push({ len: r(tail), cut: true });
  return segs;
}

function layoutOption(L, W, sheetX, sheetZ, g, align, stagger, originX = 0, originZ = 0,
                      rowAlign = null) {
  const rows = splitAxis(W, sheetZ, g, rowAlign || align);
  /* Equal division is already sliver-free, so there is nothing to stagger. */
  const xModeFor = (ri) =>
    !stagger || ri % 2 === 0 || align === "equal"
      ? align
      : align === "centre" ? "edge" : "halfEdge";

  const pieces = [];
  const xJoints = new Set();
  const zJoints = new Set();
  let z = originZ;

  rows.forEach((row, ri) => {
    const cols = splitAxis(L, sheetX, g, xModeFor(ri));
    let x = originX;
    cols.forEach((col, ci) => {
      pieces.push({ x, z, dx: col.len, dz: row.len, full: !col.cut && !row.cut, row: ri });
      if (ci < cols.length - 1) xJoints.add(x + col.len + g / 2);
      x += col.len + g;
    });
    if (ri < rows.length - 1) zJoints.add(z + row.len + g / 2);
    z += row.len + g;
  });

  return {
    pieces,
    rows,
    sheetX,
    sheetZ,
    xJoints: [...xJoints].sort((a, b) => a - b),
    zJoints: [...zJoints].sort((a, b) => a - b),
  };
}

/* Which way round to lay the sheet, and what to optimise for.

   orient: "auto" | "along" (long side follows the length) | "across"
   align:  "auto" | "centre" | "edge"
   goal:   "cuts"     fewest pieces to cut
           "material" least waste
           "largest"  biggest pieces — avoids narrow strips even at the cost
                      of an extra sheet, which is usually the right trade on
                      wall sheathing where a 60 mm ribbon is a nuisance to
                      fix and does nothing for racking. */
const SLIVER = 300;
function planSheets({
  length: L, width: W, board, gap: g, align, stagger,
  originX = 0, originZ = 0, goal = "cuts", orient = "auto", rowAlign = null,
}) {
  const boardArea = board.l * board.w;
  const score = (o) => {
    let full = 0;
    let cutArea = 0;
    let cuts = 0;
    let slivers = 0;
    let narrowest = Infinity;
    o.pieces.forEach((p) => {
      if (p.full) full += 1;
      else {
        cuts += 1;
        cutArea += p.dx * p.dz;
      }
      const thin = Math.min(p.dx, p.dz);
      narrowest = Math.min(narrowest, thin);
      if (thin < SLIVER) slivers += 1;
    });
    return { cuts, material: full + cutArea / boardArea, slivers, narrowest };
  };

  const orients = orient === "across" ? ["across"]
    : orient === "along" ? ["along"] : ["along", "across"];
  const aligns = align === "auto"
    ? (goal === "largest" ? ["equal", "edge", "centre"] : ["centre", "edge"])
    : [align];

  const candidates = [];
  orients.forEach((tag) => {
    aligns.forEach((al) => {
      const sx = tag === "along" ? board.l : board.w;
      const sz = tag === "along" ? board.w : board.l;
      candidates.push({ tag, align: al,
        o: layoutOption(L, W, sx, sz, g, al, stagger, originX, originZ, rowAlign) });
    });
  });

  candidates.forEach((c) => (c.s = score(c.o)));
  candidates.sort((a, b) => {
    if (goal === "largest") {
      if (a.s.slivers !== b.s.slivers) return a.s.slivers - b.s.slivers;
      if (Math.abs(a.s.narrowest - b.s.narrowest) > 1) return b.s.narrowest - a.s.narrowest;
      return a.s.cuts - b.s.cuts;
    }
    if (goal === "cuts") {
      if (a.s.cuts !== b.s.cuts) return a.s.cuts - b.s.cuts;
      return a.s.material - b.s.material;
    }
    if (Math.abs(a.s.material - b.s.material) > 0.01) return a.s.material - b.s.material;
    return a.s.cuts - b.s.cuts;
  });

  const best = candidates[0];
  const other = candidates[1];
  const whole = best.o.pieces.filter((x) => x.full).length;
  /* A sheet whose long side beats both frame dimensions can never land whole,
     whichever way you turn it. Otherwise it's the layout, not the sheet. */
  const impossible = board.l > Math.max(L, W);
  return {
    ...best.o,
    area: L * W,
    board,
    orient: best.tag,
    align: best.align,
    cuts: best.s.cuts,
    whole,
    impossible,
    slivers: best.s.slivers,
    narrowest: best.s.narrowest === Infinity ? 0 : r(best.s.narrowest),
    material: best.s.material,
    alternative: other
      ? {
          orient: other.tag, align: other.align, cuts: other.s.cuts,
          material: other.s.material, slivers: other.s.slivers,
          whole: other.o.pieces.filter((x) => x.full).length,
        }
      : null,
  };
}

/* ------------------- global sheet allocation ------------------- */

function allocateOnce(demands, board, opts = {}) {
  const pool = [];
  const log = [];
  let bought = 0;
  let reused = 0;
  let pieced = 0;
  let gaps = 0;
  let gapMax = 0;

  /* How short a piece is allowed to fall and still count as fitting. A
     millimetre is nothing behind a taped and foamed joint, and so is five —
     the leftovers were full of boards a single millimetre under the bay they
     were cut beside, because a 1200 board taken for a 694 bay leaves 506 and
     the next bay along is 507. Sheathing gets none of this: a structural
     sheet that short is a gap, not a tolerance, and only the insulation
     allocation passes a figure. Every gap relied on is counted and reported
     rather than quietly closed. */
  const tol = opts.tolerance || 0;
  const noteGap = (mm) => {
    if (mm <= 0) return;
    gaps += 1;
    gapMax = Math.max(gapMax, r(mm));
  };

  /* When a module allows it, a piece can be made up from several offcuts
     rather than needing one big enough to take it whole. `minPiece` is the
     smallest offcut worth cutting up and butting in: a length and a width,
     because a 500 × 80 strip is worth having across a bay and a 250 × 250
     square is worth having whole, and neither passes the other's test. */
  const min = opts.minPiece || null;
  const minL = min ? min.l : 0;
  const minW = min ? min.w : 0;
  const maxParts = opts.maxParts || 4;

  const fits = (p, rect) =>
    (p.dx <= rect.l + tol && p.dz <= rect.w + tol)
    || (p.dz <= rect.l + tol && p.dx <= rect.w + tol);

  /* A remainder earns its place in the pool if it could serve a whole piece,
     or — once pieces may be made up — if it is a strip big enough to be one
     of them. Union, not replacement: dropping the square offcuts to keep the
     strips would trade one kind of reuse for another. */
  const keep = (rect) =>
    (rect.l >= MIN_OFFCUT && rect.w >= MIN_OFFCUT) ||
    !!(min && Math.max(rect.l, rect.w) >= minL && Math.min(rect.l, rect.w) >= minW);

  /* Guillotine as ever: one cut across, one cut up, so every offcut is a
     rectangle you could actually produce on a track saw. */
  const cutFrom = (rect, a, b) => {
    /* Whichever way round overhangs least. It used to swap the moment the
       first orientation did not fit outright, which was harmless while
       nothing overhung at all — but once a piece may run a tolerance over,
       a 3 mm overshoot on one side flipped the piece and turned into a
       600 mm one on the other. */
    const over = (x, y) => Math.max(0, x - rect.l) + Math.max(0, y - rect.w);
    let [x, y] = over(a, b) <= over(b, a) ? [a, b] : [b, a];
    /* Within tolerance the piece can be asked for a millimetre or two more
       than the offcut holds. It is cut to the offcut and the shortfall is a
       gap, so the remainders stay real rectangles rather than going
       negative. */
    const short = r(Math.max(x - rect.l, y - rect.w));
    noteGap(short);
    x = Math.min(x, rect.l);
    y = Math.min(y, rect.w);
    /* Every rectangle knows where it sits on its board, so the two
       remainders of a guillotine cut are placed as well as sized: the piece
       goes in the corner, one remainder is the rest of the board alongside
       it, the other is the strip above it in the piece's own column. Carry
       the positions and the whole plan can be drawn and checked; carry only
       the sizes and it can only be asserted. */
    pool.push(...[
      { x: rect.x + x, y: rect.y, l: rect.l - x, w: rect.w },
      { x: rect.x, y: rect.y + y, l: x, w: rect.w - y },
    ]
      .filter(keep)
      .map((rem) => ({ ...rem, origin: rect.origin, module: rect.module })));
    /* What was cut, where it sits, and what it came out of. Six pieces all
       reported as "from sheet 1" is not something anybody can check. */
    return { cut: { dx: r(x), dz: r(y) },
             at: { x: r(rect.x), y: r(rect.y) },
             outOf: { l: r(rect.l), w: r(rect.w), origin: rect.origin },
             short: short > 0 ? short : 0 };
  };

  const openBoard = (module) => {
    bought += 1;
    return { x: 0, y: 0, l: board.l, w: board.w, origin: `sheet ${bought}`, module };
  };

  /* Smallest offcut that still takes the piece whole, so the big ones stay
     intact for the pieces that need them. */
  const wholeIndex = (p) => {
    const cand = pool.map((rect, i) => ({ rect, i })).filter(({ rect }) => fits(p, rect));
    if (!cand.length) return -1;
    cand.sort((a, b) => a.rect.l * a.rect.w - b.rect.l * b.rect.w);
    return cand[0].i;
  };

  /* Is a piece this size worth cutting and butting in? A length and a width,
     so a 500 × 80 strip counts and an 80 × 80 square does not. */
  const worth = (x, y) => Math.max(x, y) >= minL && Math.min(x, y) >= minW;

  /* How much of `span` to take from `avail`, never leaving a stub too small
     to be a piece in its own right: pull the cut back so what is left is
     still worth cutting. Returns 0 when neither side can be made to work. */
  const clamp = (avail, span) => {
    let t = Math.min(avail, span);
    /* A stub inside the tolerance is a gap to foam, not a piece to cut, so
       the take is only pulled back for one bigger than that. */
    if (span - t > tol && span - t < minW) t = span - minW;
    return t > 0 ? t : 0;
  };

  /* Cover an a × b region from the pool: put the offcut that covers most of
     it into a corner, then cover the two rectangles that cut leaves. Every
     piece is still a rectangle cut from a rectangle, so it is all track-saw
     work — the pieces simply no longer have to be one per bay.

     It covers the region from the pool or not at all. Letting it fall back on
     a board part way through looks like a saving and is not: the recursion
     opens one for each sub-region it cannot finish, and a run that ended up
     buying seventeen boards where whole pieces bought eight is what that
     costs. Buying can only ever go down from here. */
  /* `x0, y0` is where this sub-region sits inside the piece being made up,
     so each part knows its place in the bay as well as on the board. Without
     it the drawings can only show the piece that was planned, not the ones
     you will actually lay. The run is the piece's long side, so the region's
     axes are the piece's own — a part at (x0, y0) of an a × b region is at
     (x0, y0) of the run × cross rectangle. */
  const cover = (x0, y0, a, b, p, taken) => {
    if (a < 1 || b < 1) return true;
    /* A strip left inside the tolerance is covered by the foam gun. */
    if (a <= tol || b <= tol) {
      noteGap(Math.min(a, b));
      return true;
    }
    /* A jigsaw has a limit. Left uncapped this will cover a 2400 × 1182 tray
       board from nineteen offcuts down to 50 mm slivers, which saves boards
       on paper and is not something anybody would cut, carry up a ladder and
       tape. */
    if (taken.length >= maxParts) return false;

    let best = null;
    pool.forEach((rect, i) => {
      [[rect.l, rect.w], [rect.w, rect.l]].forEach(([ua, ub]) => {
        const x = clamp(ua, a);
        const y = clamp(ub, b);
        if (!x || !y || !worth(x, y)) return;
        if (!best || x * y > best.x * best.y) best = { i, x, y };
      });
    });
    if (!best) return false;

    const rect = pool.splice(best.i, 1)[0];
    reused += 1;
    const made = cutFrom(rect, best.x, best.y);
    taken.push({ ...p, ...made.cut, source: rect.origin, reused: true,
      from: rect.module, at: made.at, outOf: made.outOf, short: made.short,
      within: { x: r(x0), y: r(y0) } });
    return cover(x0 + best.x, y0, a - best.x, b, p, taken)
      && cover(x0, y0 + best.y, best.x, b - best.y, p, taken);
  };

  /* Make the piece up from what is lying about rather than opening a board.
     All or nothing, and the pool is put back untouched if it cannot finish. */
  const tile = (p) => {
    const snapshot = pool.map((x) => ({ ...x }));
    const reusedBefore = reused;
    const gapsBefore = gaps;
    const gapMaxBefore = gapMax;
    const taken = [];
    if (!cover(0, 0, Math.max(p.dx, p.dz), Math.min(p.dx, p.dz), p, taken)) {
      pool.length = 0;
      pool.push(...snapshot);
      reused = reusedBefore;
      /* An abandoned attempt leaves no gaps behind either. */
      gaps = gapsBefore;
      gapMax = gapMaxBefore;
      return false;
    }
    /* The whole piece each part belongs to, so the parts can be checked
       against what they are meant to cover — and which way round the run
       was taken, since `within` is in run × cross and the piece may be the
       other way about. */
    const of = { dx: p.dx, dz: p.dz, swapped: p.dz > p.dx };
    taken.forEach((t, i) => log.push({ ...t, of, part: i + 1, parts: taken.length }));
    if (taken.length > 1) pieced += 1;
    return true;
  };

  demands.forEach((p) => {
    if (p.full) {
      bought += 1;
      log.push({ ...p, source: `sheet ${bought}`, reused: false, short: 0,
        at: { x: 0, y: 0 },
        outOf: { l: board.l, w: board.w, origin: `sheet ${bought}` } });
      return;
    }

    const whole = wholeIndex(p);
    if (whole >= 0) {
      const rect = pool.splice(whole, 1)[0];
      reused += 1;
      const made = cutFrom(rect, p.dx, p.dz);
      /* Logged as cut, not as asked for: inside the tolerance the two differ
         by a millimetre or two and the report should say what you saw. */
      log.push({ ...p, ...made.cut, source: rect.origin, reused: true,
        from: rect.module, at: made.at, outOf: made.outOf, short: made.short });
      return;
    }

    /* Nothing takes it whole. Where the module allows a made-up piece, use
       what is lying about rather than opening a board for it. */
    if (min && p.piece && tile(p)) return;

    const rect = openBoard(p.module);
    const made = cutFrom(rect, p.dx, p.dz);
    log.push({ ...p, ...made.cut, source: rect.origin, reused: false,
      at: made.at, outOf: made.outOf, short: made.short });
  });

  return { bought, reused, pieced, gaps, gapMax, log, pool };
}

/* One pass is one order, and the order decides the answer. A demand met early
   sees an empty pool and opens a board; the offcut that would have taken it
   whole turns up three demands later and sits there for good. On a default
   pod that left a 376 × 1200 and a 2382 × 518 in the pile alongside twelve
   356 × 725 floor bays that either would have taken — bought, not because
   nothing fitted, but because nothing fitted *yet*.

   So run it several ways and keep the best, exactly as `planSheets` picks
   between sheet layouts rather than decreeing one. None of these is right in
   general: module order reversed wins on a default pod, largest-first on
   others, and the order as handed in on others again. Sorting is stable and
   the tie-breaks are total, so the same design always gives the same answer. */
const demandArea = (d) => d.dx * d.dz;
const demandLong = (d) => Math.max(d.dx, d.dz);
const byModule = (ds, reverse) => {
  const order = [...new Set(ds.map((d) => d.module))];
  if (reverse) order.reverse();
  return order.flatMap((m) => ds.filter((d) => d.module === m));
};
const bigFirst = (ds) =>
  [...ds].sort((a, b) => demandArea(b) - demandArea(a) || demandLong(b) - demandLong(a));

const DEMAND_ORDERS = [
  (ds) => ds,
  (ds) => [...ds].reverse(),
  bigFirst,
  (ds) => [...ds].sort((a, b) => demandArea(a) - demandArea(b) || demandLong(a) - demandLong(b)),
  (ds) => [...ds].sort((a, b) => demandLong(b) - demandLong(a) || demandArea(b) - demandArea(a)),
  (ds) => byModule(ds, true),
  (ds) => [...new Set(ds.map((d) => d.module))].reverse()
    .flatMap((m) => bigFirst(ds.filter((d) => d.module === m))),
  (ds) => byModule(ds, false),
];
const leftOver = (run) => run.pool.reduce((t, x) => t + x.l * x.w, 0);

function allocateSheets(demands, board, opts = {}) {
  const runs = DEMAND_ORDERS.map((f) => allocateOnce(f(demands), board, opts));
  /* Boards first, then material left, then joints: a gap to foam is cheap
     and a board is not, but between two answers that cost the same, take the
     one with less to tape. */
  runs.sort((a, b) =>
    a.bought - b.bought || leftOver(a) - leftOver(b)
    || a.pieced - b.pieced || a.gaps - b.gaps);
  return runs[0];
}

/* ---------------------------- framing ---------------------------- */

/* `spacing` is a maximum centre-to-centre distance by default, which is the
   convention every span table uses. Read as a clear bay instead, it becomes
   the width of the thing you have to cut and fit between the members — and
   insulation comes in 1200 mm boards, so a 400 mm clear bay takes three
   strips out of one with nothing left over, where 400 mm centres leaves a
   356 mm bay and 132 mm of offcut per board. Neither is wrong; they answer
   different questions. */
function centresFor(spacing, t, mode) {
  return mode === "clear" ? spacing + t : spacing;
}

function joistCentres({ length: L, t, spacing: S, joistAlign, jointOffset = 0 }, plan) {
  const first = t / 2;
  const last = L - t / 2;
  const span = L - t;

  if (joistAlign === "even" || !plan || !plan.xJoints.length) {
    const n = Math.max(0, Math.floor(span / S));
    const endBay = n > 0 ? (span - (n - 1) * S) / 2 : span;
    return { centres: Array.from({ length: n }, (_, i) => first + endBay + i * S), anchors: [] };
  }

  /* Sheet joints arrive in the sheathing plan's own coordinates. A wall that
     butts between two others starts partway along its face, and its sheathing
     may run past the frame at each end, so the joint has to be brought back
     into frame coordinates or the studs land the inset away from it. The
     floor gets away without this only because its deck and frame share an
     origin. */
  const anchors = plan.xJoints
    .map((j) => j - jointOffset)
    .filter((j) => j > first + t && j < last - t);
  const nodes = [first, ...anchors, last];
  const centres = [...anchors];
  for (let i = 0; i < nodes.length - 1; i++) {
    const a = nodes[i];
    const dist = nodes[i + 1] - a;
    const k = Math.max(1, Math.ceil(dist / S)) - 1;
    for (let j = 1; j <= k; j++) centres.push(a + (dist * j) / (k + 1));
  }
  centres.sort((a, b) => a - b);
  return { centres, anchors };
}

function buildFrame(p, plan) {
  const { length: L, width: W, t, d, doubleRim, nogginRows, joistAlign } = p;
  const spacing = centresFor(p.spacing, t, p.spacingMode);
  const members = [];
  const push = (role, x0, z0, dx, dz, len, axis) =>
    members.push({ role, x0: r(x0), z0: r(z0), dx: r(dx), dz: r(dz), len: r(len), axis, dy: d });

  const rimDepth = doubleRim ? 2 * t : t;

  if (doubleRim) {
    push("Outer skin", 0, 0, L, t, L, "x");
    push("Outer skin", 0, W - t, L, t, L, "x");
    push("End joist", 0, t, t, W - 2 * t, W - 2 * t, "z");
    push("End joist", L - t, t, t, W - 2 * t, W - 2 * t, "z");
    push("Rim joist", t, t, L - 2 * t, t, L - 2 * t, "x");
    push("Rim joist", t, W - 2 * t, L - 2 * t, t, L - 2 * t, "x");
  } else {
    push("Rim joist", 0, 0, L, t, L, "x");
    push("Rim joist", 0, W - t, L, t, L, "x");
    push("End joist", 0, t, t, W - 2 * t, W - 2 * t, "z");
    push("End joist", L - t, t, t, W - 2 * t, W - 2 * t, "z");
  }

  const joistLen = W - 2 * rimDepth;
  const { centres, anchors } = joistCentres({ ...p, spacing }, plan);
  centres.forEach((c) => push("Joist", c - t / 2, rimDepth, t, joistLen, joistLen, "z"));

  let rowZ = [];
  if (joistAlign === "deck" && plan && plan.zJoints.length)
    rowZ = plan.zJoints.filter((z) => z > rimDepth + t && z < W - rimDepth - t);
  if (!rowZ.length && nogginRows > 0)
    rowZ = Array.from(
      { length: nogginRows },
      (_, i) => rimDepth + (joistLen * (i + 1)) / (nogginRows + 1)
    );

  if (centres.length) {
    const solid = [[0, t], [L - t, L], ...centres.map((c) => [c - t / 2, c + t / 2])]
      .sort((a, b) => a[0] - b[0]);
    const gaps = [];
    for (let i = 0; i < solid.length - 1; i++) {
      const a = r(solid[i][1]);
      const b = r(solid[i + 1][0]);
      if (b - a > 20) gaps.push([a, b - a]);
    }
    rowZ.forEach((zc) =>
      gaps.forEach(([x0, len]) => push("Noggin", x0, zc - t / 2, len, t, len, "x"))
    );
  }

  const all = [t / 2, ...centres, L - t / 2];
  let maxBay = 0;
  for (let i = 0; i < all.length - 1; i++) maxBay = Math.max(maxBay, all[i + 1] - all[i]);

  /* The clear span between joist faces, not centre to centre. A sheet joint
     landing a hand's width from the end leaves a strip nothing can be cut
     for — and the cause is the sheet layout, not the framing, which is only
     following the joint it was told to. Reported so the panel can say which
     one to change. */
  const faces = [[0, t], ...centres.map((c) => [c - t / 2, c + t / 2]), [L - t, L]]
    .sort((a, b) => a[0] - b[0]);
  let narrowestBay = Infinity;
  for (let i = 0; i < faces.length - 1; i++)
    narrowestBay = Math.min(narrowestBay, faces[i + 1][0] - faces[i][1]);

  return { members, centres, anchors, n: centres.length, joistLen, rimDepth,
           nBays: centres.length + 1, nogginRowZ: rowZ, maxBay, spacing,
           narrowestBay: narrowestBay === Infinity ? 0 : r(narrowestBay) };
}

/* Free spans along one axis: merge what is occupied, round the boundaries,
   then take the widths from the rounded numbers. Rounding a start and a width
   separately is what once left framing members a millimetre inside each
   other, so the order here is deliberate and worth keeping. */
function freeSpans(occupied, from, to, min = MIN_PIECE) {
  const merged = [];
  [...occupied]
    .map(([a, b]) => [Math.max(a, from), Math.min(b, to)])
    .filter(([a, b]) => b > a)
    .sort((a, b) => a[0] - b[0])
    .forEach(([a, b]) => {
      const last = merged[merged.length - 1];
      if (last && a <= last[1]) last[1] = Math.max(last[1], b);
      else merged.push([a, b]);
    });

  const spans = [];
  let cursor = from;
  merged.forEach(([a, b]) => {
    if (a > cursor) spans.push([cursor, a]);
    cursor = Math.max(cursor, b);
  });
  if (cursor < to) spans.push([cursor, to]);

  return spans
    .map(([a, b]) => ({ at: r(a), len: r(b) - r(a) }))
    .filter((s) => s.len >= min);
}

/* PIR in the floor sits between the joists, so its pieces are the bays
   themselves: the free spans across the frame crossed with the free spans
   along it. Irregular compared with the roof's rectangular tray, but these
   are the shapes you would actually cut, and cutting them from the same
   boards means offcuts pool with the roof whenever the thickness matches.

   Depth is the joists': board thicker than the joists are deep cannot go
   between them, which the caller is told rather than silently trimmed.

   A joist landing just inside the end joist leaves a genuine but useless
   sliver of a bay — an 18 mm strip is something you foam, not something you
   cut from a board. Anything narrower than `minBay` is reported separately
   rather than put on the cut list as a piece nobody would cut. */
function floorInsulation(frame,
                         { length: L, width: W, thickness, joistDepth, minBay = 50 }) {
  const deeper = thickness > joistDepth;
  if (!thickness || !frame.members.length)
    return { pieces: [], area: 0, thickness: thickness || 0, bays: 0, joistDepth,
             deeper: false, airGap: 0, slivers: 0, sliverArea: 0, minBay };

  /* Joists and end joists run across the frame and divide it along x;
     rims, outer skins and noggins run along it and divide it across z. */
  const xSpans = freeSpans(
    frame.members.filter((m) => m.axis === "z").map((m) => [m.x0, m.x0 + m.dx]), 0, L);
  const zSpans = freeSpans(
    frame.members.filter((m) => m.axis === "x").map((m) => [m.z0, m.z0 + m.dz]), 0, W);

  const pieces = [];
  const slivers = [];
  zSpans.forEach((row) =>
    xSpans.forEach((col) => {
      const bay = { x: col.at, z: row.at, dx: col.len, dz: row.len, full: false };
      (Math.min(col.len, row.len) < minBay ? slivers : pieces).push(bay);
    }));

  return {
    pieces,
    area: pieces.reduce((a, p) => a + p.dx * p.dz, 0),
    thickness,
    joistDepth,
    minBay,
    slivers: slivers.length,
    sliverArea: slivers.reduce((a, p) => a + p.dx * p.dz, 0),
    bays: pieces.length,
    /* A board thinner than the joists leaves a void above or below it. Worth
       saying: it is a choice, not a fault, but it is not a filled bay. */
    airGap: Math.max(0, joistDepth - thickness),
    deeper,
  };
}

/* Battens holding the floor PIR up. The board sits high in the joist, flush
   with the underside of the deck, so there is an air gap beneath it and
   nothing stopping it dropping into that gap — a ledger each side of the bay
   is what holds it.

   Laid **on edge** the 50 mm face goes against the joist and a 22 mm edge
   carries the board, which is the user's 2 × 1 doing what it is for: a deep
   fixing into the joist and a small ledge. Laid **flat** it is the other way
   about — a 50 mm ledge that only needs 22 mm of air gap to fit, which is
   the way round to use when the board nearly fills the joist.

   The length is a **share of the bay**, not the whole of it. A board does not
   need carrying along its entire edge to stay up, and 80% of a 500 mm bay is
   a 400 mm piece rather than a 500 mm one. Centred, so the unsupported ends
   are equal. */
function joistBattens(pir, p) {
  const { section, onEdge = true, pct = 80, min = BATTEN_MIN } = p;
  const deep = onEdge ? section.d : section.t;
  const ledge = onEdge ? section.t : section.d;
  const airGap = pir.airGap || 0;

  const pieces = [];
  const short = [];
  (pir.pieces || []).forEach((bay) => {
    const len = r((bay.dz * pct) / 100);
    /* A ledger shorter than a batten is worth cutting holds nothing worth
       holding, and the bay wants a different answer — netting, or a board
       cut to a push fit. */
    if (len < min || bay.dx <= 2 * ledge) {
      short.push({ ...bay, len });
      return;
    }
    const z = r(bay.z + (bay.dz - len) / 2);
    pieces.push({ x: r(bay.x), z, dx: ledge, dz: len, side: "left" });
    pieces.push({ x: r(bay.x + bay.dx - ledge), z, dx: ledge, dz: len, side: "right" });
  });

  const rows = new Map();
  pieces.forEach((x) => rows.set(x.dz, (rows.get(x.dz) || 0) + 1));

  return {
    pieces, short: short.length, pct, onEdge, deep, ledge, section,
    count: pieces.length,
    linearM: pieces.reduce((t, x) => t + x.dz, 0) / 1000,
    cuts: [...rows.entries()]
      .map(([len, qty]) => ({ len: Number(len), qty, role: "Joist batten" }))
      .sort((a, b) => b.len - a.len),
    /* It has to fit in the gap under the board. With none there is nothing
       to hold the PIR off in the first place, and laid on edge it wants
       50 mm where laid flat it wants 22. */
    airGap: r(airGap),
    fits: airGap >= deep - 0.5,
    fitsFlat: airGap >= Math.min(section.t, section.d) - 0.5,
  };
}

/* PIR in a wall, between the studs and the noggins. Unlike a deck, a wall is
   not a clean grid: cripples exist only above and below an opening, noggins
   skip the opening zone, and the opening itself is a void that no member
   blocks out. So the bays are found a column at a time — the free spans
   between the vertical members, and then, within each column, the free spans
   between whatever horizontal members actually cross it.

   A glazed wall has no bays at all, which falls out of this rather than being
   special-cased: its opening spans everything between the end studs. */
function wallInsulation(wall, { thickness, studDepth, minBay = 50 }) {
  const frame = wall && wall.frame;
  const empty = {
    pieces: [], area: 0, thickness: thickness || 0, bays: 0, studDepth,
    deeper: false, airGap: 0, slivers: 0, sliverArea: 0, minBay,
    side: wall && wall.side,
  };
  if (!thickness || !frame || !frame.members.length) return empty;

  /* A sheared member — the header of a glazed wall under a fall — rises
     across its run, so both its underside and its top vary with position.
     Taking the lowest underside and the highest top keeps insulation out of
     the timber everywhere along it rather than only at one end. `slopeTopY`
     is handled the same way for members that carry it. */
  const spanOf = (m) => {
    const rise = m.shearY ? m.shearY * m.dx : 0;
    const lo = Math.min(m.y, m.y + rise);
    let hi = Math.max(m.y + m.dy, m.y + m.dy + rise);
    if (m.slopeTopY) hi = Math.max(hi, m.slopeTopY[0], m.slopeTopY[1]);
    return [lo, hi];
  };
  const top = Math.max(...frame.members.map((m) => spanOf(m)[1]));

  const upright = frame.members.filter((m) => m.dy > m.dx);
  const flat = frame.members.filter((m) => m.dy <= m.dx);
  const cols = freeSpans(upright.map((m) => [m.x, m.x + m.dx]), 0, wall.len);

  const o = frame.opening;
  const pieces = [];
  const slivers = [];

  cols.forEach((col) => {
    const a0 = col.at;
    const a1 = col.at + col.len;
    const occ = flat
      .filter((m) => m.x < a1 - 0.5 && m.x + m.dx > a0 + 0.5)
      .map(spanOf);
    /* The opening is a hole, not a member, so nothing in the frame excludes
       it. Any column reaching into it is cut short here instead. */
    if (o && a0 < o.x1 - 0.5 && a1 > o.x0 + 0.5)
      occ.push([o.sillY, o.sillY + o.roH]);

    freeSpans(occ, 0, top).forEach((row) => {
      const bay = { x: a0, y: row.at, dx: col.len, dy: row.len };
      (Math.min(col.len, row.len) < minBay ? slivers : pieces).push(bay);
    });
  });

  return {
    ...empty,
    pieces,
    area: pieces.reduce((a, p) => a + p.dx * p.dy, 0),
    bays: pieces.length,
    slivers: slivers.length,
    sliverArea: slivers.reduce((a, p) => a + p.dx * p.dy, 0),
    airGap: Math.max(0, studDepth - thickness),
    deeper: thickness > studDepth,
  };
}

function checkSupport(plan, frame) {
  const zRun = frame.members.filter((m) => m.axis === "z").map((m) => [m.x0, m.x0 + m.dx]);
  const xRun = frame.members.filter((m) => m.axis === "x").map((m) => [m.z0, m.z0 + m.dz]);
  const ok = (pos, ranges) => ranges.some(([a, b]) => pos - 8 >= a && pos + 8 <= b);
  const joints = [
    ...plan.xJoints.map((pos) => ({ axis: "x", pos, ok: ok(pos, zRun) })),
    ...plan.zJoints.map((pos) => ({ axis: "z", pos, ok: ok(pos, xRun) })),
  ];
  return { joints, unsupported: joints.filter((j) => !j.ok) };
}

/* --------------------------- foil tape ---------------------------

   Every PIR joint gets taped, and that is two different things at once: the
   butt joint where one board meets the next, and the perimeter where a board
   meets the timber around it. Both are an air path if they are open, so both
   are measured.

   **A seam shared by two boards is one seam.** Summing each board's perimeter
   double-counts every butt joint in the tray, which on a roof made up from
   offcuts is most of the total — the figure comes out roughly twice what you
   would buy. So the edges are gathered per line and merged: at each line the
   sweep counts how many board edges cover a stretch, and two or more means a
   butt joint where one means a perimeter run against timber. The two are
   reported apart because they are different jobs on site, and because it is
   the joint figure the roof trade-off turns on. */

const TAPE_MIN_LAP = 25;
/* Tape is about 0.05 mm thick, which is below the size at which podBoxes will
   emit a solid at all. It is drawn at 1 mm so it can be seen; that is a
   drawing decision and nothing measures it. */
const TAPE_DRAWN_T = 1;

/* The seam lines of a set of rectangles laid in one plane, classified. */
function seamRuns(rects, min = MIN_PIECE) {
  const lines = new Map();
  const put = (axis, at, lo, hi) => {
    if (hi - lo < min) return;
    const key = `${axis}|${r(at)}`;
    if (!lines.has(key)) lines.set(key, { axis, at: r(at), iv: [] });
    lines.get(key).iv.push([r(lo), r(hi)]);
  };
  rects.forEach((p) => {
    const a0 = p.a, a1 = p.a + p.da, b0 = p.b, b1 = p.b + p.db;
    put("a", a0, b0, b1);
    put("a", a1, b0, b1);
    put("b", b0, a0, a1);
    put("b", b1, a0, a1);
  });

  const runs = [];
  lines.forEach(({ axis, at, iv }) => {
    const cuts = [...new Set(iv.flat())].sort((x, y) => x - y);
    let open = null;
    for (let i = 0; i < cuts.length - 1; i += 1) {
      const lo = cuts[i];
      const hi = cuts[i + 1];
      const mid = (lo + hi) / 2;
      const depth = iv.filter(([a, b]) => a < mid && b > mid).length;
      const kind = depth >= 2 ? "joint" : depth === 1 ? "perimeter" : null;
      if (kind && open && open.kind === kind && Math.abs(open.to - lo) < 0.5) open.to = hi;
      else if (kind) runs.push((open = { axis, at, from: lo, to: hi, kind }));
      else open = null;
    }
  });

  return runs
    .map((x) => ({ ...x, len: r(x.to - x.from) }))
    .filter((x) => x.len >= min)
    .sort((a, b) => a.axis.localeCompare(b.axis) || a.at - b.at || a.from - b.from);
}

/* One tape can bridge two joints at once when they are close enough, and
   over a joist they always are: the two sides of a 44 mm joist are 44 mm
   apart, and a 100 mm tape laid down the middle laps 28 mm onto each board.
   Taping each of them separately puts two ribbons over the same piece of
   timber, overlapping each other across it — twice the tape to seal the same
   two gaps, and nobody does it.

   So a run is bridged with its neighbour when one width will reach past both
   with a lap to spare: `width >= (c2 - c1) + 2 * TAPE_MIN_LAP`. The merged
   run's centre line then sits over the timber between the two joints rather
   than over either of them, which is exactly how you run a tape down a
   joist. Where a joint has no partner near enough — a butt joint between two
   boards, or a bay against a wall plate — it keeps its own tape centred on
   it, which is the other half of the same rule.

   Only PIR-to-timber runs are paired. A butt joint is already one tape over
   one line and has no partner across a member to find. */
const mergeIv = (iv) => {
  const out = [];
  [...iv].sort((a, b) => a[0] - b[0]).forEach(([a, b]) => {
    const last = out[out.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b);
    else out.push([a, b]);
  });
  return out;
};
const interIv = (a, b) => {
  const out = [];
  a.forEach(([a0, a1]) => b.forEach(([b0, b1]) => {
    const lo = Math.max(a0, b0);
    const hi = Math.min(a1, b1);
    if (hi - lo >= MIN_PIECE) out.push([lo, hi]);
  }));
  return mergeIv(out);
};
const subIv = (a, b) => {
  const out = [];
  a.forEach(([a0, a1]) => {
    let cursor = a0;
    b.forEach(([b0, b1]) => {
      if (b1 <= cursor || b0 >= a1) return;
      if (b0 > cursor) out.push([cursor, Math.min(b0, a1)]);
      cursor = Math.max(cursor, b1);
    });
    if (cursor < a1) out.push([cursor, a1]);
  });
  return out.filter(([lo, hi]) => hi - lo >= MIN_PIECE);
};

function bridgeRuns(runs, width, lap = TAPE_MIN_LAP) {
  if (!(width > 0)) return runs;
  const out = runs.filter((x) => x.kind !== "perimeter");
  const axes = new Map();
  runs.filter((x) => x.kind === "perimeter").forEach((x) => {
    if (!axes.has(x.axis)) axes.set(x.axis, new Map());
    const lines = axes.get(x.axis);
    lines.set(x.at, mergeIv([...(lines.get(x.at) || []), [x.from, x.to]]));
  });

  axes.forEach((lines, axis) => {
    const ats = [...lines.keys()].sort((a, b) => a - b);
    const taken = new Set();
    const emit = (at, iv, bridges) => iv.forEach(([lo, hi]) =>
      out.push({ axis, at: r(at), from: r(lo), to: r(hi), len: r(hi) - r(lo),
        kind: "perimeter", bridges }));
    ats.forEach((at, i) => {
      if (taken.has(at)) return;
      const next = ats[i + 1];
      let mine = lines.get(at);
      if (next == null || taken.has(next) || next - at + 2 * lap > width)
        return void emit(at, mine, 1);
      const theirs = lines.get(next);
      const both = interIv(mine, theirs);
      if (!both.length) return void emit(at, mine, 1);
      taken.add(next);
      emit((at + next) / 2, both, 2);
      emit(at, subIv(mine, both), 1);
      emit(next, subIv(theirs, both), 1);
    });
  });
  return out.sort((a, b) =>
    a.axis.localeCompare(b.axis) || a.at - b.at || a.from - b.from);
}

const tapeTally = (runs) => {
  const of = (kind) => runs.filter((x) => x.kind === kind).reduce((t, x) => t + x.len, 0);
  const joint = of("joint");
  const perimeter = of("perimeter");
  return { runs, joint, perimeter, total: joint + perimeter, count: runs.length };
};

/* Tape over the floor bays, each wall, and every layer of the roof tray.
   Regions keep their own plane — `a` across, `b` along — exactly as the
   fixings do, so podBoxes places them with the transform it already has for
   that stage rather than the geometry being restated here. */
function planTape({ floorPir, wallPir, roofPlans, width = 100,
                    roll = 45000, waste = 0, layers = "all" }) {
  const regions = [];
  const rect = (p, b) => ({ a: p.x, b: p[b], da: p.dx, db: b === "z" ? p.dz : p.dy });
  /* Seams first, then the pairs one tape can bridge together. Doing it in
     that order matters: the bridging needs to see both sides of a member,
     and only the seam pass knows which stretches are joints and which are
     board-against-timber. */
  const tapeOf = (pieces, b) => tapeTally(
    bridgeRuns(seamRuns(pieces.map((p) => rect(p, b))), width));

  if (floorPir && floorPir.pieces.length)
    regions.push({ stage: "floor", key: "floor", label: "Floor",
      ...tapeOf(floorPir.pieces, "z") });

  /* Keyed off SIDES rather than the object's own keys: the component hangs
     its totals on the same object as the walls, and `wallPir.area` is a
     number with no bays in it. */
  SIDES.forEach(({ id: side }) => {
    const w = wallPir && wallPir[side];
    if (!w || !w.pieces || !w.pieces.length) return;
    regions.push({ stage: "walls", key: `wall-${side}`, side,
      label: `${sideLabel(side)} wall`,
      ...tapeOf(w.pieces, "y") });
  });

  /* A second layer of PIR has its own set of joints. Taping only the top one
     is ordinary practice — that is the layer carrying the seal — so it is a
     choice rather than an assumption, and taping every layer is the default
     because that is what "every joint" means. */
  const plans = roofPlans || [];
  const wanted = (i, n) => layers !== "top" || i === n - 1;
  plans.forEach((plan, li) => {
    if (!wanted(li, plans.length) || !plan.pieces || !plan.pieces.length) return;
    regions.push({ stage: "roof", key: `roof-${li}`, layer: li,
      label: plans.length > 1 ? `Roof PIR, layer ${li + 1}` : "Roof PIR",
      ...tapeOf(plan.pieces, "z") });
  });

  const sum = (f) => regions.reduce((t, g) => t + f(g), 0);
  const joint = sum((g) => g.joint);
  const perimeter = sum((g) => g.perimeter);
  const total = joint + perimeter;
  const ordered = total * (1 + Math.max(0, waste) / 100);

  const byStage = {};
  regions.forEach((g) => {
    const e = byStage[g.stage] || (byStage[g.stage] = { joint: 0, perimeter: 0, total: 0 });
    e.joint += g.joint; e.perimeter += g.perimeter; e.total += g.total;
  });

  return {
    regions, byStage, joint, perimeter, total,
    width, roll, waste, layers,
    ordered: r(ordered),
    rolls: roll > 0 ? Math.ceil(ordered / roll) : 0,
    /* Half the width each side of the joint, which is the whole reason the
       width is a setting rather than a constant. */
    lap: r(width / 2),
    lapShort: width / 2 < TAPE_MIN_LAP,
  };
}

/* What the piecing costs in tape: the same pod measured as cut against the
   same pod in whole planned pieces. A board made up from four offcuts carries
   three butt joints the planned one does not, and those joints are what a
   board saved is being traded against — so both sides of that trade have to
   cover the same ground. Comparing the roof's extra joints against a saving
   counted across the floor, the walls and the roof flatters the boards. */
function tapeExtra(cut, whole) {
  if (!cut || !whole) return null;
  const of = (t, k) => (t.byStage[k] ? t.byStage[k].total : 0);
  const stage = {};
  ["floor", "walls", "roof"].forEach((k) => {
    stage[k] = r(Math.max(0, of(cut, k) - of(whole, k)));
  });
  return {
    total: r(Math.max(0, cut.total - whole.total)),
    cut: r(cut.total), whole: r(whole.total), stage,
  };
}

/* Is the extra tape the piecing costs worth more than the boards it saved?
   Money, not metres, because the two are only comparable once priced — and a
   roll of foil tape against a board of PIR is not an obvious call either way.
   Both prices come from the order sheet, so an unpriced design gets no
   verdict rather than a made-up one. */
function tapeTrade({ extraM, tapePerRoll, roll, boardsSaved, boardPrice }) {
  const perM = roll > 0 && tapePerRoll > 0 ? tapePerRoll / (roll / 1000) : 0;
  if (!(perM > 0) || !(boardPrice > 0) || !(boardsSaved > 0)) return null;
  const tapeCost = (extraM / 1000) * perM;
  const boardCost = boardsSaved * boardPrice;
  return {
    perM: Math.round(perM * 100) / 100,
    tapeCost: Math.round(tapeCost * 100) / 100,
    boardCost: Math.round(boardCost * 100) / 100,
    boardsSaved,
    /* True when the tape those extra joints need costs more than the boards
       the piecing saved — buy the board and lay it whole. */
    boardCheaper: tapeCost > boardCost,
  };
}

/* --------------------------- fixings ---------------------------

   Four kinds, every one of them counted off geometry that is already being
   drawn, so the tally cannot drift from the drawings the way a hand count
   does:

     framing screws   at every timber-to-timber joint
     external screws  at every noggin end
     joist hangers    at every floor joist end, if you are using them
     sheet screws     wherever a sheet crosses a member, at a fixed pitch

   The counts per joint are settings, not advice. Nothing here is a structural
   claim and there is no fixing schedule behind it — set them to whatever your
   own detailing says. */

const FIXINGS = [
  { id: "framing", label: "Framing screws", unit: "each", joint: true },
  { id: "noggin", label: "120 mm external wood screws", unit: "each", joint: true },
  { id: "hanger", label: "Joist hangers", unit: "each", joint: true },
  { id: "sheet", label: "50 mm OSB screws", unit: "each", joint: false },
];

const FIXING_IDS = FIXINGS.map((f) => f.id);

/* Members that run through their frame rather than landing in it: a rim or a
   plate is the thing everything else is fixed to. Everything not listed butts
   into something at both ends, so it carries two joints — a header included,
   since the trimmers either side are what carry it. */
const THROUGH_ROLES = new Set(["Rim joist", "Outer skin", "Top plate", "Bottom plate"]);

const emptyTally = () => ({ framing: 0, noggin: 0, hanger: 0, sheet: 0 });

const addTally = (into, from) => {
  FIXING_IDS.forEach((k) => (into[k] += from[k]));
  return into;
};

/* Screws march along an overlap at `pitch` centres, centred on the overlap so
   the run is symmetrical and nothing lands hard on a sheet edge. An overlap
   shorter than the pitch still takes one, in the middle: a sheet corner
   clipping a stud is still a sheet corner that needs holding down. */
function fixingRun(a0, a1, pitch) {
  const len = a1 - a0;
  if (len <= 0 || pitch <= 0) return [];
  const n = Math.floor(len / pitch) + 1;
  const start = a0 + (len - (n - 1) * pitch) / 2;
  return Array.from({ length: n }, (_, i) => start + i * pitch);
}

/* One plane of the build, normalised: framing members and the sheets that land
   on them as rectangles in the same 2-D system. `a` runs along the panel and
   `b` across it — x and z on a deck, along and up on a wall. Keeping both in
   one system is the whole trick; it is why the same routine does a floor, a
   wall and a roof without knowing which it has. */
function panelFixings({ members, pieces = [], pitch, jointScrews, nogginScrews,
                        hangers = false }) {
  const points = [];
  const tally = emptyTally();

  members.forEach((m) => {
    if (THROUGH_ROLES.has(m.role)) return;
    const noggin = m.role === "Noggin";
    const kind = noggin ? "noggin" : "framing";
    const qty = noggin ? nogginScrews : jointScrews;
    const mid = { a: (m.a0 + m.a1) / 2, b: (m.b0 + m.b1) / 2 };
    const ends = m.axis === "a"
      ? [{ a: m.a0, b: mid.b }, { a: m.a1, b: mid.b }]
      : [{ a: mid.a, b: m.b0 }, { a: mid.a, b: m.b1 }];
    ends.forEach(({ a, b }) => {
      if (qty > 0) {
        tally[kind] += qty;
        points.push({ a: r(a), b: r(b), kind, qty, role: m.role });
      }
      /* Hangers are a floor-joist fitting, so the caller decides; the roof
         gets truss clips instead and the walls get neither. */
      if (hangers && m.role === "Joist") {
        tally.hanger += 1;
        points.push({ a: r(a), b: r(b), kind: "hanger", qty: 1, role: m.role });
      }
    });
  });

  if (pitch > 0)
    pieces.forEach((p) =>
      members.forEach((m) => {
        const a0 = Math.max(p.a0, m.a0);
        const a1 = Math.min(p.a1, m.a1);
        const b0 = Math.max(p.b0, m.b0);
        const b1 = Math.min(p.b1, m.b1);
        /* Sheets butt up to one another across a member, so two of them can
           share it. Sub-millimetre slivers are contact, not overlap. */
        if (a1 - a0 < 1 || b1 - b0 < 1) return;
        const along = m.axis === "a";
        const cross = along ? (b0 + b1) / 2 : (a0 + a1) / 2;
        fixingRun(along ? a0 : b0, along ? a1 : b1, pitch).forEach((v) => {
          tally.sheet += 1;
          points.push({ a: r(along ? v : cross), b: r(along ? cross : v),
                        kind: "sheet", qty: 1, role: m.role });
        });
      })
    );

  return { points, tally };
}

/* A deck — floor or roof. Frame and sheets already share an origin, which is
   why this one is the easy case. */
function deckPanel(frame, plan) {
  return {
    members: frame.members.map((m) => ({
      role: m.role, axis: m.axis === "x" ? "a" : "b",
      a0: m.x0, a1: m.x0 + m.dx, b0: m.z0, b1: m.z0 + m.dz,
    })),
    pieces: (plan ? plan.pieces : []).map((p) => ({
      a0: p.x, a1: p.x + p.dx, b0: p.z, b1: p.z + p.dz,
    })),
  };
}

/* A wall, where the sheets do not share the frame's origin. Sheathing comes
   out of the plan in plan coordinates: it starts `shift` before the frame, the
   wall itself starts `start` along the building, and its rows are measured
   down from the sheathed top rather than up from the floor. Undo all three or
   every screw lands where the frame is not — the same correction the stud
   anchors need, and the same one that has been got wrong before. */
function wallPanel(w, skirt = 0) {
  const off = w.shift + w.start;
  const top = w.sheathH - skirt;
  return {
    members: w.frame.members.map((m) => ({
      role: m.role, axis: m.dx >= m.dy ? "a" : "b",
      a0: m.x, a1: m.x + m.dx, b0: m.y, b1: m.y + m.dy,
    })),
    /* The cut pieces, so an opening does not collect screws into thin air. */
    pieces: (w.cutPieces || w.plan.pieces).map((p) => ({
      a0: p.x - off, a1: p.x + p.dx - off,
      b0: top - (p.z + p.dz), b1: top - p.z,
    })),
  };
}

/* Every fixing in the build, per panel and in total. Panels keep their points
   so a drawing can show them in its own coordinates; the totals are what the
   order summary buys. */
function planFixings({ floor, walls, roof, jointScrews = 0, nogginScrews = 0,
                       hangers = false, pitch = 0 }) {
  const opts = { pitch, jointScrews, nogginScrews };
  const panels = [];

  if (floor)
    panels.push({ stage: "floor", key: "floor", label: "Floor",
      ...panelFixings({ ...deckPanel(floor.frame, floor.deck), ...opts, hangers }) });

  if (walls)
    walls.walls.forEach((w) =>
      panels.push({ stage: "walls", key: `wall-${w.side}`, label: `${sideLabel(w.side)} wall`,
        side: w.side, ...panelFixings({ ...wallPanel(w, walls.skirt), ...opts }) }));

  if (roof)
    panels.push({ stage: "roof", key: "roof", label: "Roof",
      ...panelFixings({ ...deckPanel(roof.frame, roof.deck), ...opts }) });

  const total = panels.reduce((acc, p) => addTally(acc, p.tally), emptyTally());
  const byStage = {};
  panels.forEach((p) => {
    byStage[p.stage] = addTally(byStage[p.stage] || emptyTally(), p.tally);
  });

  return {
    panels, total, byStage,
    lines: FIXINGS.map((f) => ({ ...f, qty: total[f.id] })).filter((l) => l.qty > 0),
  };
}

/* --------------------------- thermal ---------------------------

   A U-value for the whole envelope, and from it a peak heating and cooling
   load. None of this is a certified calculation and none of it is evidence
   of compliance with anything: it is arithmetic on nominal figures, to tell
   you whether another 25 mm in the walls is worth arguing about. A real
   assessment uses certified lambda values, measured airtightness and the
   combined method of BS EN ISO 6946. Advisory, in exactly the way the cut
   lists are advisory about timber that is never quite its nominal section.

   What it does do better than a spreadsheet: the framing fraction is not
   assumed. Every stud, joist and noggin has already been placed, so the
   insulated area is known and what is left of the gross area is timber.
   That is the bridged fraction, counted rather than guessed at the usual
   15% — and on a pod at 400 centres it is nearer a quarter. */

/* Thermal conductivity, W/m·K. Nominal figures for ordinary materials; a
   real product certificate will differ. */
const LAMBDA = {
  pir: 0.022,
  timber: 0.13,
  osb: 0.13,
  epdm: 0.25,
};

/* Surface resistances, m²K/W, which depend on the direction of heat flow. */
const R_SURFACE = {
  wall: { in: 0.13, out: 0.04 },
  roof: { in: 0.10, out: 0.04 },
  floor: { in: 0.17, out: 0.04 },
};

/* An unventilated cavity, where insulation is shallower than the framing. */
const R_CAVITY = 0.18;

/* Below this the roof is the weak link whatever else you do, so the UI says
   so. Not a regulation, just the point where more PIR stops being the
   argument and everything else starts. */
const ROOF_PIR_MIN = 100;

/* Peak external design temperatures, °C. Winter is what a heater is sized
   against; summer is a warm-day dry bulb, not a record. */
const LOCATIONS = [
  { id: "dublin", label: "Dublin", winter: -3, summer: 24 },
  { id: "cork", label: "Cork", winter: -2, summer: 23 },
  { id: "galway", label: "Galway", winter: -2, summer: 22 },
  { id: "belfast", label: "Belfast", winter: -3, summer: 23 },
  { id: "edinburgh", label: "Edinburgh", winter: -5, summer: 23 },
  { id: "manchester", label: "Manchester", winter: -4, summer: 26 },
  { id: "london", label: "London", winter: -4, summer: 28 },
  { id: "custom", label: "Set my own", winter: -3, summer: 25 },
];

/* Peak solar irradiance on a vertical surface, W/m², by the compass point it
   faces. These are each facade's own worst hour, not one shared moment —
   east peaks in the morning and west in the evening. A pod is glazed on
   essentially one side, so the peak that matters is that side's. Northern
   hemisphere. */
const ORIENTATIONS = [
  { id: "n", label: "North", solar: 130 },
  { id: "ne", label: "North-east", solar: 350 },
  { id: "e", label: "East", solar: 500 },
  { id: "se", label: "South-east", solar: 520 },
  { id: "s", label: "South", solar: 470 },
  { id: "sw", label: "South-west", solar: 520 },
  { id: "w", label: "West", solar: 500 },
  { id: "nw", label: "North-west", solar: 350 },
];

const COMPASS = ["n", "ne", "e", "se", "s", "sw", "w", "nw"];

/* Which way one wall faces, given the compass point the front faces. The
   front is +Z and the rear −Z, so with the front south the rear is north,
   the left wall is west and the right wall east — a quarter turn each way,
   and the whole thing rotates with the front. */
const SIDE_TURN = { front: 0, left: 2, rear: 4, right: 6 };
function facingOf(frontFaces, side) {
  const at = COMPASS.indexOf(frontFaces);
  if (at < 0) return frontFaces;
  return COMPASS[(at + (SIDE_TURN[side] || 0)) % 8];
}

/* Resistance of a stack. A layer is [thickness mm, conductivity], or
   ["R", value] for anything quoted as a resistance outright — a cavity,
   which has no useful conductivity of its own. */
const rOf = (layers) =>
  layers.reduce((r, [a, b]) => {
    if (a === "R") return r + b;
    return r + (a > 0 && b > 0 ? a / 1000 / b : 0);
  }, 0);

/* An insulated bay: the board, and the cavity it leaves if it is shallower
   than the framing around it. */
const bayLayers = (base, pir, depth, lam) => [
  ...base,
  [pir, lam.pir],
  ...(depth - pir > 1 ? [["R", R_CAVITY]] : []),
];

/* One element, as parallel paths through it. U is the area-weighted mean of
   the path conductances: the simplified proportional-area method, not the
   upper/lower-limit combined method of ISO 6946. On a timber frame the two
   agree within a few per cent, and this one can be read off the page. */
function elementU(paths, surface) {
  const live = paths.filter((p) => p.fraction > 0);
  const total = live.reduce((a, p) => a + p.fraction, 0);
  if (total <= 0) return { u: 0, paths: [] };
  const out = live.map((p) => {
    const r = surface.in + surface.out + rOf(p.layers);
    return { label: p.label, fraction: p.fraction / total, r, u: 1 / r };
  });
  return { u: out.reduce((a, p) => a + p.fraction * p.u, 0), paths: out };
}

/* The whole envelope, element by element, off geometry already drawn.

   Bridging is measured, not assumed: an insulated bay's area comes from the
   same list the cut list is made from, so what is left of the gross area is
   timber. The roof is a warm roof — insulation continuous above the deck —
   so it has no repeating bridge at all and its joists sit inside the
   envelope. Linear bridges at the corners, eaves and reveals need ψ values
   this does not have, and are reported as excluded rather than ignored. */
function buildEnvelope({
  floorPir, floorSection, fDeckT, fL, fW,
  walls, wallPir, wallSection, wSheathT,
  roof, roofPir, rDeckT, topT,
  openings = [],
  /* Conductivities are overridable because PIR's varies by product — 0.019
     for a good foil-faced board against 0.026 for a plain one, on the layer
     that dominates the result. */
  lambda = {},
}) {
  const lam = { ...LAMBDA, ...lambda };
  const sqm = (mm2) => mm2 / 1e6;
  const elements = [];

  /* ---- floor: open underneath, so it loses heat like a wall lying down ---- */
  if (fL > 0 && fW > 0 && floorSection) {
    const gross = fL * fW;
    const ins = floorPir ? floorPir.area : 0;
    const pir = floorPir ? Math.min(floorPir.thickness, floorSection.d) : 0;
    const deck = [[fDeckT, lam.osb]];
    elements.push({
      id: "floor", label: "Floor", area: sqm(gross),
      ...elementU([
        { label: "Between the joists", fraction: sqm(ins),
          layers: bayLayers(deck, pir, floorSection.d, lam) },
        { label: "Through a joist", fraction: sqm(Math.max(0, gross - ins)),
          layers: [...deck, [floorSection.d, lam.timber]] },
      ], R_SURFACE.floor),
    });
  }

  /* ---- walls: one element each, so orientation and openings stay apart ---- */
  (walls ? walls.walls : []).forEach((w) => {
    const gross = w.len * walls.height;
    const wp = wallPir && wallPir[w.side];
    const ins = wp ? wp.area : 0;
    const o = w.frame.opening;
    const opaque = gross - (o ? o.roW * o.roH : 0);
    if (opaque <= 0) return;
    const pir = wp ? Math.min(wp.thickness, wallSection.d) : 0;
    const sheath = [[wSheathT, lam.osb]];
    elements.push({
      id: `wall-${w.side}`, label: `${sideLabel(w.side)} wall`, side: w.side,
      area: sqm(opaque),
      ...elementU([
        { label: "Between the studs", fraction: sqm(Math.min(ins, opaque)),
          layers: bayLayers(sheath, pir, wallSection.d, lam) },
        { label: "Through a stud", fraction: sqm(Math.max(0, opaque - ins)),
          layers: [...sheath, [wallSection.d, lam.timber]] },
      ], R_SURFACE.wall),
    });
  });

  /* ---- roof: warm roof, so one unbridged path ---- */
  if (roof) {
    elements.push({
      id: "roof", label: "Roof", area: sqm(roof.L * roof.W),
      ...elementU([
        { label: "Continuous over the deck", fraction: 1,
          layers: [[rDeckT, lam.osb], [roofPir, lam.pir],
                   [topT, lam.osb], [1.5, lam.epdm]] },
      ], R_SURFACE.roof),
    });
  }

  /* ---- openings: a manufacturer's figure, never something to derive ---- */
  openings.filter((o) => o && o.area > 0).forEach((o) =>
    elements.push({ id: o.id, label: o.label, area: o.area, u: o.u,
      side: o.side, g: o.g || 0, opening: true, paths: [] }));

  const area = elements.reduce((a, e) => a + e.area, 0);
  const ua = elements.reduce((a, e) => a + e.area * e.u, 0);
  return {
    elements, area, ua,
    /* One number for the whole box: the area-weighted mean U. */
    u: area > 0 ? ua / area : 0,
    lambda: lam,
    surfaces: R_SURFACE,
    cavityR: R_CAVITY,
    excludes: [
      "Linear bridges at the corners, eaves and window reveals",
      "Thermal mass — these are steady-state figures",
      "Air leakage beyond the ventilation rate you set",
    ],
  };
}

/* Peak loads. Fabric is UA·ΔT; ventilation is air's 0.33 Wh/m³K times the
   changes per hour and the volume.

   Internal gains are deliberately **not** credited against the heating size.
   A heater is sized for the worst case, which is an empty cold room on the
   coldest morning with nothing switched on: credit 150 W of body heat and
   the thing cannot bring the room up before anyone arrives, and fails every
   time the pod sits empty overnight. They are reported instead, with what
   the load falls to once the room is occupied, because the figure is worth
   knowing — it is just not the figure you buy a heater against.

   Cooling is the opposite case: there the gains are real, simultaneous with
   the peak, and adding them is what makes the number honest. The fabric term
   goes negative when it is cooler outside than in, which is the building
   helping. */
function heatLoads({ envelope, volume, airChanges = 0, indoorWinter, indoorSummer,
                     outdoorWinter, outdoorSummer, frontFaces = "s",
                     shading = 1, internalGain = 0, floorArea = 0 }) {
  const vent = 0.33 * airChanges * (volume || 0);

  const dtW = indoorWinter - outdoorWinter;
  const heating = { dt: dtW, fabric: envelope.ua * dtW, ventilation: vent * dtW,
                    internal: internalGain };
  heating.total = heating.fabric + heating.ventilation;
  /* What is left for the heater once the room is occupied and the kit is on.
     Reported, never the sizing figure. */
  heating.occupied = Math.max(0, heating.total - internalGain);

  const solarOn = (side) => {
    const o = ORIENTATIONS.find((x) => x.id === facingOf(frontFaces, side));
    return o ? o.solar : 0;
  };
  const gains = envelope.elements
    .filter((e) => e.opening && e.g > 0)
    .map((e) => ({ id: e.id, label: e.label, side: e.side,
      facing: facingOf(frontFaces, e.side),
      watts: e.area * e.g * solarOn(e.side) * shading }));

  const dtS = outdoorSummer - indoorSummer;
  const solar = gains.reduce((a, x) => a + x.watts, 0);
  const cooling = { dt: dtS, fabric: envelope.ua * dtS, ventilation: vent * dtS,
                    solar, internal: internalGain, gains };
  cooling.total = cooling.fabric + cooling.ventilation + solar + internalGain;

  const per = (w) => (floorArea > 0 ? w / floorArea : 0);
  return {
    heating: { ...heating, perM2: per(heating.total) },
    cooling: { ...cooling, perM2: per(cooling.total) },
    vent,
  };
}

/* ------------------------- timber packing ------------------------- */

function packStock(pieces, stock, kerf = 3) {
  const flat = [];
  pieces.forEach(({ len, qty }) => {
    for (let i = 0; i < qty; i++) flat.push(len);
  });
  flat.sort((a, b) => b - a);
  const bars = [];
  flat.forEach((len) => {
    if (len > stock) return void bars.push({ cuts: [len], used: len, over: true });
    let bar = bars.find((b) => !b.over && b.used + len + (b.cuts.length ? kerf : 0) <= stock);
    if (!bar) bars.push((bar = { cuts: [], used: 0, over: false }));
    bar.used += len + (bar.cuts.length ? kerf : 0);
    bar.cuts.push(len);
  });
  return bars.map((b) => ({ ...b, offcut: r(stock - b.used) }));
}

function rollUp(members) {
  const map = new Map();
  members.forEach((m) => {
    const k = `${m.len}|${m.role}`;
    map.set(k, (map.get(k) || 0) + 1);
  });
  return [...map.entries()]
    .map(([k, qty]) => {
      const [len, role] = k.split("|");
      return { len: Number(len), role, qty };
    })
    .sort((a, b) => b.len - a.len);
}

/* Where the roof falls, how steeply, and the shear that expresses it.
   Water runs to the gutter, so the gutter side is always the low one. */
function fallOf({ gutterSide, buildingL, buildingW, furrLow, furrHigh, furrOn }) {
  const axis = gutterSide === "left" || gutterSide === "right" ? "x" : "z";
  const span = axis === "x" ? buildingL : buildingW;
  if (!furrOn || span <= 0)
    return { on: false, axis, span, rise: 0, slope: 0, low: furrLow, high: furrLow,
             shear: null, ratio: null, degrees: 0, perMetre: 0 };

  const rise = furrHigh - furrLow;
  const slope = rise / span;
  const lowAtStart = gutterSide === "rear" || gutterSide === "left";
  const origin = lowAtStart ? 0 : span;
  return {
    on: true, axis, span, rise, slope: Math.abs(slope),
    low: furrLow, high: furrHigh,
    shear: { axis, origin, slope: lowAtStart ? slope : -slope, base: furrLow },
    ratio: rise > 0 ? Math.round(span / rise) : null,
    degrees: (Math.atan2(rise, span) * 180) / Math.PI,
    perMetre: Math.round((rise / span) * 1000),
    lowAtStart,
    /* Height of the fall plane above the wall plate at a position. */
    heightAt: (pos) => furrLow + (pos - origin) * (lowAtStart ? slope : -slope),
  };
}


/* --------------------------- cladding battens --------------------------- */

/* Battens carry the cladding off the sheathing so water can get out again,
   and which way they run is decided by which way the cladding runs:

   - Horizontal cladding fixes to **vertical** battens, one layer.
   - Vertical cladding fixes to **horizontal** battens, which themselves need
     vertical ones underneath or the drainage path is dammed at every rail.
     Two layers, and the horizontal one is the fixing layer.

   Everything here works on the **sheathed face**: `a` runs along it from the
   left-hand edge of the sheathing, `b` runs up it from the bottom edge. The
   frame's own origin is the bottom plate, which is `skirt` higher, and the
   opening arrives in frame coordinates — so it has to be moved into the face
   before anything is set out against it. This is the same correction the
   stud anchors and the fixings need, in both directions at once. */

/* Where to put a row of members between two fixed ends. `equal` divides the
   run into the fewest equal bays that come in under the spacing, which
   leaves no odd gap anywhere; `fixed` steps at exactly the spacing from the
   left and lets the last bay be whatever is left, which is what you get if
   you set out with a tape. Neither is wrong; they answer different
   questions, and a wall that has to be set out around an opening uses the
   same routine between each pair of anchors. */
function battenRun(lo, hi, width, spacing, mode) {
  /* `lo` is the left anchor's near edge and `hi` the right anchor's far
     edge, so the clear span between the two is what has to be broken up. */
  const clear = hi - lo - 2 * width;
  if (clear <= spacing + 0.5) return [];
  const n = Math.max(0, Math.ceil((clear - spacing) / (spacing + width)));
  if (n <= 0) return [];
  const out = [];
  if (mode === "fixed") {
    for (let at = lo + width + spacing; at + width <= hi - width - 0.5;
      at += spacing + width) out.push(r(at));
    return out;
  }
  const gap = (clear - n * width) / (n + 1);
  for (let i = 1; i <= n; i += 1)
    out.push(r(lo + width + i * gap + (i - 1) * width));
  return out;
}

/* Every upright a batten can be fixed into, as a centre on the sheathed
   face. Full height only: a batten runs the wall and a cripple does not, and
   the cripples line up with the common studs anyway. */
function battenStuds(w, off) {
  const f = w.frame;
  const t = f.t;
  /* A glazed wall keeps its uprights in `studFaces` and hands back no
     `centres` at all, so reading only the latter leaves the band under the
     sill — the one band it does clad — with nothing to fix to. */
  if (f.glazed && f.studFaces)
    return [...new Set(f.studFaces.map(([a, b]) => r((a + b) / 2 + off)))]
      .sort((a, b) => a - b);
  const c = [t / 2, f.len - t / 2, ...f.centres];
  const o = f.opening;
  if (o && !f.glazed) {
    if (!o.mergeLeft && !o.absorbLeft) c.push(o.kingLeft + t / 2);
    if (!o.mergeRight && !o.absorbRight) c.push(o.kingRight + t / 2);
    /* Cripples too. They are not full height, but they are the only timber
       in the band over a window and under its sill — which is exactly where
       a batten across the opening gets cut in two and needs fixing. They
       exist nowhere else, so adding them costs nothing anywhere else. */
    (o.cripples || []).forEach((x) => c.push(x));
  }
  return [...new Set(c.map((x) => r(x + off)))].sort((a, b) => a - b);
}

/* Which of them get one, between two that must. The spacing is a **maximum**
   and the studs are the only places it can land, so it cannot always be met:
   ask for 300 on a frame at 400 centres and you get 356, because the
   alternative is a batten screwed to 11 mm of sheathing. What was achieved
   is reported rather than the rule being bent. */
function pickBetween(a0, a1, inner, width, spacing, mode) {
  const worst = (list) => {
    const all = [a0, ...list, a1];
    let g = 0;
    for (let i = 0; i < all.length - 1; i += 1)
      g = Math.max(g, all[i + 1] - all[i] - width);
    return g;
  };
  if (worst([]) <= spacing + 0.5) return [];

  if (mode === "fixed") {
    /* Step as far as the spacing reaches and land on the last stud inside
       it, which is what setting out with a tape and a pencil gives. */
    const out = [];
    let at = a0;
    for (let guard = 0; guard < inner.length + 2; guard += 1) {
      if (a1 - at - width <= spacing + 0.5) break;
      const reach = inner.filter((x) => x > at + 0.5 && x - at - width <= spacing + 0.5);
      const next = reach.length ? reach[reach.length - 1] : inner.find((x) => x > at + 0.5);
      if (next == null) break;
      out.push(next);
      at = next;
    }
    return out;
  }

  /* Equal: work out how many are needed, take the stud nearest each ideal
     position — and then **check**, because snapping moves them. Three evenly
     spaced ideals on a 2,526 run come out at 518, 548, 552 and 708 once they
     land on timber, which is over the 600 asked for even though the
     arithmetic said three would do. So the count climbs until the worst gap
     is met or the studs run out. */
  const pick = (n) => {
    const out = [];
    for (let i = 1; i <= n; i += 1) {
      const ideal = a0 + ((a1 - a0) * i) / (n + 1);
      let near = null;
      inner.forEach((x) => {
        if (out.includes(x)) return;
        if (near === null || Math.abs(x - ideal) < Math.abs(near - ideal)) near = x;
      });
      if (near !== null) out.push(near);
    }
    return out.sort((x, y) => x - y);
  };
  const from = Math.max(1, Math.ceil((a1 - a0 - width) / (spacing + width))) - 1;
  let best = pick(from);
  for (let n = from; n <= inner.length; n += 1) {
    const got = pick(n);
    if (worst(got) < worst(best)) best = got;
    if (worst(got) <= spacing + 0.5) return got;
  }
  return best;
}

/* One wall's battens, in face coordinates. `runs` are the pieces you cut;
   `lines` are where the battens sit, which is what the drawings mark. */
/* Battens and cladding both work on the sheathed face, and both need the
   same two corrections to get onto it — so they are written once here
   rather than twice and left to drift.

   **The top.** Battens stop under the furring. A wall carrying the fall has
   a tapered plate sitting on its head, and the sheathed face runs up past it
   to the high corner — so anything set out against that face runs straight
   into the furring and stands the cladding off it by a batten's thickness at
   the top and nothing at the bottom. The furring's **underside** is flat at
   the wall plate whichever way it is packed, tapered or constant, so that is
   the ceiling: `faceH - lift` is the plate top. Where the furring is inside
   the header there is no strip in the way and the face is the limit. */
const faceCap = (w) =>
  (w.lift > 0 && !w.furringInHeader ? r(w.sheathH - w.lift) : w.sheathH);

/* **The opening**, moved out of the frame and onto the face. Plan
   coordinates run from the sheathing's own left edge, which is
   `shift + start` along from the frame's; heights run up from the bottom of
   the skirt rather than from the bottom plate. A glazed wall's opening runs
   to the top of the face rather than to a head. */
const faceHole = (w, skirt) => {
  const o = w.frame.opening;
  if (!o) return null;
  const a0 = r(o.x0 + w.shift + w.start);
  const a1 = r(o.x1 + w.shift + w.start);
  const b0 = r(skirt + o.sillY);
  return { a0, a1, b0, b1: w.frame.glazed ? w.sheathH : r(b0 + o.roH) };
};

function wallBattens(w, p) {
  const { width, proud, spacing, mode, cladding, hSpacing, hMode, skirt } = p;
  const faceL = w.sheath;
  const faceH = w.sheathH;
  const cap = faceCap(w);
  const hole = faceHole(w, skirt);

  /* **Every vertical batten lands on a stud.** A batten fixed in a bay is a
     batten fixed to 11 mm of sheathing, and the cladding hangs off it — so
     the stud, not the spacing, is what decides where one can go.

     A batten over the end stud at each corner, because cladding with nothing
     behind its own end is the one place it will lift. At an opening, one
     either side with its **side in the plane of the reveal** — which puts it
     over the trimmer, the member the cladding and the reveal trim both want
     to fix to, so the two rules agree rather than compete. */
  const off = w.shift + w.start;
  const studs = battenStuds(w, off);
  const onStud = (centre) =>
    r(Math.max(0, Math.min(faceL - width, centre - width / 2)));

  const anchors = [onStud(studs[0]), onStud(studs[studs.length - 1])];
  if (hole) {
    if (hole.a0 - width > anchors[0] + width) anchors.push(hole.a0 - width);
    if (hole.a1 + width < anchors[1]) anchors.push(hole.a1);
  }
  anchors.sort((x, y) => x - y);

  const placed = studs.map(onStud);
  const verts = [...anchors];
  for (let i = 0; i < anchors.length - 1; i += 1) {
    const lo = anchors[i];
    const hi = anchors[i + 1];
    const inner = placed.filter((x) => x > lo + width - 0.5 && x + width < hi + 0.5);
    verts.push(...pickBetween(lo, hi, inner, width, spacing, mode));
  }
  const vertAt = [...new Set(verts.map((x) => r(x)))].sort((x, y) => x - y);

  /* A vertical batten that crosses the opening is two pieces, one under the
     sill and one over the head — there is cladding above and below a window
     and it has to fix to something. */
  const runs = [];
  const stubs = [];
  /* A batten stub over a door head or under a sill can come out at 40 mm.
     Nobody cuts that, and putting it on the list makes the list untrue — so
     it is reported as a strip to fix the cladding another way. */
  const line = (kind, a, b, da, db) => {
    const len = r(kind === "vertical" ? db : da);
    const piece = { kind, a: r(a), b: r(b), da: r(da), db: r(db), len };
    if (len < BATTEN_MIN) stubs.push(piece);
    else runs.push(piece);
  };

  vertAt.forEach((a) => {
    const crosses = hole && a + width > hole.a0 + 0.5 && a < hole.a1 - 0.5;
    if (!crosses) return line("vertical", a, 0, width, cap);
    if (hole.b0 > 0) line("vertical", a, 0, width, Math.min(hole.b0, cap));
    if (cap - hole.b1 > 0) line("vertical", a, hole.b1, width, cap - hole.b1);
  });

  let horizAt = [];
  if (cladding === "vertical") {
    /* One hard at the bottom and one hard at the top: the bottom rail is
       what the first board and the vent strip fix to, and the top is what
       stops the last board flapping under the soffit. */
    const hAnchors = [0, cap - width];
    hAnchors.push(...battenRun(0, cap, width, hSpacing, hMode));
    horizAt = [...new Set(hAnchors.map((x) => r(x)))].sort((x, y) => x - y);
    horizAt.forEach((b) => {
      const crosses = hole && b + width > hole.b0 + 0.5 && b < hole.b1 - 0.5;
      if (!crosses) return line("horizontal", 0, b, faceL, width);
      if (hole.a0 > 0) line("horizontal", 0, b, hole.a0, width);
      if (faceL - hole.a1 > 0) line("horizontal", hole.a1, b, faceL - hole.a1, width);
    });
  }

  const gaps = [];
  for (let i = 0; i < vertAt.length - 1; i += 1)
    gaps.push(r(vertAt[i + 1] - vertAt[i] - width));

  return {
    side: w.side, faceL: r(faceL), faceH: r(faceH), cap, width, proud,
    verticalAt: vertAt, horizontalAt: horizAt, runs, stubs, hole,
    /* The uprights, and where a batten would sit on each — so the choice of
       which ones got one can be checked against the ones that could have. */
    studAt: studs, battenAt: placed,
    onStud: vertAt.every((a) =>
      studs.some((c) => c >= a - 0.5 && c <= a + width + 0.5)
      || (hole && (a === hole.a0 - width || a === hole.a1))),
    widestGap: gaps.length ? Math.max(...gaps) : r(faceL - 2 * width),
    /* What the cladding actually fixes to. With horizontal cladding that is
       the vertical battens; with vertical cladding it is the horizontal ones
       sitting on top of them. */
    fixesTo: cladding === "vertical" ? "horizontal" : "vertical",
  };
}

function buildBattens(walls, p) {
  const { section, onEdge = false, cladding = "horizontal", spacing = 600,
          mode = "equal", hSpacing = 600, hMode = "equal" } = p;
  const width = onEdge ? section.t : section.d;
  const proud = onEdge ? section.d : section.t;
  const per = walls.walls.map((w) =>
    wallBattens(w, { width, proud, spacing, mode, cladding, hSpacing, hMode,
      skirt: walls.skirt }));

  const rows = new Map();
  per.forEach((b) => b.runs.forEach((x) => rows.set(x.len, (rows.get(x.len) || 0) + 1)));
  const cuts = [...rows.entries()]
    .map(([len, qty]) => ({ len: Number(len), qty, role: "Batten" }))
    .sort((a, b) => b.len - a.len);

  return {
    per, cuts, section, width, proud, cladding, spacing, mode, hSpacing, hMode,
    count: per.reduce((n, b) => n + b.runs.length, 0),
    stubs: per.reduce((n, b) => n + b.stubs.length, 0),
    onStud: per.every((b) => b.onStud),
    linearM: per.reduce((n, b) => n + b.runs.reduce((t, x) => t + x.len, 0), 0) / 1000,
    /* Two layers on a vertical-clad wall: the verticals hold the horizontals
       off the sheathing so the drainage path is not dammed at every rail. */
    layers: cladding === "vertical" ? 2 : 1,
    widestGap: per.length ? Math.max(...per.map((b) => b.widestGap)) : 0,
  };
}

/* ---------------------------- cladding ----------------------------

   The boards themselves, on the battens. **Which way they run decides which
   way the battens run**, not the other way about: horizontal cladding fixes
   to vertical battens, and vertical cladding needs horizontals to fix to and
   verticals under those to keep the drainage path open. One setting drives
   both, which is why `cladding` lives on the cladding and `buildBattens`
   reads it.

   **Shadow gap.** These are tongue-and-groove boards with the tongue left
   longer than the groove is deep, so the boards cannot close up and a square
   groove is left between every pair. That gap is not waste and it is not a
   tolerance — it is the detail, and it is what makes the **cover** wider
   than the face: a 98 mm face with a 6 mm shadow repeats every 104 mm. Count
   coverage off the face and you under-order; count it off the cover and the
   last board is wrong. Both are reported.

   The **last board is ripped**, almost always: a face is rarely a whole
   number of covers. That is a real cut on a real board and it is reported
   rather than rounded away, because a 12 mm rip is a board you cannot use
   and a 60 mm rip is one you can. */

const CLAD_MIN = 150;
/* A piece under this is a short — the kind an opening throws up, and the
   kind a tail off another length is for. It is not a threshold for dropping
   anything; nothing is dropped. */
const CLAD_SHORT = 900;
/* Stock lengths a cladding merchant actually sells, for the ladder below. */
const CLAD_STOCK = [2400, 3000, 3600, 3900, 4200, 4800, 5100, 5400];

function wallCladding(w, p) {
  const { face, gap, cladding, skirt, fall = null, short = CLAD_SHORT } = p;
  const faceL = w.sheath;
  const plate = faceCap(w);
  const hole = faceHole(w, skirt);
  const cover = r(face + gap);

  /* **The cladding goes up to the furring, not to the plate.** The battens
     stop at the plate because the strip is in the way of a straight piece —
     but the cladding carries on over it to the roof, and on a wall running
     down the fall that top edge is a sloping line, not a level one.

     `plate` is the flat underside of the furring. The strip's **top** at any
     point is `plate + heightAt(pos)`, and the position it wants is the
     building one, which is the face coordinate less the wall's shift — the
     same correction `angledSheetCuts` makes to mark out the sheets under it.
     A cross wall gets a constant packer rather than a taper, so its top is
     flat at the full sheathed height. */
  /* A fall set to the same height at both ends is a flat roof with a packer
     on it, not a slope — so there is nothing to cut to and `sloped` says so
     rather than being true of a top edge that is level. */
  const sloped = !!(w.tapered && fall && fall.on && Math.abs(fall.rise) > 0.5);
  const topAt = (a) => (sloped
    ? r(plate + fall.heightAt(a - (w.shift || 0)))
    : w.sheathH);
  const top0 = topAt(0);
  const top1 = topAt(faceL);
  const topMax = Math.max(top0, top1);
  const topMin = Math.min(top0, top1);
  /* Where along the face the sloping top passes a given height. */
  const aAtHeight = (h) => (top1 === top0 ? null : ((h - top0) * faceL) / (top1 - top0));

  const boards = [];
  /* **Every piece beside an opening is real cladding and stays on the list.**
     A batten stub under a door head is a strip nobody cuts, and dropping it
     is right — but a 120 mm board beside a reveal is not a stub, it is the
     bit of wall between the trimmer and the next board, and leaving it off
     leaves a hole. So nothing is dropped here; the short ones are flagged
     instead, because what they want is a tail off a length already cut
     rather than a length of their own. */
  let angled = 0;
  const put = (a, b, da, db, ripped, cut) => {
    const len = r(cladding === "vertical" ? db : da);
    if (len < MIN_PIECE || da <= 0.5 || db <= 0.5) return;
    if (cut) angled += 1;
    boards.push({ a: r(a), b: r(b), da: r(da), db: r(db), len,
      ripped: !!ripped, short: len < short, cut: cut || null });
  };

  /* One routine, run along whichever axis the boards do. `at` steps across
     the face by a cover each time and `band` is the board's own width on
     that axis — the face, or whatever is left at the top or the end.

     **A vertical board steps across the face and runs up to the furring**, so
     on a sloping wall every one of them is a different length and every one
     gets an angled cut at the top. **A horizontal board runs along**, so only
     the courses the slope passes through are cut — one if the fall is gentle,
     several if it is not. Two different problems, one loop. */
  const across = cladding === "vertical" ? faceL : topMax;
  let ripped = 0;
  for (let at = 0; at < across - 0.5; at = r(at + cover)) {
    const band = Math.min(face, r(across - at));
    if (band < MIN_PIECE) break;
    const isRip = band < face - 0.5;
    if (isRip) ripped += 1;

    /* Cut around the opening. A board crossing it is two pieces — either
       side of a window for horizontal cladding, above and below it for
       vertical — because there is cladding all round an opening and each
       piece is a separate cut off a length. */
    const lo = cladding === "vertical" ? hole && hole.a0 : hole && hole.b0;
    const hi = cladding === "vertical" ? hole && hole.a1 : hole && hole.b1;
    const crosses = hole && at + band > lo + 0.5 && at < hi - 0.5;
    const cutLo = cladding === "vertical" ? hole && hole.b0 : hole && hole.a0;
    const cutHi = cladding === "vertical" ? hole && hole.b1 : hole && hole.a1;

    if (cladding === "vertical") {
      /* The board's own two edges meet the furring at different heights, so
         it is cut from a length of the taller of them and the angle is
         marked from one edge to the other. */
      const hA = topAt(at);
      const hB = topAt(at + band);
      const tall = Math.max(hA, hB);
      const cut = sloped && Math.abs(hA - hB) > 0.5
        ? { hStart: r(hA), hEnd: r(hB), fallOver: r(band) } : null;
      const run = (from, len) => put(at, from, band, len, isRip, from === 0 ? cut : null);
      if (!crosses) run(0, tall);
      else {
        run(0, cutLo);
        run(cutHi, r(tall - cutHi));
      }
      continue;
    }

    /* Horizontal: the course exists only where the furring is above it, and
       is tapered where the furring passes through its own width. */
    if (at >= topMax - 0.5) break;
    let from = 0;
    let to = faceL;
    if (sloped && at > topMin - 0.5) {
      const edge = aAtHeight(at);
      if (edge != null) {
        if (top1 > top0) from = Math.max(0, Math.min(faceL, edge));
        else to = Math.max(0, Math.min(faceL, edge));
      }
    }
    if (to - from < MIN_PIECE) break;
    const hAt = (a) => r(Math.min(at + band, topAt(a)) - at);
    /* Where the fall carries the furring past the course's own width the
       board is square, and the taper is ripped only over the stretch the
       rake actually crosses. Running one wedge the length of the wall is
       both a cut nobody makes and the wrong angle: clamping the rake to the
       board and then drawing a straight line between the two ends gives a
       chord of the real line, which on a default pod is half the pitch the
       panel tells you to set the saw to. `full` is where the rake leaves the
       board, and the course is cut there. */
    const full = sloped ? aAtHeight(at + band) : null;
    const emit = (a0, a1) => {
      if (a1 - a0 < MIN_PIECE) return;
      const piece = sloped && Math.abs(hAt(a0) - hAt(a1)) > 0.5
        ? { hStart: hAt(a0), hEnd: hAt(a1), fallOver: r(a1 - a0) } : null;
      put(a0, at, r(a1 - a0), band, isRip, piece);
    };
    const run = (a0, a1) => {
      if (full != null && full > a0 + 0.5 && full < a1 - 0.5) {
        emit(a0, r(full));
        emit(r(full), a1);
        return;
      }
      emit(a0, a1);
    };
    if (!crosses) run(from, to);
    else {
      run(from, Math.min(cutLo, to));
      run(Math.max(cutHi, from), to);
    }
  }

  const area = boards.reduce((t, x) => t + x.da * x.db, 0);
  return {
    side: w.side, faceL: r(faceL), cap: plate, cover, face, gap, cladding, hole,
    boards, ripped,
    /* The top edge, level or not, and how many boards take an angled cut. */
    sloped, topLow: topMin, topHigh: topMax, angled,
    short: boards.filter((x) => x.short).length,
    /* Rows for horizontal cladding, columns for vertical — the same count
       either way, since it is how many covers fit across the face. */
    courses: Math.ceil(across / cover),
    area: r(area),
    linear: boards.reduce((t, x) => t + x.len, 0),
  };
}

function buildCladding(walls, p) {
  const { face = 98, gap = 6, thickness = 18, cladding = "horizontal",
          stock = 3600, kerf = 3, sides = null, fall = null } = p;
  /* **Not every wall gets clad.** A feature wall in another material, or a
     wall so close to a boundary that nobody will ever see or maintain it —
     both are ordinary, and cladding a wall you cannot reach is money on a
     surface nobody looks at. `sides` is the list that does get boards;
     everything else is still built and still drawn, just bare. */
  const clads = (side) => !sides || sides.includes(side);
  const per = walls.walls.map((w) => (clads(w.side)
    ? wallCladding(w, { face, gap, cladding, skirt: walls.skirt, fall })
    : { side: w.side, faceL: r(w.sheath), cap: faceCap(w), cover: r(face + gap),
        face, gap, cladding, hole: faceHole(w, walls.skirt), boards: [],
        ripped: 0, short: 0, courses: 0, area: 0, linear: 0, angled: 0,
        sloped: false, off: true }));

  const rows = new Map();
  per.forEach((c) => c.boards.forEach((x) => rows.set(x.len, (rows.get(x.len) || 0) + 1)));
  const cuts = [...rows.entries()]
    .map(([len, qty]) => ({ len: Number(len), qty, role: "Cladding" }))
    .sort((a, b) => b.len - a.len);

  /* Packed the same way the timber is: longest first into whatever length
     still has room, so a short piece beside a door lands on the tail of a
     length already cut rather than opening another. The bars are the cutting
     list — each one a stock length, what comes off it and what is left. */
  const bars = packStock(cuts, stock, kerf);
  const linear = per.reduce((t, c) => t + c.linear, 0);
  /* An offcut long enough to be a board somewhere is not waste — and the
     packing has already tried to use it, so what is listed here is what is
     genuinely left over at the end rather than a saving still on the table. */
  const offcuts = bars.filter((b) => !b.over).map((b) => b.offcut)
    .filter((x) => x >= CLAD_MIN).sort((a, b) => b - a);
  /* **A board longer than the stock cannot be cut from it.** `packStock`
     hands those back flagged rather than quietly splitting them, because a
     cladding board spliced mid-wall is a different building. So they are not
     counted as bought — there is nothing to buy them from — and `minStock`
     says what length would actually do, since that is the question the flag
     raises. */
  const over = bars.filter((b) => b.over);
  const bought = (bars.length - over.length) * stock;
  const minStock = cuts.length ? Math.max(...cuts.map((c) => c.len)) : 0;

  /* **What the openings throw up, and where it comes from.** A board beside
     a reveal is a short piece, and the whole point of packing longest first
     is that it lands on the tail of a length already cut rather than opening
     one of its own. So the reuse is counted rather than claimed: a piece
     that is not the first cut on its length came off an offcut. */
  const shortLens = new Set();
  per.forEach((c) => c.boards.forEach((x) => { if (x.short) shortLens.add(x.len); }));
  let fromOffcut = 0;
  let shortFromOffcut = 0;
  bars.forEach((bar) => bar.cuts.forEach((len, i) => {
    if (i === 0) return;
    fromOffcut += 1;
    if (shortLens.has(len)) shortFromOffcut += 1;
  }));

  /* **Fewer lengths is not less material, and the difference is not small.**
     A 2,570 board off a 4,800 length leaves 2,230 that no second board fits,
     so long stock buys more timber in fewer pieces — 201 m against 165 m on
     a default pod, which is the opposite of what "fewer lengths" suggests.
     So the ladder is walked and the least-material length named, the same
     way the PIR piecing threshold is measured rather than guessed. */
  const ladder = [...new Set([...CLAD_STOCK, stock])].sort((a, b) => a - b)
    .map((len) => {
      const got = packStock(cuts, len, kerf);
      return { stock: len, lengths: got.length, bought: got.length * len,
        over: got.some((b) => b.over) };
    })
    .filter((x) => !x.over);
  const best = ladder.reduce(
    (a, b) => (!a || b.bought < a.bought || (b.bought === a.bought && b.stock < a.stock)
      ? b : a), null);

  return {
    per, cuts, bars, face, gap, thickness, cover: r(face + gap), cladding, stock,
    ladder, best,
    sides: walls.walls.filter((w) => clads(w.side)).map((w) => w.side),
    count: per.reduce((n, c) => n + c.boards.length, 0),
    short: per.reduce((n, c) => n + c.short, 0),
    fromOffcut, shortFromOffcut,
    ripped: per.reduce((n, c) => n + c.ripped, 0),
    /* Boards whose top is cut to the fall rather than square. On a wall
       running down the fall with vertical boards that is every one of them;
       with horizontal boards it is only the courses the slope passes
       through — one if it is gentle, several if it is not. */
    angled: per.reduce((n, c) => n + c.angled, 0),
    degrees: fall && fall.on ? Math.round(fall.degrees * 10) / 10 : 0,
    area: per.reduce((t, c) => t + c.area, 0),
    linear,
    lengths: bars.length - over.length,
    over: over.length,
    overLen: over.length ? Math.max(...over.flatMap((b) => b.cuts)) : 0,
    minStock,
    offcuts,
    /* Bought against cut. The difference is every saw kerf plus every tail
       too short to be anything, which is the figure you actually pay for. */
    bought,
    /* Bought against cut, over the part that can actually be bought. */
    waste: r(Math.max(0, bought - linear)),
    wastePct: bought > 0
      ? Math.round((1 - Math.min(1, linear / bought)) * 1000) / 10 : 0,
  };
}

/* ---------------------- breather membrane ----------------------

   A wrap outside the sheathing and under the battens, laid in horizontal
   courses from the bottom up so each one laps **over** the one below —
   shingle fashion, because water running down the outside has to be shed
   onto the course below rather than into the lap.

   The courses are bands round the whole building, not per wall: the roll
   goes round the corner and keeps going. So the run is the perimeter of the
   sheathed face, which is the sum of the walls' own sheathed lengths — a
   lapping wall's board already runs past the corner by a sheet thickness at
   each end, and `w.sheath` carries that.

   Height is set by the tallest point rather than the average: the bottom is
   level all round (the sheathing bottom is `sheathH - lift` on every wall,
   and `sheathH` already includes that wall's lift) but the top is not, so a
   wall carrying the fall decides how many courses there are. */

const WRAP_ROLL_W = [1000, 1200, 1400, 1500, 2700, 3000];
/* Membrane is about 0.5 mm. Drawn at 1 mm for the same reason tape is: it
   is the thinnest solid podBoxes will emit. Nothing measures it. */
const WRAP_DRAWN_T = 1;
const WRAP_MAX_COURSES = 200;

/* How many widths of roll it takes to cover a run, and where each one sits.
   Shared by the wall wrap and the roof's tray lining: a wall course and a
   deck strip are the same arithmetic, one measured up the face and the other
   across the developed width of the tray. Each lands one lap below the top
   of the one before, and the last is trimmed. */
function wrapCourses(cover, rollW, overlap) {
  const step = rollW - overlap;
  if (!(cover > 0) || step <= 0) return [];
  const out = [];
  for (let at = 0; out.length < WRAP_MAX_COURSES; at += step) {
    const top = Math.min(at + rollW, cover);
    out.push({ at: r(at), h: r(top - at), top: r(top),
      trimmed: at + rollW > cover + 0.5 });
    if (at + rollW >= cover - 0.5) break;
  }
  return out;
}
function planWrap(walls, p = {}) {
  const { rollW = 1500, overlap = 150, drop = 20, rollLen = 50000,
          tapeW = 60, tapeRoll = 25000 } = p;

  const list = (walls && walls.walls) || [];
  const perimeter = r(list.reduce((t, w) => t + w.sheath, 0));
  /* The high corner of the tallest wall. A fall lifts one wall's sheathing
     past the others, and the wrap has to reach the top of that one too. */
  const faceH = list.length ? r(Math.max(...list.map((w) => w.sheathH))) : 0;
  const cover = r(faceH + drop);
  const step = rollW - overlap;

  const empty = {
    courses: [], n: 0, perimeter, faceH, cover, rollW, overlap, drop, rollLen,
    tapeW, tapeRoll, runM: 0, area: 0, rolls: 0,
    tape: { lap: 0, join: 0, opening: 0, total: 0, rolls: 0 },
    impossible: step <= 0, dropShort: drop <= 0,
  };
  if (!perimeter || !cover || step <= 0) return empty;

  /* Course 1 starts a `drop` below the sheathing so its bottom edge hangs
     clear of the floor frame and sheds onto nothing. Every course after it
     starts one lap below the top of the one beneath. */
  const courses = wrapCourses(cover, rollW, overlap);

  /* One closing lap per course, where the roll meets its own start. */
  const perCourse = perimeter + overlap;
  const runM = r(courses.length * perCourse);
  const rolls = rollLen > 0 ? Math.ceil(runM / rollLen) : 0;

  /* Every lap is taped, and there are three kinds. The horizontal ones run
     the perimeter once each, between one course and the next. The vertical
     ones are the closing lap on each course plus one wherever a roll ran
     out mid-course — a roll change is a join like any other. And the
     membrane is cut at every opening and taped back to the frame. */
  const lap = r(Math.max(0, courses.length - 1) * perimeter);
  const join = r(courses.reduce((t, c) => t + c.h, 0)
    + Math.max(0, rolls - 1) * rollW);
  const opening = r(list.reduce(
    (t, w) => t + (w.hole ? 2 * (w.hole.dx + w.hole.dz) : 0), 0));
  const total = lap + join + opening;

  return {
    ...empty,
    courses, n: courses.length, runM,
    area: r(runM * rollW),
    rolls,
    tape: { lap, join, opening, total,
      rolls: tapeRoll > 0 ? Math.ceil(total / tapeRoll) : 0 },
    impossible: false,
  };
}

/* The same membrane under the roof insulation: laid on the structural deck,
   turned up the inside of the upstand and dressed over its top, so the whole
   warm-roof build-up sits in a tray rather than on a flat sheet with four
   open edges.

   **The quantity is the developed width, not the tray.** Take a section
   across it and the membrane runs: over the upstand top (`uw`), down its
   inner face (`uh`), across the tray, up the far face (`uh`) and out over the
   far top (`uw`). That comes to `W - 2uw + 2uh + 2uw`, which is simply
   `W + 2uh` — the roof's own width plus a turn-up at each side. The upstand
   width cancels out, which reads like a mistake until you draw the section:
   what the turn-up takes off the tray it gives back going over the top.

   Strips run along one axis, so the cross axis decides how many there are
   and the along axis decides how long each one is. `auto` runs them the long
   way because that buys the fewest laps, and a lap is both tape and a place
   to get it wrong. */
function planRoofWrap(roof, p = {}) {
  const { rollW = 1500, overlap = 150, rollLen = 50000,
          tapeW = 60, tapeRoll = 25000, orient = "auto" } = p;

  const up = roof && roof.upstand;
  const uw = up ? up.uw : 0;
  const uh = up ? up.uh : 0;
  const devL = roof ? r(roof.L + 2 * uh) : 0;
  const devW = roof ? r(roof.W + 2 * uh) : 0;
  const step = rollW - overlap;

  const empty = {
    strips: [], n: 0, devL, devW, uw, uh, rollW, overlap, rollLen, tapeW, tapeRoll,
    orient, along: 0, cross: 0, alongAxis: "x",
    runM: 0, area: 0, rolls: 0, perStrip: 1,
    tape: { lap: 0, join: 0, upstand: 0, total: 0, rolls: 0 },
    impossible: step <= 0,
  };
  if (!roof || !devL || !devW || step <= 0) return empty;

  /* Fewest strips means running them along the longer developed side. */
  const alongX = orient === "along" ? true
    : orient === "across" ? false
      : devL >= devW;
  const along = alongX ? devL : devW;
  const cross = alongX ? devW : devL;

  const strips = wrapCourses(cross, rollW, overlap);
  /* A strip longer than the roll is made from more than one length, and each
     extra length costs a lap and a cross join. On a pod roof it never is. */
  const perStrip = rollLen > 0 ? Math.max(1, Math.ceil(along / rollLen)) : 1;
  const stripRun = along + (perStrip - 1) * overlap;
  const runM = r(strips.length * stripRun);
  const rolls = rollLen > 0 ? Math.ceil(runM / rollLen) : 0;

  /* Three kinds of joint again: the laps between strips, the cross joins
     where a strip is made from more than one length, and the line all round
     where it is dressed over the top of the upstand. */
  const lap = r(Math.max(0, strips.length - 1) * along);
  const join = r(strips.length * (perStrip - 1) * rollW);
  const upstandM = r(2 * (roof.L + roof.W));
  const total = lap + join + upstandM;

  return {
    ...empty,
    strips, n: strips.length, along, cross, alongAxis: alongX ? "x" : "z",
    perStrip, runM, area: r(runM * rollW), rolls,
    tape: { lap, join, upstand: upstandM, total,
      rolls: tapeRoll > 0 ? Math.ceil(total / tapeRoll) : 0 },
    impossible: false,
  };
}

/* ---------------------------- the roof ---------------------------- */

function buildRoof(p) {
  const {
    buildingL, buildingW, ohLeft, ohRight, ohFront, ohRear,
    section, spacing, joistAlign, nogginRows,
    deckT, deckBoard, deckAlign, deckOrient, stagger, gap, goal,
    upstandSection, upstandOnEdge, pirT, pirBoard, pirLayers = 1,
    topT, topBoard, topAlign, topOrient,
    epdmMargin, gutterSide, wall, spacingMode = "centres",
  } = p;

  const L = buildingL + ohLeft + ohRight;
  const W = buildingW + ohFront + ohRear;

  const deck = planSheets({ length: L, width: W, board: deckBoard, gap,
    align: deckAlign, stagger, goal, orient: deckOrient });
  const frame = buildFrame(
    { length: L, width: W, t: section.t, d: section.d, spacing, joistAlign,
      nogginRows, doubleRim: false, spacingMode },
    deck
  );
  const support = checkSupport(deck, frame);

  /* On edge, a 45 × 120 gives a 120 mm upstand off a 45 mm footprint —
     which is how it usually gets used, and how it matches 120 mm PIR. */
  const uw = upstandOnEdge ? upstandSection.t : upstandSection.d;
  const uh = upstandOnEdge ? upstandSection.d : upstandSection.t;

  const upstand = {
    section: upstandSection, onEdge: upstandOnEdge, uw, uh,
    label: upstandSection.label,
    pieces: [
      { role: "Upstand, long", len: r(L), qty: 2 },
      { role: "Upstand, short", len: r(W - 2 * uw), qty: 2 },
    ],
    boxes: [
      { x: 0, z: 0, dx: L, dz: uw },
      { x: 0, z: W - uw, dx: L, dz: uw },
      { x: 0, z: uw, dx: uw, dz: W - 2 * uw },
      { x: L - uw, z: uw, dx: uw, dz: W - 2 * uw },
    ],
  };

  /* The tray can be made up of one board or two thinner ones. Two layers of
     the wall's board is the reason this exists: 2 × 50 reaches 100 mm on the
     roof off the same pile the walls and floor cut from, rather than a third
     thickness nobody can share.

     The second layer is planned across the first and aligned the other way,
     so its joints cross rather than sit over them — a joint straight through
     both layers is an air path from the warm side to the cold one, which is
     the whole point of building it up rather than butting one board. */
  const layerT = r(pirT / Math.max(1, pirLayers));
  const trayPlan = (over) => planSheets({ length: L - 2 * uw, width: W - 2 * uw,
    board: pirBoard, gap: 0, align: "edge", stagger: false,
    originX: uw, originZ: uw, goal, ...over });
  const pir = trayPlan();

  /* Measured, not assumed: the closest a joint in one layer comes to a joint
     in the next on the same axis. Zero means they coincide, which is an air
     path straight through the tray. */
  const nearestJoint = (a, b) => {
    let best = Infinity;
    ["xJoints", "zJoints"].forEach((axis) =>
      (a[axis] || []).forEach((p) => (b[axis] || []).forEach((q) => {
        best = Math.min(best, Math.abs(p - q));
      })));
    return best;
  };

  /* Each further layer is chosen rather than decreed: of the four ways to lay
     it, take the fewest cuts among those whose joints clear the layer below
     by PIR_STAGGER_MIN, and if none does, the one that clears it by most.
     Running perpendicular is the usual answer, but on some trays offsetting
     along the same axis clears just as well for half the cuts, and there is
     no reason to pay for the cuts. */
  const pirPlans = [pir];
  for (let i = 1; i < pirLayers; i += 1) {
    const below = pirPlans[i - 1];
    const tried = [];
    ["edge", "centre"].forEach((align) =>
      ["along", "across"].forEach((orient) => {
        const o = trayPlan({ align, orient });
        tried.push({ o, clear: nearestJoint(below, o), cuts: o.pieces.length });
      }));
    tried.sort((a, b) => {
      const ok = (c) => (c.clear >= PIR_STAGGER_MIN ? 1 : 0);
      if (ok(a) !== ok(b)) return ok(b) - ok(a);
      if (ok(a)) return a.cuts - b.cuts;
      return b.clear - a.clear;
    });
    pirPlans.push(tried[0].o);
  }

  let pirStagger = null;
  for (let i = 1; i < pirPlans.length; i += 1) {
    const d = nearestJoint(pirPlans[i - 1], pirPlans[i]);
    if (d < Infinity) pirStagger = pirStagger == null ? r(d) : Math.min(pirStagger, r(d));
  }
  const top = planSheets({ length: L, width: W, board: topBoard, gap,
    align: topAlign, stagger, goal, orient: topOrient });

  const epdmL = L + 2 * epdmMargin;
  const epdmW = W + 2 * epdmMargin;
  const short = Math.min(epdmL, epdmW);
  const long = Math.max(epdmL, epdmW);
  const roll = EPDM_WIDTHS.find((w) => w >= short);

  const runFor = { rear: L, front: L, left: W, right: W };
  const gutterRun = runFor[gutterSide];

  /* Placeholder gutter, sitting just outside the fascia. */
  const gy = section.d - GUTTER + 20;
  const gBox =
    gutterSide === "rear" ? { x: 0, y: gy, z: -GUTTER - 20, dx: L, dy: GUTTER, dz: GUTTER }
    : gutterSide === "front" ? { x: 0, y: gy, z: W + 20, dx: L, dy: GUTTER, dz: GUTTER }
    : gutterSide === "left" ? { x: -GUTTER - 20, y: gy, z: 0, dx: GUTTER, dy: GUTTER, dz: W }
    : { x: L + 20, y: gy, z: 0, dx: GUTTER, dy: GUTTER, dz: W };

  const ohFor = { left: ohLeft, right: ohRight, front: ohFront, rear: ohRear };
  const clearance = wall && wall.side !== "none"
    ? boundaryClearance({
        side: wall.side, gap: wall.gap, from: wall.from,
        overhang: ohFor[wall.side],
        gutter: wall.side === gutterSide ? GUTTER + 20 : 0,
        wallH: wall.height, roofUnderY: wall.roofUnderY,
      })
    : null;

  return {
    L, W, frame, deck, support, pir, top, upstand, deckT, topT, pirT,
    pirPlans, pirLayers: pirPlans.length, pirLayerT: layerT, pirStagger,
    epdm: { l: epdmL, w: epdmW, short, long, roll, area: epdmL * epdmW, margin: epdmMargin },
    gutter: { side: gutterSide, run: gutterRun, edgeTrim: 2 * L + 2 * W - gutterRun, box: gBox },
    upstandMatchesPir: uh === pirT,
    clearance,
    layerTop: section.d + deckT + Math.max(uh, pirT) + topT,
  };
}

/* How close the building can stand to a boundary wall.

   **The thing that hits the wall is the roof, not the frame.** The overhang
   reaches past the building face on that side, and on the gutter side the
   gutter reaches past that again — so the setting-out figure you need on the
   ground and the clearance you care about are two different numbers, and
   which one you are given depends on which you set. `from: "roof"` takes the
   gap as the clear distance from the roof edge and works the frame back from
   it, which is the way round you measure on site when the wall is already
   there; `from: "frame"` is the other way and is the default, so no saved
   design moves.

   It is also a question in three dimensions, not two. A boundary wall lower
   than the underside of the roof at that edge is **passed over** rather than
   hit: the overhang still oversails the boundary, which may well matter, but
   it is not a collision and calling it one sends you moving the building for
   nothing. So the height is compared when it is known, and the verdict says
   which of the two it is. */
function boundaryClearance(p) {
  const { side, gap = 0, from = "frame", overhang = 0, gutter = 0,
          wallH = null, roofUnderY = null } = p;
  if (!side || side === "none") return null;
  /* How far the roof reaches past the building face on this side. */
  const reach = r(overhang + gutter);
  const roofToWall = r(from === "roof" ? gap : gap - reach);
  const frameToWall = r(from === "roof" ? gap + reach : gap);
  const known = wallH != null && roofUnderY != null && wallH > 0;
  const passesOver = known ? wallH < roofUnderY : null;
  return {
    side, from, gap, overhang, gutter, reach,
    roofToWall, frameToWall,
    wallH: wallH == null ? null : r(wallH),
    roofUnderY: roofUnderY == null ? null : r(roofUnderY),
    /* How much taller the wall is than the roof edge, or how far the roof
       clears it by. Negative means the roof passes over. */
    standsProud: known ? r(wallH - roofUnderY) : null,
    passesOver,
    /* A clash is the roof and the wall wanting the same space. Where the
       roof passes over the wall there is no clash however far it oversails,
       so that is reported as an oversail instead. */
    clash: roofToWall < 0 && passesOver !== true,
    oversails: roofToWall < 0,
  };
}

/* Wall slab, positioned from the building face outward. */
/* Boundary wall, standing on the ground plane rather than floating. */
function wallBox(wall, { L, W, offsetX = 0, offsetZ = 0, groundY, height }) {
  if (!wall || wall.side === "none") return null;
  const y0 = groundY;
  const h = height;
  const t = 220;
  const over = 400;
  const g = wall.gap;
  if (wall.side === "left")
    return { x: offsetX - g - t, y: y0, z: -over, dx: t, dy: h, dz: W + over * 2 };
  if (wall.side === "right")
    return { x: offsetX + L + g, y: y0, z: -over, dx: t, dy: h, dz: W + over * 2 };
  if (wall.side === "rear")
    return { x: -over, y: y0, z: offsetZ - g - t, dx: L + over * 2, dy: h, dz: t };
  return { x: -over, y: y0, z: offsetZ + W + g, dx: L + over * 2, dy: h, dz: t };
}

/* Window frame and glass for one wall's opening, in building coordinates.
   The frame's outer face finishes flush with the outside of the sheathing. */
function windowFrameBoxes(w, walls, fL, fW, frameDepth, frameWidth, ox = 0, oy = 0, oz = 0) {
  const o = w.frame.opening;
  if (!o) return [];
  const st = walls.sheathT;
  const g = w.gapMm != null ? w.gapMm : 0;
  const a0 = w.start + o.x0 + g;
  const aw = o.roW - 2 * g;
  const y0 = o.sillY + g;
  const ah = o.roH - 2 * g;
  const fw = Math.min(frameWidth, aw / 2 - 1, ah / 2 - 1);
  const glassT = Math.min(28, frameDepth - 10);
  const panel = o.isDoor && w.solidLeaf ? "leaf" : "glazing";

  const alongX = w.axis === "x";
  /* Depth range, measured from the outside face of the sheathing inward. */
  let d0;
  if (alongX) d0 = w.side === "rear" ? -st : fW + st - frameDepth;
  else d0 = w.side === "left" ? -st : fL + st - frameDepth;

  const out = [];
  const bar = (along, alen, yy, yh) => {
    if (alen <= 0 || yh <= 0) return;
    if (alongX) out.push({ part: "frame", x: ox + along, y: oy + yy, z: oz + d0,
      dx: alen, dy: yh, dz: frameDepth });
    else out.push({ part: "frame", x: ox + d0, y: oy + yy, z: oz + along,
      dx: frameDepth, dy: yh, dz: alen });
  };

  bar(a0, aw, y0, fw);
  bar(a0, aw, y0 + ah - fw, fw);
  bar(a0, fw, y0 + fw, ah - 2 * fw);
  bar(a0 + aw - fw, fw, y0 + fw, ah - 2 * fw);

  const gd = d0 + (frameDepth - glassT) / 2;
  if (alongX)
    out.push({ part: panel, x: ox + a0 + fw, y: oy + y0 + fw, z: oz + gd,
      dx: aw - 2 * fw, dy: ah - 2 * fw, dz: glassT });
  else
    out.push({ part: panel, x: ox + gd, y: oy + y0 + fw, z: oz + a0 + fw,
      dx: glassT, dy: ah - 2 * fw, dz: aw - 2 * fw });

  return out;
}

/* Frame and glass sizes for reporting, without building any geometry. */
function windowSizes(w, frameWidth, gapMm) {
  const o = w && w.frame && w.frame.opening;
  if (!o) return null;
  const frameW = o.roW - 2 * gapMm;
  const frameH = o.roH - 2 * gapMm;
  const fw = Math.min(frameWidth, frameW / 2 - 1, frameH / 2 - 1);
  return { frameW: r(frameW), frameH: r(frameH), fw: r(fw),
           glassW: r(frameW - 2 * fw), glassH: r(frameH - 2 * fw) };
}

/* ---------------------------- the walls ---------------------------- */

/* Remove a rectangle from a rectangle, guillotine style: up to four
   surviving pieces (left, right, below, above). Used to cut sheathing
   around an opening. */
function rectSubtract(rect, hole) {
  const ax0 = rect.x, ax1 = rect.x + rect.dx;
  const ay0 = rect.z, ay1 = rect.z + rect.dz;
  const bx0 = Math.max(ax0, hole.x), bx1 = Math.min(ax1, hole.x + hole.dx);
  const by0 = Math.max(ay0, hole.z), by1 = Math.min(ay1, hole.z + hole.dz);
  if (bx1 <= bx0 || by1 <= by0) return [rect];
  const out = [];
  const keep = (x, z, dx, dz) => {
    if (dx > 2 && dz > 2) out.push({ ...rect, x, z, dx: r(dx), dz: r(dz), full: false });
  };
  keep(ax0, ay0, bx0 - ax0, ay1 - ay0);
  keep(bx1, ay0, ax1 - bx1, ay1 - ay0);
  keep(bx0, ay0, bx1 - bx0, by0 - ay0);
  keep(bx0, by1, bx1 - bx0, ay1 - by1);
  return out;
}

/* The part of a sheet the opening takes out — what sheeting over and cutting
   afterwards throws away, and the honest price of working that way. A
   1500 × 1500 window is 2.25 m², most of a sheet. */
function rectIntersect(rect, hole) {
  const x0 = Math.max(rect.x, hole.x);
  const x1 = Math.min(rect.x + rect.dx, hole.x + hole.dx);
  const z0 = Math.max(rect.z, hole.z);
  const z1 = Math.min(rect.z + rect.dz, hole.z + hole.dz);
  if (x1 - x0 <= 2 || z1 - z0 <= 2) return null;
  return { x: r(x0), z: r(z0), dx: r(x1 - x0), dz: r(z1 - z0) };
}

/* Stud wall. With an opening it grows the full anatomy: trimmers carrying
   the header, king studs outside them, a rough sill, and cripples above and
   below lining up with the common studs so the sheathing still lands on
   something. */
/* Keep only the uprights that leave `minBay` clear on both sides of a band
   running lo..hi. Every kept one must clear on both sides, so a pass cannot
   create a sliver it has already walked past.

   Three bands use this and they are not the same: common studs run between
   the wall ends and the opening's king studs; a cripple above the header is
   bounded by those kings; a cripple below a sill is bounded by the trimmers,
   which reach the plate there. Comparing across bands is meaningless — a
   trimmer and a top cripple never share a height. */
function spaceOut(positions, t, lo, hi, minBay) {
  const kept = [];
  const dropped = [];
  let cursor = lo;
  positions.forEach((c) => {
    if (c - t / 2 - cursor < minBay - 0.5 || hi - (c + t / 2) < minBay - 0.5)
      return void dropped.push(r(c));
    kept.push(c);
    cursor = c + t / 2;
  });
  return { kept, dropped };
}

/* The narrowest clear span in a frame, measured at the heights things
   actually stand at — a trimmer and a top cripple never share one, so
   comparing every upright against every other invents bays nobody fills.
   Shared because `buildWall` returns from two places and the glazed one
   silently lacked this, which blanked the page on the micro pod. */
function narrowestIn(members) {
  const heights = [...new Set(members.flatMap((m) => [m.y + 1, m.y + m.dy - 1]))]
    .filter((y) => y > 0);
  let narrowest = Infinity;
  heights.forEach((y) => {
    const at = members
      .filter((m) => m.y < y - 0.5 && m.y + m.dy > y + 0.5 && m.dy > m.dx)
      .map((m) => [m.x, m.x + m.dx])
      .sort((a, b) => a[0] - b[0]);
    for (let i = 0; i < at.length - 1; i += 1) {
      const g = at[i + 1][0] - at[i][1];
      if (g > 0.5) narrowest = Math.min(narrowest, g);
    }
  });
  return narrowest === Infinity ? 0 : r(narrowest);
}

function buildWall({ len, height: H, t, d, spacing, studAlign, nogginRows, opening,
                     jointOffset = 0, minBay = 0, spacingMode = "centres",
                     sheathTop = null }, plan) {
  const members = [];
  const push = (role, x, y, dx, dy, cut, extra) =>
    members.push({ role, x: r(x), y: r(y), dx: r(dx), dy: r(dy), len: r(cut), ...extra });

  /* The wall-to-wall mechanic is a glazing arrangement; a door is always
     framed in the ordinary way, however tall it is. */
  const glazed = !!(opening && opening.style === "glazedWall" && opening.kind !== "door");
  /* A full-width header replaces the top plate and sits on the furring plane,
     so the studs stop under it. That is the whole of the glazed-wall trick,
     and it is worth having on its own: a tall door often wants the same
     treatment while still being an ordinary opening part-way along the wall.
     So the two are separate — `glazed` spans the opening, `headerSpansWall`
     spans only the header. */
  const fullHeader = glazed || !!(opening && opening.headerSpansWall);
  /* A door is a window with no sill: the opening reaches the floor, there is
     no rough sill or cripple below it, and the bottom plate is cut away
     between the trimmers. Everything above — trimmers, kings, header, and the
     three absorption tiers as it widens — is identical. */
  const isDoor = !!(opening && opening.kind === "door");
  const { centres, anchors } = joistCentres(
    { length: len, t, spacing: centresFor(spacing, t, spacingMode),
      joistAlign: studAlign, jointOffset }, plan);

  /* The plate is kept full length while the wall is built and cut out across
     the doorway afterwards, so it is reported as two pieces. */
  const doorGap = isDoor && opening
    ? (glazed
        /* A glazed wall's opening runs between the end stud pairs. */
        ? [2 * t, len - 2 * t]
        : [opening.centre - (opening.w + 2 * opening.gap) / 2,
           opening.centre + (opening.w + 2 * opening.gap) / 2])
    : null;
  if (doorGap) {
    if (doorGap[0] > 20) push("Bottom plate", 0, 0, doorGap[0], t, doorGap[0]);
    if (len - doorGap[1] > 20)
      push("Bottom plate", doorGap[1], 0, len - doorGap[1], t, len - doorGap[1]);
  } else push("Bottom plate", 0, 0, len, t, len);

  /* Glazed wall: the header runs the full length and does the top plate's
     job, so there is no top plate and the two end studs stop under it. */
  if (glazed) {
    const headerH = opening.headerH;
    /* With a fall on, the header takes the furring's place on this wall: its
       underside stays level so the glazing stays rectangular, and its top is
       cut to whatever the roof needs — a constant lift on a cross wall, the
       angle itself on a wall running down the fall. */
    const liftY0 = opening.liftY0 || 0;
    const liftY1 = opening.liftY1 || 0;
    const plies = opening.headerT >= d - 2 ? 1 : Math.max(1, Math.round(d / opening.headerT));
    /* The header is stock, not a made-up depth: it keeps the section's own
       depth and its top sits on the same plane as the furring on the walls
       either side, so the two meet at the corner and this wall needs no
       strip or packer of its own. That puts the glazing head as high as it
       can go, which is the point of a glazed wall.

       On a wall running down the fall the whole beam tilts, so its underside
       tilts with it and the two ends bear at different heights. The opening
       head is taken from the lowest point of the underside, keeping the
       glazing rectangular and clear of the header along its whole length. */
    const liftLo = Math.min(liftY0, liftY1);
    const liftHi = Math.max(liftY0, liftY1);
    const headerBase = H + liftY0 - headerH;
    const headerShear = len > 0 ? (liftY1 - liftY0) / len : 0;
    const studLenAt = (l) => H + l - headerH - t;
    const studLenG = studLenAt(liftLo);

    /* Which of the window and the sill is fixed is the user's choice. Fixing
       the window is the useful default — it's the thing you order — and then a
       shallower header lifts the opening, lengthening the jack studs and the
       cripples below rather than resizing the glass. */
    const roTop = H + liftLo - headerH;
    const mode = isDoor ? "door"
      : opening.heightMode || (opening.fixWindow === false ? "sill" : "window");
    /* Tallest possible still leaves room for a cripple below, since a glazed
       wall always wants some solid under the glass. */
    const minSillY = 2 * t + 100;
    const sillY = mode === "door" ? 0
      : mode === "window" ? roTop - (opening.h + 2 * opening.gap)
        : mode === "max" ? minSillY : opening.sill - opening.gap;
    const roH = mode === "window" ? opening.h + 2 * opening.gap : roTop - sillY;
    /* A pair of studs at each end: together they roughly match the thickness
       of the walls butting into them, and they carry the header. */
    const endPair = 2 * t;
    const roW = len - 2 * endPair;
    const below = sillY - 2 * t;

    push("Header", 0, headerBase, len, headerH, len,
      { plies, plyThickness: opening.headerT, isHeader: true,
        ...(liftY0 || liftY1
          ? { shearY: headerShear, carriesFurring: true,
              headerTops: [H + liftY0, H + liftY1] }
          : null) });

    /* Jack studs bear the header, so at the low end they are shorter. */
    [[0, liftY0], [t, liftY0], [len - endPair, liftY1], [len - t, liftY1]]
      .forEach(([x, l]) => {
        const sl = studLenAt(l);
        push("Jack stud", x, t, t, sl, sl);
      });
    if (!isDoor) push("Rough sill", endPair, sillY - t, roW, t, roW);

    const spanned = centres.filter((c) => c > endPair + t / 2 && c < len - endPair - t / 2);
    /* The spandrel band below the glazing is framed like any other, so the
       same minimum applies to it. */
    const band = spaceOut(spanned, t, endPair, len - endPair, minBay);
    const inside = band.kept;
    if (!isDoor && below >= 100)
      inside.forEach((c) => push("Bottom cripple", c - t / 2, t, t, below, below));

    const op2 = {
      style: "glazedWall", glazed: true, liftY0, liftY1, liftLo, liftHi,
      carriesFurring: !!(liftY0 || liftY1),
      headerDeep: [headerH, headerH],
      headerTops: [H + liftY0, H + liftY1],
      studLens: [studLenAt(liftY0), studLenAt(liftY1)],
      roW, roH, x0: endPair, x1: len - endPair, sillY, roTop, endPair,
      headerY: roTop, headerH, headerTop: H, headerLen: len,
      plies, plyThickness: opening.headerT,
      plyTotal: plies * opening.headerT, plyShort: d - plies * opening.headerT,
      studLen: studLenG, cripples: inside, bottomCripple: below >= 100 ? below : 0,
      topCripple: 0, packAbove: 0, headroom: 0,
      mergeLeft: true, mergeRight: true, absorbLeft: true, absorbRight: true,
      winW: roW - 2 * opening.gap, winH: roH - 2 * opening.gap,
      heightMode: mode,
      sill: sillY + opening.gap,
      isDoor,
      fitsWidth: roW > 0, fitsHeight: roH >= 100 && (isDoor || below >= 0),
      minSill: minSillY + opening.gap, maxSill: roTop - 100 + opening.gap,
      maxWidth: roW - 2 * opening.gap,
      maxHeight: roTop - minSillY - 2 * opening.gap,
      below,
    };

    const faces = [[0, endPair], [len - endPair, len],
                   ...inside.map((c) => [c - t / 2, c + t / 2])];
    return { members, centres: [], allCentres: centres, anchors, n: 0,
             studLen: studLenG, maxBay: 0, len, height: H, t, d,
             opening: op2, studFaces: faces, glazed: true,
             droppedStuds: band.dropped, minBay, nogginRowY: [],
             narrowestBay: narrowestIn(members) };
  }

  /* With a full-width header there is no top plate: the header does its job,
     spanning the wall and tying the studs across the opening. */
  const fhLift0 = fullHeader ? (opening.liftY0 || 0) : 0;
  const fhLift1 = fullHeader ? (opening.liftY1 || 0) : 0;
  const fhLiftLo = Math.min(fhLift0, fhLift1);
  const fhHeaderH = fullHeader ? opening.headerH : 0;
  /* Underside taken at the low end, so the opening stays clear of it along
     the whole wall even where the beam tilts to the fall. */
  const fhUnder = H + fhLiftLo - fhHeaderH;

  if (!fullHeader) push("Top plate", 0, H - t, len, t, len);
  else {
    const plies = opening.headerT >= d - 2
      ? 1 : Math.max(1, Math.round(d / opening.headerT));
    push("Header", 0, H + fhLift0 - fhHeaderH, len, fhHeaderH, len, {
      plies, plyThickness: opening.headerT, isHeader: true, spansWall: true,
      ...(fhLift0 || fhLift1
        ? { shearY: len > 0 ? (fhLift1 - fhLift0) / len : 0, carriesFurring: true,
            headerTops: [H + fhLift0, H + fhLift1] }
        : null),
    });
  }

  const studLen = (fullHeader ? fhUnder : H - t) - t;
  push("End stud", 0, t, t, studLen, studLen);
  push("End stud", len - t, t, t, studLen, studLen);

  let op = null;
  if (opening) {
    const roW = opening.w + 2 * opening.gap;
    const x0 = opening.centre - roW / 2;
    /* Under a full-width header the opening runs up to its underside: a door
       from the floor, a window from whatever sill is asked for. */
    const roH = fullHeader
      ? (isDoor ? fhUnder : Math.min(opening.h + 2 * opening.gap,
          fhUnder - (opening.sill - opening.gap)))
      : opening.h + 2 * opening.gap;
    const sillY = isDoor ? 0 : fullHeader ? fhUnder - roH : opening.sill - opening.gap;
    const headerH = fullHeader ? fhHeaderH : opening.headerH;
    const headerY = fullHeader ? fhUnder : sillY + roH;
    const x1 = x0 + roW;
    /* Once the opening is wide enough that the king stud would land on top of
       the end stud, there is no room for both — the end stud becomes the king
       and the trimmer bears directly against it. */
    /* Three tiers as the opening widens. First the king stud has nowhere to
       go and the end stud takes its place. Widen further and the trimmer goes
       the same way, leaving the header butted into the end stud.

       The first tier triggers on usable space, not bare fit: a king stud
       24 mm off the end stud technically fits and is worth nothing — two
       studs and a gap you cannot insulate. `minBay` pulls the tier forward so
       the king is dropped and the end stud does its job, which is the same
       trade the tier was always making, just sooner. */
    const mergeLeft = x0 - 2 * t < t + minBay - 0.5;
    const mergeRight = x1 + 2 * t > len - t - minBay + 0.5;
    const absorbLeft = x0 - t < t - 0.5;
    const absorbRight = x1 + t > len - t + 0.5;

    const headerX0 = absorbLeft ? t : x0 - t;
    const headerX1 = absorbRight ? len - t : x1 + t;

    op = {
      roW, roH, x0, x1, sillY, headerY, headerH,
      headerTop: headerY + headerH,
      mergeLeft, mergeRight, absorbLeft, absorbRight,
      headerLen: fullHeader ? len : headerX1 - headerX0,
      kingLeft: mergeLeft ? 0 : x0 - 2 * t,
      kingRight: mergeRight ? len - t : x1 + t,
      zoneL: mergeLeft ? 0 : x0 - 2 * t,
      zoneR: mergeRight ? len : x1 + 2 * t,
      isDoor,
      fitsWidth: x0 >= t - 0.5 && x1 <= len - t + 0.5,
      fullHeader,
      liftY0: fhLift0, liftY1: fhLift1, liftLo: fhLiftLo,
      headerTops: fullHeader ? [H + fhLift0, H + fhLift1] : null,
      fitsHeight: (fullHeader || headerY + headerH <= H - t + 0.5)
        && roH >= 100 && (isDoor || sillY - t >= t - 0.5),
      headroom: H - t - (headerY + headerH),
      maxSill: H - t - headerH - roH + opening.gap,
      maxWidth: len - 2 * t - 2 * opening.gap,
      maxWidthWithTrimmers: len - 4 * t - 2 * opening.gap,
      maxWidthWithKings: len - 6 * t - 2 * opening.gap,
    };

    if (!absorbLeft) push("Trimmer", x0 - t, t, t, headerY - t, headerY - t);
    if (!absorbRight) push("Trimmer", x1, t, t, headerY - t, headerY - t);
    if (!mergeLeft) push("King stud", op.kingLeft, t, t, studLen, studLen);
    if (!mergeRight) push("King stud", op.kingRight, t, t, studLen, studLen);
    const hPlies = opening.headerT >= d - 2 ? 1 : Math.max(1, Math.round(d / opening.headerT));
    op.plies = hPlies;
    op.plyThickness = opening.headerT;
    op.plyTotal = hPlies * opening.headerT;
    op.plyShort = d - hPlies * opening.headerT;
    const inside = centres.filter((c) => c > x0 + t / 2 && c < x0 + roW - t / 2);
    const below = isDoor ? 0 : sillY - t - t;
    /* Nothing sits above a full-width header: it is already the top of the wall. */
    const above = fullHeader ? 0 : H - t - (headerY + headerH);
    /* Anything shorter than this isn't worth cutting as a cripple. */
    const MIN_CRIPPLE = 100;

    /* A tall opening — a door especially — leaves too little between the
       header and the top plate to fill with cripples. Rather than a separate
       packer, the header itself is made deep enough to reach the plate: one
       piece instead of two, and it takes over the plate's job of tying the
       studs across the opening. */
    const fillsToPlate = above > 0 && above < MIN_CRIPPLE;
    const headerDepth = headerH + (fillsToPlate ? above : 0);
    op.headerTop = headerY + headerDepth;
    op.headerDepth = headerDepth;
    op.fillsToPlate = fillsToPlate;
    op.deepenedBy = fillsToPlate ? above : 0;

    if (!fullHeader)
      push("Header", headerX0, headerY, headerX1 - headerX0, headerDepth, headerX1 - headerX0,
        { plies: hPlies, plyThickness: opening.headerT, isHeader: true });
    /* The sill spans between the trimmers, resting on the cripples below —
       not over them, which is the header's job. */
    if (!isDoor) push("Rough sill", x0, sillY - t, roW, t, roW);

    op.bottomCripple = below >= MIN_CRIPPLE ? below : 0;
    op.topCripple = above >= MIN_CRIPPLE ? above : 0;
    op.packAbove = 0;

    /* Above the header the band runs king to king, which is exactly the
       header's own span; below a sill it runs trimmer to trimmer. */
    const topBand = spaceOut(inside, t, headerX0, headerX1, minBay);
    const botBand = spaceOut(inside, t, x0, x1, minBay);
    op.cripples = topBand.kept;
    op.bottomCripples = botBand.kept;
    op.droppedCripples = [...new Set([...topBand.dropped, ...botBand.dropped])];

    if (op.bottomCripple)
      botBand.kept.forEach((c) => push("Bottom cripple", c - t / 2, t, t, below, below));
    if (op.topCripple)
      topBand.kept.forEach((c) =>
        push("Top cripple", c - t / 2, op.headerTop, t, above, above));
  }

  let nogginRowY = [];

  const zoneL = op ? op.zoneL : Infinity;
  const zoneR = op ? op.zoneR : -Infinity;
  const clear = centres.filter((c) => c + t / 2 <= zoneL + 0.5 || c - t / 2 >= zoneR - 0.5);

  /* A king stud is a full-height stud like any other, so a common stud
     landing just outside the opening zone leaves two studs with a bay
     between them too narrow to cut insulation for — 90 mm on a 2.4 m wall
     with a door 600 mm in, which is the case that prompted this. Drop the
     common one: the king is already doing its job, and doubling up buys
     nothing but a sliver.

     Every kept stud must leave `minBay` clear on both sides, so a pass can
     never create a sliver it has already walked past. The wider bay that
     results is reported, not hidden — and if a sheet joint loses its stud,
     the existing joint check says so. */
  const regions = op ? [[t, zoneL], [zoneR, len - t]] : [[t, len - t]];
  const commons = [];
  const droppedStuds = [];
  regions.forEach(([lo, hi]) => {
    const inBand = clear.filter((c) => c - t / 2 >= lo - 0.5 && c + t / 2 <= hi + 0.5);
    const { kept, dropped } = spaceOut(inBand, t, lo, hi, minBay);
    commons.push(...kept);
    droppedStuds.push(...dropped);
  });
  commons.forEach((c) => push("Stud", c - t / 2, t, t, studLen, studLen));

  if (nogginRows > 0) {
    /* A noggin fits in the clear space between things already there: the end
       studs, every common stud, and the opening's king-to-king zone. Work
       from occupied spans rather than a list of left faces — the old version
       added one gap from the opening straight to the far end, ignoring any
       stud in between, and produced a noggin running through them. */
    const solid = [[0, t], [len - t, len]];
    commons.forEach((c) => solid.push([c - t / 2, c + t / 2]));
    if (op) solid.push([op.zoneL, op.zoneR]);
    solid.sort((a, b) => a[0] - b[0]);

    const merged = [];
    solid.forEach(([s, e]) => {
      const last = merged[merged.length - 1];
      if (last && s <= last[1] + 0.001) last[1] = Math.max(last[1], e);
      else merged.push([s, e]);
    });

    /* Boundaries are rounded before the width is taken, so a noggin can't end
       up a millimetre inside the stud it butts against. */
    const gaps = [];
    for (let i = 0; i < merged.length - 1; i += 1) {
      const a = r(merged[i][1]);
      const b = r(merged[i + 1][0]);
      if (b - a > 20) gaps.push([a, b - a]);
    }

    /* Noggin rows go under the horizontal sheathing joints, the same way
       studs go behind the vertical ones — a joint with nothing behind it is
       an edge you cannot fix. A deck has done this with its `zJoints` all
       along; a wall never did, so a horizontal joint could sit 200 mm off the
       row meant to back it.

       The plan's rows are measured down from the top of the sheathed face
       while the frame is measured up from the bottom plate, so they have to
       be turned over — the same correction the stud anchors and the fixings
       need. Falls back to even division when there is nothing to anchor to,
       which is every wall whose sheathing runs in one piece.

       A row is only wanted where one will fit clear of the plates: its
       underside has to clear the bottom plate and its top the head, so
       1.5t either end. A joint nearer than that — or below the frame
       altogether, out in the skirt — is already backed by a plate or by
       the floor frame, and `rowJoints` counts it so rather than as a
       miss. */
    const anchored = studAlign === "deck" && sheathTop != null && plan && plan.zJoints
      ? [...new Set(plan.zJoints.map((z) => r(sheathTop - z)))]
        .filter((y) => y >= 1.5 * t && y <= H - 1.5 * t)
        .sort((a, b) => a - b)
      : [];
    const rowY = anchored.length
      ? anchored
      : Array.from({ length: nogginRows },
        (_, i) => t + (studLen * (i + 1)) / (nogginRows + 1));
    nogginRowY = rowY.map((y) => r(y));

    rowY.forEach((yc) => {
      gaps.forEach(([x, g]) => {
        /* Skip anything that would run through the opening. */
        if (op && yc > op.sillY - t - t && yc < op.headerTop &&
            x < op.zoneR && x + g > op.zoneL) return;
        push("Noggin", x, yc - t / 2, g, t, g);
      });
    });
  }

  const all = [t / 2, ...commons, len - t / 2];
  let maxBay = 0;
  for (let i = 0; i < all.length - 1; i += 1) maxBay = Math.max(maxBay, all[i + 1] - all[i]);

  /* Some strips no tier can remove — a door set 150 mm off the end leaves one
     between the end stud and its trimmer — so the figure is reported and the
     UI says move the door. */
  return { members, centres: commons, allCentres: centres, anchors, n: commons.length,
           studLen, maxBay, len, height: H, t, d, opening: op, droppedStuds, minBay,
           nogginRowY, narrowestBay: narrowestIn(members) };
}

function buildWalls(p) {
  const { buildingL: L, buildingW: W, section, spacing, studAlign, nogginRows,
          ceilingHeight: H, sheathT, sheathBoard, gap, goal, stagger, skirt = 0,
          minBay = 0, spacingMode = "centres",
          window: win, door: dr, layout = "lengthThrough", lap = "lengthOver", fall = null,
          sheathAlign = "auto", sheathOrient = "auto" } = p;

  const t = section.t;
  const wt = section.d;
  const sheathH = H + skirt;

  /* Which pair of walls runs the full length, and which butts between them.
     Sheathing lap is a separate decision: whichever pair goes on second runs
     long at each end to cover the other's edge. */
  const lengthThrough = layout === "lengthThrough";
  const lengthLaps = lap === "lengthOver";
  const xLen = lengthThrough ? L : L - 2 * wt;
  const zLen = lengthThrough ? W - 2 * wt : W;
  const xStart = lengthThrough ? 0 : wt;
  const zStart = lengthThrough ? wt : 0;

  /* A fall makes two walls taller at one end and lifts the other two by a
     constant. The sheathing has to cover the tallest point of each, or the
     sheet count comes up short. */
  const liftFor = (side, axis, start, len) => {
    if (!fall || !fall.on) return 0;
    const runsWithFall = (fall.axis === "z") === (axis === "z");
    /* A tapered wall rises only over its own run. Using the building's full
       rise makes the sheathing too tall and throws the drawn top edge off. */
    if (runsWithFall)
      return Math.max(0, fall.heightAt(start), fall.heightAt(start + len));
    const pos = fall.axis === "z" ? (side === "rear" ? 0 : W) : (side === "left" ? 0 : L);
    return Math.max(0, fall.heightAt(pos));
  };

  const mk = (side, axis, at) => {
    const alongX = axis === "x";
    const faceLen = alongX ? L : W;
    const laps = alongX ? lengthLaps : !lengthLaps;
    const len = alongX ? xLen : zLen;
    const start = alongX ? xStart : zStart;
    const lift = liftFor(side, axis, start, len);
    return {
      side, axis, at,
      len,
      start,
      faceLen,
      laps,
      lift,
      sheathH: sheathH + lift,
      tapered: !!(fall && fall.on && (fall.axis === "z") === (axis === "z")),
      shift: laps ? sheathT : 0,
      sheath: faceLen + (laps ? 2 * sheathT : 0),
    };
  };

  const specs = [
    mk("rear", "x", 0), mk("front", "x", W - wt),
    mk("left", "z", 0), mk("right", "z", L - wt),
  ];

  const walls = specs.map((s) => {
    /* On a tapered wall the rows start from the top, so the angled cut lands
       on a full sheet rather than on whatever sliver a centred split leaves. */
    const plan = planSheets({ length: s.sheath, width: s.sheathH, board: sheathBoard, gap,
      align: sheathAlign, orient: sheathOrient, stagger, goal,
      rowAlign: s.tapered ? "edge" : null });
    /* One opening per wall. A door wins if both are put on the same wall,
       and the clash is reported rather than silently dropped. */
    const wantsDoor = dr && dr.side === s.side;
    const wantsWindow = win && win.side === s.side;
    const raw = wantsDoor ? { ...dr, kind: "door" }
      : wantsWindow ? { ...win, kind: "window" } : null;
    const clash = !!(wantsDoor && wantsWindow);

    const glazedHere = raw
      && (raw.style === "glazedWall" || raw.headerSpansWall)
      && !(raw.style === "glazedWall" && raw.kind === "door");
    const liftProfile = glazedHere && fall && fall.on
      ? (s.tapered
          ? { liftY0: fall.heightAt(s.start), liftY1: fall.heightAt(s.start + s.len) }
          : { liftY0: s.lift, liftY1: s.lift })
      : null;

    /* An opening can be centred or set out from either end of the wall, which
       is how a door is usually located: so many millimetres off the corner. */
    const placed = raw && (() => {
      const roW = raw.w + 2 * raw.gap;
      if (raw.from === "start") return raw.offset + roW / 2;
      if (raw.from === "end") return s.len - raw.offset - roW / 2;
      return raw.centre === null || raw.centre === undefined ? s.len / 2 : raw.centre;
    })();
    const opening = raw ? { ...raw, centre: placed, ...liftProfile } : null;
    const frame = buildWall(
      { len: s.len, height: H, t, d: wt, spacing, studAlign, nogginRows, opening,
        jointOffset: s.shift + s.start, minBay, spacingMode,
        sheathTop: s.sheathH - skirt },
      plan
    );

    const { shift, start } = s;
    const studFaces = frame.studFaces || [
      [0, t], [s.len - t, s.len],
      ...frame.centres.map((c) => [c - t / 2, c + t / 2]),
    ];
    if (frame.opening && !frame.glazed) {
      const o = frame.opening;
      studFaces.push(
        [o.absorbLeft ? 0 : o.kingLeft, o.x0],
        [o.x1, o.absorbRight ? s.len : o.zoneR]
      );
      /* Sheathing above and below the opening lands on the cripples. */
      (o.cripples || []).forEach((c) => studFaces.push([c - t / 2, c + t / 2]));
    }
    const joints = plan.xJoints.map((pos) => {
      const local = pos - shift - start;
      return { axis: "x", pos, ok: studFaces.some(([a, b]) => local - 8 >= a && local + 8 <= b) };
    });

    /* The horizontal joints, against the noggin rows meant to back them.
       This used to go unchecked, so a wall could report nothing wrong and
       still have a sheet edge on fresh air. The rows are anchored to these
       joints now, so a failure here means something stopped them landing —
       a row pushed out by an opening, or a joint too near a plate.

       Three things back a horizontal joint and only the first is a noggin:
       the plates close either end of the frame, and the skirt below the
       bottom plate runs over the floor frame rather than over the wall. So
       the bottom band runs down from the plate without limit and the top one
       up from the head. The default pod puts its only joint 98 mm into the
       skirt, and counting that reported four faults on four sound walls. */
    const top = s.sheathH - skirt;
    const backing = [
      ...(frame.nogginRowY || []).map((y) => [y - t / 2, y + t / 2]),
      [-Infinity, t], [H - t, Infinity],
    ];
    const rowJoints = plan.zJoints.map((pos) => {
      const y = top - pos;
      return { axis: "z", pos, y: r(y),
        ok: backing.some(([a, b]) => y - 8 >= a && y + 8 <= b) };
    });

    /* Sheathing drawn with the opening cut out. Allocation still uses whole
       sheets, since the usual practice is to fit them whole and cut the hole
       afterwards rather than piece around it. */
    let cutPieces = plan.pieces;
    let holePieces = [];
    let hole = null;
    if (frame.opening) {
      const o = frame.opening;
      hole = { x: start + o.x0 + shift, z: s.sheathH - skirt - (o.sillY + o.roH),
               dx: o.roW, dz: o.roH };
      cutPieces = plan.pieces.flatMap((pc) => rectSubtract(pc, hole));
      /* What comes out of the hole, sheet by sheet. It is **scrap**, not
         stock: cut freehand against a frame it is rough on every edge, and
         squaring it up costs more board than it saves. So it is reported as
         what sheeting over throws away rather than fed back into the pool as
         though it were a usable offcut. */
      holePieces = plan.pieces
        .map((pc) => rectIntersect(pc, hole))
        .filter(Boolean);
    }

    return { ...s, plan, frame, joints, rowJoints, hole, cutPieces, holePieces, clash,
             furringInHeader: !!(liftProfile
               && (frame.glazed || (frame.opening && frame.opening.fullHeader))),
             unsupported: joints.filter((j) => !j.ok),
             rowUnsupported: rowJoints.filter((j) => !j.ok) };
  });

  const cutRows = new Map();
  walls.forEach((w) =>
    rollUp(w.frame.members.map((m) => ({ len: m.len, role: m.role }))).forEach((row) =>
      cutRows.set(`${row.len}|${row.role}`, (cutRows.get(`${row.len}|${row.role}`) || 0) + row.qty)
    )
  );
  const cuts = [...cutRows.entries()]
    .map(([k, qty]) => {
      const [len, role] = k.split("|");
      return { len: Number(len), role, qty };
    })
    .sort((a, b) => b.len - a.len);

  const openings = walls.filter((w) => w.frame.opening);
  const opened = openings.find((w) => !w.frame.opening.isDoor) || null;
  const doored = openings.find((w) => w.frame.opening.isDoor) || null;
  return {
    openings, doored,
    walls, cuts, t, wt, height: H, sheathT, skirt, sheathH,
    sheathArea: walls.reduce((s, w) => s + w.sheath * w.sheathH, 0),
    unsupported: walls.reduce((s, w) => s + w.unsupported.length, 0),
    rowUnsupported: walls.reduce((s, w) => s + w.rowUnsupported.length, 0),
    opened,
  };
}

/* How the furring strips sit across the wall thickness: **whole boards only,
   as many as fit, set out from the outside face**. One where two will not go.

   It used to tile the plate exactly, ripping the last piece to whatever was
   left — 44 + 44 + 12 on a 100 mm wall off a 44 mm section, a third piece
   12 mm wide and three and a half metres long, standing on edge with nothing
   to fix through. Nobody cuts that, and nobody needs to: a single strip
   carries the roof perfectly well where a pair does not fit, so a 75 mm wall
   takes one 44 and a 100 mm wall takes two. What is left over is **reported
   rather than made up** — `furringCover` carries it and the panel prints it,
   because it is a packer on site, not a line on a cut list.

   **Biased to the outer wall**, which is the whole reason the leftover is
   safe: the boards set out from the outside face, so the bare strip falls on
   the inside over the ceiling rather than under the sheathing. And never
   proud of the plate — boards standing over it foul the adjoining walls'
   plates and a glazed wall's header, and there are tests for both. */
function furringWidths(wallThickness, stripWidth) {
  /* A section wider than the plate is ripped down to it: one strip, flush. */
  const width = r(Math.min(stripWidth, wallThickness));
  const n = width > 0 ? Math.max(1, Math.floor(wallThickness / width)) : 0;
  return Array.from({ length: n }, (_, i) => ({ off: r(i * width), width }));
}

/* What those boards come to against the plate they sit on, as information.
   `bare` is the strip of plate they do not reach, on the inside face. */
function furringCover(wallThickness, stripWidth) {
  const strips = furringWidths(wallThickness, stripWidth);
  const covered = r(strips.reduce((t, x) => t + x.width, 0));
  return { n: strips.length, width: r(Math.min(stripWidth, wallThickness)),
           covered, wall: r(wallThickness), bare: r(wallThickness - covered) };
}

/* The furring as pieces to cut and to buy, not as a plane to draw.

   Both the cut list and the timber pile read this one list, because the
   strips were on the cut list and off the order for as long as the
   arithmetic lived in a table in the component — drawn, measured, and never
   bought. A section's pile has to carry them or the length count is short by
   two strips a wall.

   A **packer is split across the thickness exactly as a tapered strip is**.
   It used to be one piece the full wall thickness wide, which on a 100 mm
   wall is a 100 mm packer nobody rips from the 44 mm section the taper comes
   off the same stick of. The two walls get the same treatment because they
   are cut from the same timber; only the top edge differs.

   Heights come off `w.lift`, which `buildWalls` already set from the fall —
   the high end of a tapered wall and the constant of a cross wall — so a
   piece cannot disagree with the sheathing that was sized over it. */
function furringPieces(walls, fall, strip) {
  if (!walls || !fall || !fall.on || !strip) return [];
  const rows = [];
  walls.walls.forEach((w) => {
    if (w.furringInHeader || !(w.lift > 0.5)) return;
    const h0 = w.tapered ? r(fall.heightAt(w.start)) : r(w.lift);
    const h1 = w.tapered ? r(fall.heightAt(w.start + w.len)) : r(w.lift);
    furringWidths(walls.wt, strip.t).forEach(({ off, width }) =>
      rows.push({
        side: w.side, kind: w.tapered ? "tapered" : "packer",
        off, width, len: r(w.len), h0, h1, qty: 1,
      }));
  });
  return rows;
}

/* Marking out the angled sheets on a wall that runs down the fall. Each
   piece in the top row is a trapezoid: full row height where the fall is at
   its highest, less the rise as it works toward the gutter. Returns the two
   end heights so it can be set out from the sheet edge. */
function angledSheetCuts(w, fall) {
  if (!w || !w.tapered || !fall || !fall.on) return null;
  const shift = w.shift || 0;
  const lift = w.lift || 0;
  const rows = [...new Set(w.cutPieces.map((pc) => r(pc.z)))].sort((a, b) => a - b);
  const topZ = rows[0];

  return w.cutPieces
    .filter((pc) => Math.abs(pc.z - topZ) < 1)
    .map((pc) => {
      const p0 = pc.x - shift;
      const p1 = p0 + pc.dx;
      const at = (pos) => pc.dz + fall.heightAt(pos) - lift;
      const lo = Math.min(at(p0), at(p1));
      return {
        from: r(p0), to: r(p1), width: r(pc.dx),
        hStart: r(at(p0)), hEnd: r(at(p1)),
        sheetH: r(pc.dz), full: pc.full,
        waste: r(pc.dz - lo),
        ok: lo > 0,
      };
    })
    .sort((a, b) => a.from - b.from);
}

/* Spread a wall's own pieces apart, in the plane of the wall, by pushing
   every member's centre away from the wall's centre. Sizes are untouched, so
   the studs, plates and noggins stay the pieces they are and only the gaps
   between them open up — which is what makes an exploded wall readable.

   `by` is in millimetres and sets the scale: a piece is displaced in
   proportion to how far its centre already sits from the wall's centre,
   reaching `by` at the wall's ends. So a piece dead centre doesn't move, and
   the numbers behave the same on a short wall as on a long one. */
const spreadFactor = (half, by) => (half + by) / half;

/* Move one coordinate away from a centre, in proportion to how far its own
   centre already sits from it. The size is passed in only so the result can
   be handed back as an edge again — it is never scaled. Both the wall and the
   deck explode go through here, so the rule lives in one place. */
const spreadAxis = (edge, size, centre, k) =>
  centre + (edge + size / 2 - centre) * k - size / 2;

function explodeWallBox(b, ctx) {
  const { by, alongAxis, alongCentre, alongHalf, yCentre, yHalf } = ctx;
  if (!by) return b;
  const kA = spreadFactor(alongHalf, by);
  const kY = spreadFactor(yHalf, by);
  const d = `d${alongAxis}`;

  const out = { ...b };
  out[alongAxis] = spreadAxis(b[alongAxis], b[d], alongCentre, kA);
  out.y = spreadAxis(b.y, b.dy, yCentre, kY);

  if (b.slopeTop)
    out.slopeTop = {
      ...b.slopeTop,
      y0: yCentre + (b.slopeTop.y0 - yCentre) * kY,
      y1: yCentre + (b.slopeTop.y1 - yCentre) * kY,
    };

  /* A sheared member's height varies with position, so the shear has to be
     restated about the new origin and re-pitched for both scales. */
  if (b.shear) {
    const { origin, slope, base = 0 } = b.shear;
    out.shear = {
      ...b.shear,
      origin: alongCentre,
      slope: (slope * kY) / kA,
      base: (base + (alongCentre - origin) * slope) * kY,
    };
  }
  return out;
}

/* The same idea in plan: spread a deck's framing apart in its own plane so
   the joists, rims and noggins separate and the assembly can be read. A deck
   has no shear and no sloped top, so this is the plain case — but it shares
   `spreadAxis` with the wall, so neither can drift into scaling sizes.

   Sheet layers are left alone: they are lifted clear as whole layers by the
   caller, and spreading them too would just scatter the deck. */
function explodeDeckBox(b, ctx) {
  const { by, xCentre, xHalf, zCentre, zHalf } = ctx;
  if (!by) return b;
  return {
    ...b,
    x: spreadAxis(b.x, b.dx, xCentre, spreadFactor(xHalf, by)),
    z: spreadAxis(b.z, b.dz, zCentre, spreadFactor(zHalf, by)),
  };
}

/* Running cost. Lines come from whatever has been quantified elsewhere;
   this only multiplies and adds, and says how much is still unpriced so a
   total is never quietly wrong. */
function priceTotal(lines, prices = {}) {
  let total = 0;
  let priced = 0;
  const unpriced = [];
  lines.forEach((l) => {
    const unit = Number(prices[l.id]);
    if (Number.isFinite(unit) && unit > 0) {
      total += unit * l.qty;
      priced += 1;
    } else if (l.qty > 0) unpriced.push(l.label);
  });
  return {
    total: Math.round(total * 100) / 100,
    priced,
    unpriced,
    complete: unpriced.length === 0 && lines.length > 0,
  };
}

/* ------------------------ the pod, assembled ------------------------

   One builder for every view. Origin at the building's rear-left corner,
   ground at y = 0, so the floor frame starts at the ground clearance and
   the roof hangs out into negative coordinates by its overhangs.

   Views differ only in which stages they ask for, which is what stops the
   drawings from disagreeing with each other.

   opts.explodeWalls pushes each wall out along its own axis and
   opts.explodeRoof lifts the roof layers apart. Both are ways of looking at
   the thing; neither changes a dimension.

   opts.pieces draws sheet goods as the individual sheets they're cut from,
   joints and all — that's what the 3D views want. Left off, each layer is
   one continuous slab, which is what the STL wants: a print has no use for
   3 mm expansion gaps and would just render them as slivers.
------------------------------------------------------------------ */
function podBoxes(a) {
  const {
    fL, fW, floorSection, fDeckT, groundClear, floorFrame, floorDeckPlan, floorPir,
    walls, wallPir, ceilingHeight, roof, roofSection, rDeckT, pirT, topT,
    ohLeft, ohRear, windowGap, frameDepth, frameWidth, doorFrameWidth, pirAsCut,
    battens, joistBat, tape, wrap, roofWrap, clad, furrSection, opts,
  } = a;

  const out = [];
  /* Wall-stage pieces are gathered first so the exploded transform can be
     applied to all of them together — framing, sheathing, glazing, furring. */
  const wallStage = [];
  const sink = (stage) => (stage === "walls" ? wallStage : out);
  const add = (stage, part, x, y, z, dx, dy, dz, side, full) => {
    if (dx > 0.5 && dy > 0.5 && dz > 0.5)
      sink(stage).push({ stage, part, x, y, z, dx, dy, dz, side, full });
  };
  const pieces = !!opts.pieces;

  /* Tape is drawn from the very runs that are measured, so the model and the
     order cannot disagree about how much of it there is. A run becomes a
     ribbon of the ordered width centred on its seam, sunk into the face it
     seals rather than standing proud of it — the deck lands on that face. */
  const tapeOn = !!(opts.tape && tape && tape.width > 0);
  const tapeW = tapeOn ? tape.width : 0;
  const tapeRuns = (key) => (tapeOn
    ? ((tape.regions || []).find((g) => g.key === key) || { runs: [] }).runs
    : []);
  const ribbon = (run) => (run.axis === "a"
    ? { a: run.at - tapeW / 2, b: run.from, da: tapeW, db: run.len }
    : { a: run.from, b: run.at - tapeW / 2, da: run.len, db: tapeW });

  /* Which way each wall moves when the walls are exploded apart. */
  const exW = opts.explodeWalls || 0;
  const shove = (side) => ({
    x: side === "left" ? -exW : side === "right" ? exW : 0,
    z: side === "rear" ? -exW : side === "front" ? exW : 0,
  });

  /* Lay a horizontal sheet layer, either as its real sheets or as one slab. */
  const sheetLayer = (stage, part, plan, ox, oz, y, thickness, wholeL, wholeW,
                      insetX = 0, insetZ = 0, shear = null) => {
    const put = (x, yy, z, dx, dy, dz, full) => {
      if (dx > 0.5 && dy > 0.5 && dz > 0.5)
        sink(stage).push({ stage, part, x, y: yy, z, dx, dy, dz, full, shear });
    };
    if (pieces && plan)
      plan.pieces.forEach((pc) =>
        put(ox + pc.x, y, oz + pc.z, pc.dx, thickness, pc.dz, pc.full));
    else put(ox + insetX, y, oz + insetZ, wholeL, thickness, wholeW, true);
  };

  const floorBottom = groundClear;
  const floorTop = groundClear + floorSection.d + fDeckT;
  const wallTop = floorTop + ceilingHeight;
  const st = walls.sheathT;

  /* ---- floor ---- */
  /* The frame spreads in plan and the layers above it lift clear, so an
     exploded floor reads the same way an exploded wall does. */
  const exF = opts.explodeFloor || 0;
  const deckCtx = (L, W, by) => ({
    by, xCentre: L / 2, xHalf: L / 2, zCentre: W / 2, zHalf: W / 2 });
  const fCtx = deckCtx(fL, fW, exF);
  const spreadF = (b) => (exF ? explodeDeckBox(b, fCtx) : b);

  floorFrame.members.forEach((m) =>
    out.push(...[spreadF({ stage: "floor", part: "floor frame",
      x: m.x0, y: floorBottom, z: m.z0, dx: m.dx, dy: floorSection.d, dz: m.dz })]
      .filter((b) => b.dx > 0.5 && b.dy > 0.5 && b.dz > 0.5))
  );
  /* Insulation sits in the bays, so it travels with them. Board thinner than
     the joists are deep is pushed to the top of the bay, flush with the
     underside of the deck: the deck then sits on insulation rather than on an
     air gap, and what void there is falls underneath where it is harmless. */
  if (floorPir && floorPir.pieces.length) {
    const pirT = Math.min(floorPir.thickness, floorSection.d);
    const pirY = floorBottom + floorSection.d - pirT;
    if (opts.floorPir)
      floorPir.pieces.forEach((pc) =>
        out.push(spreadF({ stage: "floor", part: "floor insulation",
          x: pc.x, y: pirY, z: pc.z, dx: pc.dx, dy: pirT, dz: pc.dz })));

    /* The ledgers that hold that board up, hard under it: their top edge is
       the board's underside, which is the whole point of them. They are their
       own layer, though — hiding the board to look at what carries it is
       exactly when you want to see them, so this is not nested inside the
       insulation's own toggle. */
    if (opts.ledgers && joistBat)
      joistBat.pieces.forEach((pc) =>
        out.push(spreadF({ stage: "floor", part: "joist batten",
          x: pc.x, y: pirY - joistBat.deep, z: pc.z,
          dx: pc.dx, dy: joistBat.deep, dz: pc.dz })));

    /* On the warm face, which here is the top, laid **over** the board and
       over the joist it laps onto rather than sunk into them — half a
       ribbon's width lands on timber, and buried in the joist it is neither
       visible nor what happens. The deck comes down on top of it, so with
       the deck shown the tape is inside its bottom millimetre, which is
       exactly where it is on the real thing. */
    tapeRuns("floor").forEach((run) => {
      const s = ribbon(run);
      out.push(spreadF({ stage: "floor", part: "tape",
        x: s.a, y: pirY + pirT, z: s.b,
        dx: s.da, dy: TAPE_DRAWN_T, dz: s.db }));
    });
  }
  if (opts.floorDeck)
    sheetLayer("floor", "floor deck", floorDeckPlan, 0, 0,
      floorBottom + floorSection.d + exF, fDeckT, fL, fW);

  /* ---- walls ---- */
  if (opts.walls) {
    walls.walls.forEach((w) => {
      const alongX = w.axis === "x";
      const o = shove(w.side);
      w.frame.members.forEach((m) => {
        const slopeTop = m.slopeTopY
          ? { axis: alongX ? "x" : "z",
              y0: floorTop + m.slopeTopY[0], y1: floorTop + m.slopeTopY[1] }
          : null;
        /* A tilted member keeps its depth: top and underside stay parallel. */
        const startPos = alongX ? o.x + w.start + m.x : o.z + w.start + m.x;
        const shear = m.shearY
          ? { axis: alongX ? "x" : "z", origin: startPos, slope: m.shearY, base: 0 }
          : null;
        laminate(m, walls.wt).forEach((ply) => {
          const part = m.isHeader ? "header" : "wall frame";
          if (alongX)
            wallStage.push({ stage: "walls", part, side: w.side, slopeTop, shear,
              x: o.x + w.start + m.x, y: floorTop + m.y, z: o.z + w.at + ply.off,
              dx: m.dx, dy: m.dy, dz: ply.depth });
          else
            wallStage.push({ stage: "walls", part, side: w.side,
              slopeTop: slopeTop ? { ...slopeTop, axis: "z" } : null, shear,
              x: o.x + w.at + ply.off, y: floorTop + m.y, z: o.z + w.start + m.x,
              dx: ply.depth, dy: m.dy, dz: m.dx });
        });
      });

      /* Insulation goes in the wall stage with the framing, so it explodes
         with the wall rather than hanging in the air where the wall was.
         Pushed to the **inside** face of the cavity, flush with the studs and
         so with the internal lining. Any shortfall then falls on the outside,
         against the sheathing, where it is a ventilated cavity rather than a
         gap behind the plasterboard. It used to be the other way about. */
      const wp = wallPir && wallPir[w.side];
      if (wp && wp.pieces.length) {
        const wt = walls.wt;
        const pt = Math.min(wp.thickness, wt);
        /* The inside is the high coordinate on a rear or left wall and the
           low one on a front or right wall, because the building is between
           them. */
        const off = w.side === "rear" || w.side === "left" ? wt - pt : 0;
        if (opts.wallPir) wp.pieces.forEach((bay) => {
          if (alongX)
            wallStage.push({ stage: "walls", part: "wall insulation", side: w.side,
              x: o.x + w.start + bay.x, y: floorTop + bay.y, z: o.z + w.at + off,
              dx: bay.dx, dy: bay.dy, dz: pt });
          else
            wallStage.push({ stage: "walls", part: "wall insulation", side: w.side,
              x: o.x + w.at + off, y: floorTop + bay.y, z: o.z + w.start + bay.x,
              dx: pt, dy: bay.dy, dz: bay.dx });
        });

        /* The board is flush with the **inside** of the studs, so the face
           to seal is that one — the high side of a rear or left wall and the
           low side of a front or right one, exactly as `off` is. Tape the
           cold face and the seal is on the wrong side of the insulation. */
        const inner = w.side === "rear" || w.side === "left"
          ? off + pt
          : off - TAPE_DRAWN_T;
        tapeRuns(`wall-${w.side}`).forEach((run) => {
          const s = ribbon(run);
          if (alongX)
            wallStage.push({ stage: "walls", part: "tape", side: w.side,
              x: o.x + w.start + s.a, y: floorTop + s.b, z: o.z + w.at + inner,
              dx: s.da, dy: s.db, dz: TAPE_DRAWN_T });
          else
            wallStage.push({ stage: "walls", part: "tape", side: w.side,
              x: o.x + w.at + inner, y: floorTop + s.b, z: o.z + w.start + s.a,
              dx: TAPE_DRAWN_T, dy: s.db, dz: s.da });
        });
      }
    });
  }

  if (opts.sheathing) {
    walls.walls.forEach((w) => {
      const alongX = w.axis === "x";
      const o = shove(w.side);
      const a0 = -w.shift + (alongX ? o.x : o.z);
      const y0 = wallTop + (w.lift || 0) - w.sheathH;
      const depth = alongX
        ? { at: (w.side === "rear" ? -st : fW) + o.z, size: st }
        : { at: (w.side === "left" ? -st : fL) + o.x, size: st };

      const put = (along, alen, ylo, yhi) => {
        if (alen <= 0.5 || yhi - ylo <= 0.5) return;
        if (alongX)
          add("walls", "sheathing", along, ylo, depth.at, alen, yhi - ylo, depth.size, w.side);
        else
          add("walls", "sheathing", depth.at, ylo, along, depth.size, yhi - ylo, alen, w.side);
      };

      if (pieces) {
        /* cutPieces already has the opening subtracted, and its z runs down
           from the top of the wall. The top row is cut to the fall. */
        const f = a.fall;
        const top = wallTop + (w.lift || 0);
        w.cutPieces.forEach((pc) => {
          const ylo = top - pc.z - pc.dz;
          const isTop = pc.z < 1;
          let slopeTop = null;
          const dy = pc.dz;
          if (isTop && w.tapered && f && f.on) {
            const s0 = a0 + pc.x;
            const s1 = s0 + pc.dx;
            slopeTop = { axis: alongX ? "x" : "z",
              y0: wallTop + f.heightAt(s0), y1: wallTop + f.heightAt(s1) };
          }
          if (alongX)
            wallStage.push({ stage: "walls", part: "sheathing", side: w.side, full: pc.full,
              x: a0 + pc.x, y: ylo, z: depth.at, dx: pc.dx, dy, dz: depth.size, slopeTop });
          else
            wallStage.push({ stage: "walls", part: "sheathing", side: w.side, full: pc.full,
              x: depth.at, y: ylo, z: a0 + pc.x, dx: depth.size, dy, dz: pc.dx, slopeTop });
        });
        return;
      }

      const h = w.hole;
      if (!h) return void put(a0, w.sheath, y0, y0 + w.sheathH);
      /* Hole positions come straight from the wall's own sheathing plan, so
         they can't drift from what the elevation draws. */
      const hStart = a0 + h.x;
      const hEnd = hStart + h.dx;
      const hTop = y0 + w.sheathH - h.z;
      const hBot = hTop - h.dz;
      put(a0, h.x, y0, y0 + w.sheathH);
      put(hEnd, a0 + w.sheath - hEnd, y0, y0 + w.sheathH);
      put(hStart, h.dx, y0, hBot);
      put(hStart, h.dx, hTop, y0 + w.sheathH);
    });
  }

  /* ---- breather membrane ----
     Outside the sheathing and under the battens, in courses from the bottom
     up. The bottom is level all round — a wall's `sheathH` already carries
     its own lift, so `wallTop + lift - sheathH` is the same height on every
     wall — which is why one set of courses serves all four. Each band is
     clipped to its own wall's top, since the tallest wall set the course
     count and the others stop short of it. */
  if (opts.wrap && wrap && wrap.n) {
    const mt = WRAP_DRAWN_T;
    const fallW = a.fall;
    walls.walls.forEach((w) => {
      const alongX = w.axis === "x";
      const o = shove(w.side);
      const a0 = -w.shift + (alongX ? o.x : o.z);
      const y0 = wallTop + (w.lift || 0) - w.sheathH;
      const outward = alongX ? w.side === "rear" : w.side === "left";
      const face = alongX
        ? (w.side === "rear" ? -st : fW + st) + o.z
        : (w.side === "left" ? -st : fL + st) + o.x;
      const at = outward ? face - mt : face;
      const wallTopY = y0 + w.sheathH;

      const emit = (alo, alen, ylo, yhi) => {
        if (alen <= 0.5 || yhi - ylo <= 0.5) return;
        /* The top band on a wall carrying the fall follows it, the way the
           top row of sheathing under it does — a square band there stands
           proud of the wall at the low end. Pitched across this piece's own
           ends rather than the whole wall's, since a band cut by an opening
           is several pieces. */
        const slopeTop = yhi >= wallTopY - 0.5 && w.tapered && fallW && fallW.on
          ? { axis: alongX ? "x" : "z",
              y0: wallTop + fallW.heightAt(alo),
              y1: wallTop + fallW.heightAt(alo + alen) }
          : null;
        if (alongX)
          wallStage.push({ stage: "walls", part: "wrap", side: w.side,
            x: alo, y: ylo, z: at, dx: alen, dy: yhi - ylo, dz: mt, slopeTop });
        else
          wallStage.push({ stage: "walls", part: "wrap", side: w.side,
            x: at, y: ylo, z: alo, dx: mt, dy: yhi - ylo, dz: alen, slopeTop });
      };

      /* It is run over the opening and cut out after, like the sheathing —
         and the tape figure already pays for dressing it back to the frame
         all round, so a model that leaves it whole is drawing a pod with no
         window and disagreeing with its own order sheet. */
      const h = w.hole;
      const hStart = h ? a0 + h.x : 0;
      const hEnd = h ? hStart + h.dx : 0;
      const hTop = h ? wallTopY - h.z : 0;
      const hBot = h ? hTop - h.dz : 0;
      wrap.courses.forEach((c) => {
        const lo = y0 - wrap.drop + c.at;
        const hi = Math.min(lo + c.h, wallTopY);
        if (hi - lo <= 0.5) return;
        if (!h || hi <= hBot + 0.5 || lo >= hTop - 0.5)
          return void emit(a0, w.sheath, lo, hi);
        emit(a0, hStart - a0, lo, hi);
        emit(hEnd, a0 + w.sheath - hEnd, lo, hi);
        emit(hStart, h.dx, lo, Math.min(hi, hBot));
        emit(hStart, h.dx, Math.max(lo, hTop), hi);
      });
    });
  }

  /* ---- cladding battens ----
     Outside the sheathing, in the order they go on: the verticals against
     the board, and with vertical cladding a second layer of horizontals on
     top of them. `runs` are already in face coordinates — along the face
     from the sheathing's left edge, up from its bottom edge — which is the
     same plane the fixings use and needs the same correction back out of
     it. */
  if (opts.battens && battens) {
    walls.walls.forEach((w, i) => {
      const b = battens.per[i];
      if (!b) return;
      const alongX = w.axis === "x";
      const o = shove(w.side);
      const a0 = -w.shift + (alongX ? o.x : o.z);
      const y0 = wallTop + (w.lift || 0) - w.sheathH;
      const outward = alongX ? w.side === "rear" : w.side === "left";
      const face = alongX
        ? (w.side === "rear" ? -st : fW + st) + o.z
        : (w.side === "left" ? -st : fL + st) + o.x;
      /* A wall on the near side grows outward in the negative direction, so
         its layers start a thickness further back rather than further on. */
      const skin = opts.wrap && wrap && wrap.n ? WRAP_DRAWN_T : 0;
      const layerAt = (n) => (outward
        ? face - skin - (n + 1) * b.proud
        : face + skin + n * b.proud);
      b.runs.forEach((run) => {
        const n = run.kind === "horizontal" ? 1 : 0;
        const part = run.kind === "horizontal" && battens.layers > 1
          ? "counter batten" : "batten";
        const d = layerAt(n);
        if (alongX)
          add("walls", part, a0 + run.a, y0 + run.b, d, run.da, run.db, b.proud, w.side);
        else
          add("walls", part, d, y0 + run.b, a0 + run.a, b.proud, run.db, run.da, w.side);
      });
    });
  }
  /* ---- cladding ----
     On the outside of the battens, in face coordinates like everything else
     out here. A board stands `thickness` proud of whatever batten layer it
     fixes to, and the shadow gap between boards is simply the space the
     geometry leaves — nothing is drawn in a groove. */
  if (opts.cladding && clad && battens) {
    walls.walls.forEach((w, i) => {
      const c = clad.per[i];
      const b = battens.per[i];
      if (!c || !b || !c.boards.length) return;
      const alongX = w.axis === "x";
      const o = shove(w.side);
      const a0 = -w.shift + (alongX ? o.x : o.z);
      const y0 = wallTop + (w.lift || 0) - w.sheathH;
      const outward = alongX ? w.side === "rear" : w.side === "left";
      const face = alongX
        ? (w.side === "rear" ? -st : fW + st) + o.z
        : (w.side === "left" ? -st : fL + st) + o.x;
      const skin = opts.wrap && wrap && wrap.n ? WRAP_DRAWN_T : 0;
      /* Clear of every batten layer, not just the first: with vertical
         cladding the boards fix to the horizontals, which are themselves
         sitting on the verticals. */
      const stand = skin + battens.layers * b.proud;
      const at = outward ? face - stand - clad.thickness : face + stand;
      c.boards.forEach((pc) => {
        /* A board cut to the fall is drawn to it, the way the top row of
           sheathing under it is — square here and the model shows a step
           where the real thing has a line. */
        const slopeTop = pc.cut
          ? { axis: alongX ? "x" : "z",
              y0: y0 + pc.b + pc.cut.hStart, y1: y0 + pc.b + pc.cut.hEnd }
          : null;
        if (alongX)
          wallStage.push({ stage: "walls", part: "cladding", side: w.side,
            x: a0 + pc.a, y: y0 + pc.b, z: at,
            dx: pc.da, dy: pc.db, dz: clad.thickness, slopeTop });
        else
          wallStage.push({ stage: "walls", part: "cladding", side: w.side,
            x: at, y: y0 + pc.b, z: a0 + pc.a,
            dx: clad.thickness, dy: pc.db, dz: pc.da, slopeTop });
      });
    });
  }

  if (opts.glazing) {
    walls.walls.forEach((w) => {
      if (!w.frame.opening) return;
      const o = shove(w.side);
      /* A wall carries one opening at most, so which frame it is falls out
         of the opening itself. Depth is shared — it is set by the wall — but
         the face width is the door's own. */
      const fw = w.frame.opening.isDoor && doorFrameWidth != null
        ? doorFrameWidth : frameWidth;
      windowFrameBoxes({ ...w, gapMm: windowGap, solidLeaf: a.solidLeaf }, walls, fL, fW,
        frameDepth, fw, o.x, floorTop, o.z)
        .forEach((b) => add("walls", b.part, b.x, b.y, b.z, b.dx, b.dy, b.dz, w.side));
    });
  }

  /* ---- furring strips ----
     Tapered plates on the two walls that run down the fall, carrying the
     roof at an angle. The two cross walls get a constant packer instead. */
  const fall = a.fall;
  if (opts.furring && fall && fall.on) {
    /* The strips are cut from the furring section, not from the wall's — the
       two are the same width on a default pod and nothing said otherwise
       while the model took `walls.t` and the cut list took the furring's. */
    const t = furrSection ? furrSection.t : walls.t;
    const along = fall.axis;
    walls.walls.forEach((w) => {
      if (w.furringInHeader) return;
      const runsWithFall = (along === "z") === (w.axis === "z");
      /* Strips and packers sit on their own wall's plate, so they run the
         frame's length — not the building's. A wall that butts between two
         others is already shortened by their thickness, and using the full
         building dimension drives the timber straight into the adjoining
         wall's plate or header. */
      const p0 = w.start;
      const p1 = w.start + w.len;

      /* Whole boards side by side, never a rip. Where they do not come to
         the plate exactly, any overhang falls on the **inside** face, over
         the ceiling — the other way runs the strip into the sheathing. A
         shortfall simply leaves the inside edge of the plate bare, which is
         a packer on site rather than a piece on the cut list. */
      const strips = furringWidths(walls.wt, t);
      const lowOutside = w.axis === "x" ? w.side === "rear" : w.side === "left";
      const covered = strips.reduce((sum, x) => sum + x.width, 0);
      const base = lowOutside ? w.at : r(w.at + walls.wt - covered);

      if (runsWithFall) {
        strips.forEach(({ off, width }) => {
          const box = along === "z"
            ? { x: base + off, y: wallTop, z: p0, dx: width, dy: 0, dz: w.len }
            : { x: p0, y: wallTop, z: base + off, dx: w.len, dy: 0, dz: width };
          wallStage.push({ stage: "walls", part: "furring", side: w.side, ...box,
            slopeTop: { axis: along, y0: wallTop + fall.heightAt(p0),
                        y1: wallTop + fall.heightAt(p1) } });
        });
      } else {
        /* A packer is the same boards with a level top, cut from the same
           section — not one solid the full wall thickness wide. */
        const h = w.lift;
        if (h > 0.5)
          strips.forEach(({ off, width }) => {
            if (along === "z")
              add("walls", "furring packer", p0, wallTop, base + off, w.len, h, width, w.side);
            else
              add("walls", "furring packer", base + off, wallTop, p0, width, h, w.len, w.side);
          });
      }
    });
  }

  /* Spread each wall's own pieces, then hand them on. */
  if (exW) {
    walls.walls.forEach((w) => {
      const ctx = {
        by: exW,
        alongAxis: w.axis === "x" ? "x" : "z",
        alongCentre: w.start + w.len / 2,
        alongHalf: Math.max(1, w.len / 2),
        yCentre: floorTop + ceilingHeight / 2,
        yHalf: Math.max(1, ceilingHeight / 2),
      };
      wallStage.forEach((b, i) => {
        if (b.side === w.side) wallStage[i] = explodeWallBox(b, ctx);
      });
    });
  }
  wallStage.forEach((b) => out.push(b));

  /* ---- roof ---- */
  if (opts.roof) {
    const rx = -ohLeft;
    const rz = -ohRear;
    const shear = fall && fall.on ? fall.shear : null;
    const addR = (part, x, y, z, dx, dy, dz, side, full) => {
      if (dx > 0.5 && dy > 0.5 && dz > 0.5)
        out.push({ stage: "roof", part, x, y, z, dx, dy, dz, side, full, shear });
    };
    const exR = opts.explodeRoof || 0;
    const rCtx = deckCtx(roof.L, roof.W, exR);
    roof.frame.members.forEach((m) => {
      const b = { x: m.x0, y: wallTop, z: m.z0, dx: m.dx, dy: roofSection.d, dz: m.dz };
      const sp = exR ? explodeDeckBox(b, rCtx) : b;
      addR(m.role === "Noggin" ? "roof noggin" : "roof frame",
        rx + sp.x, sp.y, rz + sp.z, sp.dx, sp.dy, sp.dz);
    });

    const layers = opts.roofLayers || [];
    /* An exploded view separates the warm-roof build-up vertically, on the
       same number that spread the frame in plan above. Purely a way of
       looking at it: the real heights are unchanged. */
    const deckY = wallTop + roofSection.d + exR;
    if (layers.includes("deck"))
      sheetLayer("roof", "roof deck", roof.deck, rx, rz, deckY, rDeckT, roof.L, roof.W, 0, 0, shear);

    const uy = deckY + rDeckT + exR;
    const uw = roof.upstand.uw;
    const uh = roof.upstand.uh;
    if (layers.includes("upstand")) {
      addR("upstand", rx, uy, rz, roof.L, uh, uw);
      addR("upstand", rx, uy, rz + roof.W - uw, roof.L, uh, uw);
      addR("upstand", rx, uy, rz + uw, uw, uh, roof.W - 2 * uw);
      addR("upstand", rx + roof.L - uw, uy, rz + uw, uw, uh, roof.W - 2 * uw);
    }
    /* The membrane lining the tray: on the deck, under the insulation, up
       the inside of the upstand and over its top. Drawn as the strips it is
       laid in across the tray — the laps are the thing worth seeing — with
       the turn-ups at the two ends of a strip as their own pieces, since
       those run the other way. */
    if (opts.roofWrap && roofWrap && roofWrap.n) {
      const mt = WRAP_DRAWN_T;
      const alongX = roofWrap.alongAxis === "x";
      const inL = roof.L - 2 * uw;
      const inW = roof.W - 2 * uw;
      /* Where a point on the developed width lands in the tray: over the
         near top, down the near face, across the deck, up the far face and
         out over the far top. */
      const innerCross = alongX ? inW : inL;
      const segs = [
        { at: 0, len: uw, kind: "top", near: true },
        { at: uw, len: uh, kind: "face", near: true },
        { at: uw + uh, len: innerCross, kind: "floor" },
        { at: uw + uh + innerCross, len: uh, kind: "face", near: false },
        { at: uw + uh + innerCross + uh, len: uw, kind: "top", near: false },
      ];
      /* The strip runs the full inner length on the deck and the faces, and
         the full roof length over the caps, which overhang it at each end. */
      const a0 = (alongX ? rx : rz) + uw;
      const aLen = alongX ? inL : inW;
      const capA = alongX ? rx : rz;
      const capLen = alongX ? roof.L : roof.W;
      const crossBase = alongX ? rz : rx;
      const crossFull = alongX ? roof.W : roof.L;
      const put = (aAt, aSize, y, cAt, cSize, dy) => {
        if (alongX) addR("roof wrap", aAt, y, cAt, aSize, dy, cSize);
        else addR("roof wrap", cAt, y, aAt, cSize, dy, aSize);
      };
      roofWrap.strips.forEach((st) => {
        segs.forEach((sg) => {
          const lo = Math.max(st.at, sg.at);
          const hi = Math.min(st.at + st.h, sg.at + sg.len);
          if (hi - lo <= 0.5) return;
          const off = lo - sg.at;
          const size = hi - lo;
          if (sg.kind === "floor")
            put(a0, aLen, uy, crossBase + uw + off, size, mt);
          else if (sg.kind === "top")
            put(capA, capLen, uy + uh,
              sg.near ? crossBase + off : crossBase + crossFull - uw + off, size, mt);
          else
            /* Down the near face, up the far one — the developed run goes
               one way round the tray, so the near face is measured from the
               top down and the far one from the floor up. */
            put(a0, aLen,
              sg.near ? uy + uh - off - size : uy + off,
              sg.near ? crossBase + uw : crossBase + crossFull - uw - mt,
              mt, size);
        });
      });
      /* The ends of every strip, turned up and over the two upstands that
         run the other way. One piece each rather than one per strip, since
         a strip's end is a single dressed edge. */
      const farEdge = capA + capLen;
      [false, true].forEach((far) => {
        /* The turn-up sits on the tray side of that upstand's inner face:
           at the near end the face is at `a0`, so the membrane spans back a
           thickness from it; at the far end it is at `farEdge - uw` and the
           membrane spans forward. */
        put(far ? farEdge - uw : a0 - mt, mt, uy,
          crossBase + uw, innerCross, uh);
        put(far ? farEdge - uw : capA, uw, uy + uh, crossBase, crossFull, mt);
      });
    }

    /* One board or a stack of them, each sitting on the one below — and an
       exploded view parts them on the same number as everything else, since
       a two-layer tray is precisely the thing you open up to look at. */
    const lt = roof.pirLayerT || pirT;
    /* Drawn as it will be laid where the allocation has been done: a tray
       board made up from butted offcuts is several boards on the roof, not
       one, and a model that shows one is telling you the wrong thing to
       carry up. Falls back to the planned pieces when nothing has said
       otherwise. */
    const pirPlans = pirAsCut || roof.pirPlans || [roof.pir];
    if (layers.includes("pir"))
      pirPlans.forEach((plan, i) =>
        sheetLayer("roof", "insulation", plan, rx, rz, uy + i * (lt + exR), lt,
          roof.L - 2 * uw, roof.W - 2 * uw, uw, uw, shear));
    /* Each taped layer gets its runs on its own top face, which is why the
       region key carries the layer index: tape only the top layer and the
       lower one draws nothing rather than the same ribbons twice. */
    pirPlans.forEach((plan, i) => {
      void plan;
      tapeRuns(`roof-${i}`).forEach((run) => {
        const s = ribbon(run);
        addR("tape", rx + s.a, uy + i * (lt + exR) + lt, rz + s.b,
          s.da, TAPE_DRAWN_T, s.db);
      });
    });
    if (layers.includes("top"))
      sheetLayer("roof", "top deck", roof.top, rx, rz,
        uy + Math.max(uh, pirT) + (pirPlans.length - 1) * exR + exR,
        topT, roof.L, roof.W, 0, 0, shear);

    if (opts.gutter) {
      const g = roof.gutter.box;
      addR("gutter", rx + g.x, wallTop + g.y, rz + g.z, g.dx, g.dy, g.dz);
    }
  }

  return out;
}

/* Chunky solid version for printing: no thin parts at any scale. */
function massingBoxes(a) {
  const { fL, fW, floorSection, fDeckT, groundClear, walls, ceilingHeight,
          roof, roofSection, rDeckT, pirT, topT, ohLeft, ohRear } = a;
  const st = walls.sheathT;
  const floorTop = groundClear + floorSection.d + fDeckT;
  const roofThick = roofSection.d + rDeckT + Math.max(roof.upstand.uh, pirT) + topT;
  return [
    { stage: "floor", part: "plinth", x: 0, y: groundClear, z: 0,
      dx: fL, dy: floorSection.d + fDeckT, dz: fW },
    { stage: "walls", part: "walls", x: -st, y: floorTop, z: -st,
      dx: fL + 2 * st, dy: ceilingHeight, dz: fW + 2 * st },
    { stage: "roof", part: "roof", x: -ohLeft, y: floorTop + ceilingHeight, z: -ohRear,
      dx: roof.L, dy: roofThick, dz: roof.W },
  ];
}

/* Binary STL, millimetres, Z up.

   The model is Y up, so exporting swaps Y and Z. That's a reflection, which
   inverts every normal, so triangle winding is reversed on the way out. */
/* Grow anything that would print thinner than the nozzle can manage, about
   its own centre. Distorts the model slightly; the alternative is a feature
   that simply doesn't appear. */
function inflate(boxes, scale, minMm) {
  if (!minMm) return boxes;
  const need = minMm / scale;
  return boxes.map((b) => {
    const o = { ...b };
    ["x", "y", "z"].forEach((ax) => {
      const dk = `d${ax}`;
      if (o[dk] < need) {
        o[ax] -= (need - o[dk]) / 2;
        o[dk] = need;
      }
    });
    return o;
  });
}

function boxesToSTL(boxes, scale, name = "pod") {
  const tris = [];
  const sub = (p, q) => [p[0] - q[0], p[1] - q[1], p[2] - q[2]];
  const cross = (p, q) => [
    p[1] * q[2] - p[2] * q[1], p[2] * q[0] - p[0] * q[2], p[0] * q[1] - p[1] * q[0],
  ];
  const dot = (p, q) => p[0] * q[0] + p[1] * q[1] + p[2] * q[2];

  boxes.forEach((b) => {
    const V = cornersOf(b);
    BOX_FACES.forEach((f) => {
      [[0, 1, 2], [0, 2, 3]].forEach((idx) => {
        let t = idx.map((i) => V[f.v[i]]);
        if (dot(cross(sub(t[1], t[0]), sub(t[2], t[0])), f.n) < 0) t = [t[0], t[2], t[1]];
        // model (x, y, z) -> print (x, z, y); a reflection, so reverse winding
        const p = t.map((v2) => [v2[0] * scale, v2[2] * scale, v2[1] * scale]);
        tris.push([p[0], p[2], p[1]]);
      });
    });
  });

  const buf = new ArrayBuffer(84 + tris.length * 50);
  const view = new DataView(buf);
  const header = `${name} - generated`.slice(0, 79);
  for (let i = 0; i < header.length; i += 1) view.setUint8(i, header.charCodeAt(i));
  view.setUint32(80, tris.length, true);

  let o = 84;
  tris.forEach((t) => {
    const u = sub(t[1], t[0]);
    const w = sub(t[2], t[0]);
    let n = cross(u, w);
    const len = Math.hypot(n[0], n[1], n[2]) || 1;
    n = n.map((c) => c / len);
    n.forEach((c, i) => view.setFloat32(o + i * 4, c, true));
    o += 12;
    t.forEach((v2) => {
      v2.forEach((c, i) => view.setFloat32(o + i * 4, c, true));
      o += 12;
    });
    view.setUint16(o, 0, true);
    o += 2;
  });
  return { buffer: buf, triangles: tris.length };
}

function extents(boxes) {
  if (!boxes.length) return { dx: 0, dy: 0, dz: 0, minFeature: 0 };
  let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
  let minFeature = Infinity;
  let thinnest = null;
  boxes.forEach((b) => {
    const pts = cornersOf(b);
    pts.forEach(([px, py, pz]) => {
      x0 = Math.min(x0, px); y0 = Math.min(y0, py); z0 = Math.min(z0, pz);
      x1 = Math.max(x1, px); y1 = Math.max(y1, py); z1 = Math.max(z1, pz);
    });
    const ys = pts.map((q2) => q2[1]);
    const m = Math.min(b.dx, b.dz, Math.max(...ys) - Math.min(...ys));
    if (m < minFeature) { minFeature = m; thinnest = b.part; }
  });
  return { dx: x1 - x0, dy: y1 - y0, dz: z1 - z0, minFeature, thinnest };
}

export {
  ALL_LAYERS,
  BOX_FACES,
  DEFAULT_DESIGN,
  DEFAULT_VIEWS,
  DESIGN_VERSION,
  EPDM_WIDTHS,
  EXAMPLES,
  FIXINGS,
  FRAMING_ONLY,
  GUTTER,
  LAYERS,
  LAYER_SINCE,
  planTape,
  bridgeRuns,
  planWrap,
  boundaryClearance,
  planRoofWrap,
  WRAP_ROLL_W,
  tapeExtra,
  TAPE_W,
  TAPE_MIN_LAP,
  tapeTrade,
  seamRuns,
  TAPE_DRAWN_T,
  LAYER_COLOURS,
  LAMBDA,
  LEGACY_SECTIONS,
  LOCATIONS,
  MIN_OFFCUT,
  MIN_PIECE,
  ORIENTATIONS,
  OSB_T,
  PALETTES,
  PART_LAYER,
  PIR_BOARDS,
  PIR_STAGGER_MIN,
  BATTEN_MIN,
  PIR_T,
  R_CAVITY,
  R_SURFACE,
  ROOF_PIR_MIN,
  REFERENCE_SIDE,
  SECTIONS,
  SHEETS,
  SIDES,
  SIDE_ANGLE,
  SLIVER,
  STOCK,
  VIEW_DEPTH,
  allocateSheets,
  angledSheetCuts,
  boxesToSTL,
  buildEnvelope,
  buildFrame,
  centresFor,
  buildRoof,
  buildWall,
  buildWalls,
  buildBattens,
  buildCladding,
  CLAD_MIN,
  CLAD_SHORT,
  CLAD_STOCK,
  joistBattens,
  checkSupport,
  cornersOf,
  darken,
  explodeDeckBox,
  explodeWallBox,
  extents,
  facingOf,
  fallOf,
  fixingRun,
  floorInsulation,
  freeSpans,
  fmt,
  furringWidths,
  furringCover,
  furringPieces,
  heatLoads,
  hexInt,
  inflate,
  joistCentres,
  laminate,
  layoutOption,
  lighten,
  m2,
  makeSection,
  massingBoxes,
  migrateDesign,
  packStock,
  planFixings,
  planSheets,
  podBoxes,
  quantise,
  priceTotal,
  r,
  rectSubtract,
  rollUp,
  stepPlaces,
  sideLabel,
  splitAxis,
  viewForSide,
  wallBox,
  wallInsulation,
  windowFrameBoxes,
  windowSizes,
};
